// Slice 2A → 2D1 · Set Up Your Business + shared authenticated-shell
// localization dictionary. English is the source-of-truth and remains
// the fallback for any key not yet translated. Only two locales are
// supported by this slice: "en" and "es".
//
// This module is intentionally stateless: dictionaries are static
// data exported for the LangContext, which owns the single reactive
// source of truth for the active language and hands components a
// bound `t()` via `useLang()`. Do not import `t` from this module
// directly inside a component — use `useLang().t` so the component
// re-renders when the contractor toggles the language.

const EN = {
  brand: "Set Up Your Business",
  brand_tagline: "Set it up once. Reuse it on future jobs. Adjust it anytime.",
  welcome_hi: "Welcome",
  welcome_body_intro: "JobClose AI is preparing your setup. This helps us:",
  welcome_bullets: [
    "Organize trade-specific job information",
    "Reuse your business details on every job",
    "Prepare estimates and customer documents",
    "Apply the defaults you save here",
  ],
  welcome_continue: "Continue to Business & Trades",
  welcome_active_company: "Active company",
  step_labels: {
    business_and_trades: "Business & Trades",
    trade_setup: "Trade Setup",
    documents_and_customer_defaults: "Documents & Customer Defaults",
    review_and_ready: "Review & Ready",
  },
  progress_label: (done, total) => `${done} of ${total} steps complete`,
  next_step_label: (label) => `Next: ${label}`,
  step1_title: "Business & Trades",
  step1_subtitle: "Your business details, service trades, and setup order.",
  section_business: "Business information",
  section_trades: "Trades",
  section_trade_order: "Trade setup order",
  section_trade_order_help: "Choose the order you want to set up your trades. You can change it later.",
  section_logo: "Company logo",
  section_time_zone: "Time zone",
  labels: {
    business_name: "Business name",
    business_phone: "Business phone",
    business_email: "Business email",
    business_zip: "Business ZIP code",
    business_address: "Business address (optional)",
    business_city: "City",
    business_state: "State",
    website: "Website (optional)",
    service_area: "Service area (optional)",
    license: "License / credentials (optional)",
    logo: "Company logo (optional)",
    time_zone: "Time zone",
    trades: "Trades you offer",
  },
  time_zone_detected: (label) => `Detected: ${label} — change it if needed.`,
  logo_replace: "Replace logo",
  logo_remove: "Remove logo",
  logo_remove_confirm: "Remove the current logo?",
  logo_types: "PNG, JPG, WebP, or SVG (max 2 MB).",
  trade_status: { in_progress: "Setup in progress", later: "Set up later", ready: "Ready" },
  trades_hint: "Select every trade you offer. You don't need to pick a primary.",
  save: "Save Step 1",
  saving: "Saving…",
  saved: "Saved",
  save_failed: "Save failed. Your entries are preserved — please try again.",
  back_to_dashboard: "Back to Dashboard",
  future_step_title: "Coming next",
  future_step_body: (label) => `${label} isn't available yet. You'll be able to finish it in the next Set Up Your Business slice.`,
  step1_complete_banner: "Step 1 complete — next up: Trade Setup.",
  required_hint: "Required",
  errors: {
    business_name_required: "Business name is required.",
    business_phone_required: "Business phone is required.",
    business_email_required: "Business email is required.",
    business_zip_required: "Business ZIP code is required.",
    at_least_one_trade: "Select at least one trade.",
  },

  review_title: "Review & Ready",
  review_intro:
    "Take a moment to review everything you configured. No new setup options are added here.",
  review_section_company: "Company Profile",
  review_section_trades: "Active Trades",
  review_section_documents: "Document Defaults",
  review_company_no_name: "No company name yet",
  review_trades_none: "No trades selected yet",
  review_pill_configured: "Configured",
  review_pill_set_later: "Set up later",
  review_pill_needs_attention: "Needs attention",
  review_finish_setup: "Finish setup",
  review_continue_later: "Continue later",
  review_edit: "Edit",
  review_hint_complete:
    "Setup is complete. You can revisit any section any time.",
  review_hint_ready:
    "Everything is ready. Press Finish setup to enter the dashboard.",
  review_hint_blocked:
    "Every document family must be Ready or Later before you can finish.",
  family_names: {
    proposal: "Proposal Defaults",
    contract: "Contract Defaults",
    invoice: "Invoice Defaults",
    customer_update: "Customer Update Defaults",
    closeout: "Closeout Defaults",
    warranty: "Warranty Defaults",
  },

  // Slice 2S · Live-path onboarding continuity — SetUpYourBusiness extras
  soyb: {
    toast_logo_uploaded: "Logo uploaded",
    toast_logo_upload_failed: "Logo upload failed",
    toast_logo_remove_failed: "Could not remove logo",
    toast_finish_setup_failed: "Could not finish setup. Please try again.",
    step2_subtitle:
      "Set up how you price and build each type of work. You can adjust it anytime.",
    continue_to_trade_setup: "Continue to Trade Setup",
    step1_blocked_title: "Business & Trades needs attention",
    step1_blocked_body:
      "Fix the highlighted fields below before continuing to Trade Setup. Your previously saved information has not been changed.",
    logo_upload: "Upload",
    future_step_default_label: "Next step",
    placeholder_street: "Street (optional)",
    trade_action: {
      edit_setup: "Edit setup",
      resume_setup: "Resume setup",
      continue_setup: "Continue setup",
    },
    title_status_only: "Change status only",
    title_open_trade: "Open this trade in Trade Setup",
    title_move_up: "Move up (order only)",
    title_move_down: "Move down (order only)",
    add_another_trade: "Add Another Trade",
    add_another_trade_hint:
      "Expanding into a new trade? Pick it up in Set Up Your Business — your existing trades, pricing, and history stay untouched.",
    add_another_trade_cta_aria:
      "Open Set Up Your Business to add another trade",
    systems_optional_helper:
      "You only need to set up the systems you actually install. Systems you don't use can be left blank — they won't block your pricing setup.",
  },

  // Slice 2S · Live-path onboarding continuity — Step 3 (Documents & Customer Defaults) UI
  step3: {
    loading: "Loading document setup…",
    load_error: "Could not load document setup state",
    headline: "Documents & Customer Defaults",
    subtitle:
      "Set the standard wording JobClose AI uses to prepare future proposals, contracts, invoices, customer updates, and closeout packets. You can review and edit every document before it is sent.",
    ready_helper_text:
      "Ready means you reviewed this section and accept your saved wording or JobClose AI's built-in defaults. You can still edit future documents before sending.",
    needs_attention_pill: "Needs attention",
    complete_title: "Documents & Customer Defaults complete",
    complete_body:
      "Your document families are ready or intentionally set up for later. You can update them at any time.",
    continue_to_review: "Continue to Review & Ready",
    review_defaults: "Review document defaults",
    blocked_title: "Documents & Customer Defaults needs attention",
    blocked_body:
      "One of your document families contains example information that could appear on a customer document. Fix it before continuing to Review & Ready.",
    fix_family: (name) => `Fix ${name}`,
    current_status: "Current status:",
    mark_ready: "Mark ready",
    set_up_later: "Set up later",
    dirty_blocker:
      "Save or discard this section's unsaved changes before marking it ready.",
    conflict_blocker:
      "Resolve this section's draft or conflict warning before changing its setup status.",
    status_save_error: "Could not save this section's setup status.",
    later_dialog_title: "Set up this section later?",
    later_dialog_body:
      "You can return at any time. JobClose AI will use its available built-in defaults where supported until you customize this section.",
    later_dialog_keep: "Keep setting it up",
    later_dialog_confirm: "Set up later",
    toast_save_recommended: "Saved with JobClose AI recommended wording.",
    toast_save_customized: "Saved your customized wording.",
    toast_save_generic: "Setup saved.",
    toast_guided_partial:
      "Could not fully save this section. Please review the section and try again.",
    toast_later_failed: "Could not save Set-up-later for this family.",
    toast_outside_failed: "Could not save Handle-outside for this family.",
    explain_generic_error: "Could not save guided setup.",
    family_status_labels: {
      not_started: "Not started",
      in_progress: "In progress",
      ready: "Ready",
      later: "Set up later",
    },
    family_action_labels: {
      not_started: "Set up",
      in_progress: "Continue setup",
      ready: "Edit setup",
      later: "Set up now",
    },
    family_titles: {
      proposal: "Proposal defaults",
      contract: "Contract defaults",
      invoice: "Invoice defaults",
      customer_update: "Customer update defaults",
      closeout: "Closeout defaults",
      warranty: "Warranty defaults",
    },
    family_purposes: {
      proposal: "Standard wording for future proposals and payment terms.",
      contract:
        "Standard contract terms, disclaimers, and customer approval language.",
      invoice: "Standard invoice notes and payment instructions.",
      customer_update:
        "Standard signature, review, and referral wording for contractor-approved updates.",
      closeout:
        "Standard maintenance, closeout, review, and referral wording for future closeout packets.",
      warranty:
        "Standard workmanship warranty terms, manufacturer warranty handling, and warranty-service contact wording.",
    },
  },

  // Slice 2S · Live-path onboarding continuity — Raw-editor family field config translations
  family_config: {
    proposal: {
      title: "Proposal defaults",
      purpose: "Copy that prefills future proposals. Existing proposals remain unchanged.",
      snapshotNote: "These defaults are used only for future proposals. Existing proposals remain unchanged snapshots, and you can still edit the wording before sending.",
      saveButtonLabel: "Save proposal defaults",
      successMessage: "Proposal defaults saved. They'll apply to future proposals.",
      fields: {
        proposal_intro: {
          label: "Proposal intro",
          helper: "The first paragraph the customer reads. Write it in your company's voice.",
          placeholder: "Enter the opening paragraph new proposals should begin with.",
        },
        proposal_footer: {
          label: "Scope notes / proposal footer",
          helper: "Standard scope notes, change-order rules, warranty notes, or other wording that normally appears near the end of a proposal.",
          placeholder: "Enter closing scope notes or standard fine print used on your proposals.",
        },
        deposit_terms: {
          label: "Deposit terms",
          helper: "Explain when and how much the customer must pay before work begins.",
          placeholder: "Explain your deposit requirement in your own words.",
        },
        final_payment_terms: {
          label: "Final payment terms",
          helper: "Explain when the remaining balance becomes due.",
          placeholder: "Explain when the final payment is expected.",
        },
        late_payment_note: {
          label: "Late-payment note",
          helper: "Explain what happens when an unpaid balance remains past its due date.",
          placeholder: "Explain your policy for balances past their due date.",
        },
      },
    },
    contract: {
      title: "Contract defaults",
      purpose: "Standard contract wording your company has reviewed and approved. Existing contracts remain unchanged.",
      snapshotNote: "These defaults are used only for future contracts. Existing contracts remain unchanged snapshots, and you can still edit the wording before sending.",
      saveButtonLabel: "Save contract defaults",
      successMessage: "Contract defaults saved. They'll apply to future contracts.",
      fields: {
        contract_terms: {
          label: "Contract terms",
          helper: "Your company's standard terms for performing the work.",
          placeholder: "Enter your company's standard contract terms.",
        },
        contractor_disclaimer: {
          label: "Contractor disclaimer",
          helper: "Limitations, exclusions, and responsibilities specific to your company or trade.",
          placeholder: "Enter limitations, exclusions, or responsibilities specific to your trade.",
        },
        customer_approval_language: {
          label: "Customer approval language",
          helper: "The acknowledgment shown near the customer's approval or signature.",
          placeholder: "Enter the acknowledgment shown above the signature line.",
        },
      },
    },
    invoice: {
      title: "Invoice defaults",
      purpose: "Standard notes and payment instructions for future invoices. Existing invoices remain unchanged.",
      snapshotNote: "These defaults are used only for future invoices. Existing invoices remain unchanged snapshots, and you can still edit the wording before sending.",
      saveButtonLabel: "Save invoice defaults",
      successMessage: "Invoice defaults saved. They'll apply to future invoices.",
      fields: {
        invoice_notes: {
          label: "Invoice notes",
          helper: "Payment timing, invoice-reference wording, or general notes shown on new invoices.",
          placeholder: "Enter the standard notes that appear on new invoices.",
        },
        payment_instructions: {
          label: "Payment instructions",
          helper: "Explain how the customer should submit payment.",
          placeholder: "Enter the company name and contact details customers should use to submit payment.",
        },
      },
    },
    customer_update: {
      title: "Customer update defaults",
      purpose: "Standard wording used in future contractor-approved customer updates. Existing updates remain unchanged.",
      snapshotNote: "These defaults are used only for future customer updates. Existing updates remain unchanged snapshots, and you can still edit the wording before sending.",
      saveButtonLabel: "Save customer update defaults",
      successMessage: "Customer update defaults saved. They'll apply to future customer updates.",
      fields: {
        customer_update_signature: {
          label: "Signature / contact block",
          helper: "The company or team signature placed at the end of a customer update.",
          placeholder: "Enter the team name and the phone/email the customer should use.",
        },
        review_request: {
          label: "Default review request",
          helper: "Optional wording asking the customer to leave a review.",
          placeholder: "Enter the wording you use to ask for a review.",
        },
        referral_request: {
          label: "Default referral request",
          helper: "Optional wording asking the customer to refer friends, family, or neighbors.",
          placeholder: "Enter the wording you use to ask for referrals.",
        },
      },
    },
    closeout: {
      title: "Closeout & warranty defaults",
      purpose: "Warranty, maintenance, service, and thank-you wording for future closeout packets. Existing packets remain unchanged.",
      snapshotNote: "These defaults are used only for future closeout packets. Existing packets remain unchanged snapshots, and you can still edit the wording before sending.",
      saveButtonLabel: "Save closeout & warranty defaults",
      successMessage: "Closeout and warranty defaults saved. They'll apply to future closeout packets.",
      fields: {
        workmanship_warranty_terms: {
          label: "Workmanship warranty terms",
          helper: "Explain what your company covers for workmanship and for how long.",
          placeholder: "Explain the length and scope of your workmanship warranty.",
        },
        manufacturer_warranty_notes: {
          label: "Manufacturer warranty notes",
          helper: "Explain how manufacturer warranties are handled for installed products or materials.",
          placeholder: "Explain how manufacturer warranties are handled for materials you install.",
        },
        maintenance_notes: {
          label: "Maintenance notes",
          helper: "Tell the customer how to maintain the completed work and preserve applicable warranties.",
          placeholder: "Explain the care steps customers should follow after the job is complete.",
        },
        warranty_contact_notes: {
          label: "Warranty / service contact",
          helper: "Explain how the customer should contact your company with a warranty or service question.",
          placeholder: "Enter the company name and contact details customers should use for warranty or service questions.",
        },
        closeout_notes_template: {
          label: "Closeout notes template",
          helper: "Standard thank-you, completion, or closeout wording used on new packets.",
          placeholder: "Enter the standard closing wording used on new packets.",
        },
        review_request: {
          label: "Post-completion review request",
          helper: "Optional wording asking for a review after the job is complete.",
          placeholder: "Enter the wording you use to request a review after completion.",
        },
        referral_request: {
          label: "Post-completion referral request",
          helper: "Optional wording asking for referrals after the job is complete.",
          placeholder: "Enter the wording you use to request referrals after completion.",
        },
      },
    },
    warranty: {
      title: "Warranty defaults",
      purpose: "Standard workmanship warranty terms, manufacturer warranty handling, and warranty-service contact wording for future project documents. Existing warranty documents remain unchanged snapshots.",
      snapshotNote: "These defaults are used only for future project documents. Existing warranty documents remain unchanged snapshots, and you can still edit the wording before sending.",
      saveButtonLabel: "Save warranty defaults",
      successMessage: "Warranty defaults saved. They'll apply to future project documents.",
      fields: {
        workmanship_warranty_terms: {
          label: "Workmanship warranty terms",
          helper: "Explain what your company covers for workmanship and for how long.",
          placeholder: "Explain the length and scope of your workmanship warranty.",
        },
        manufacturer_warranty_notes: {
          label: "Manufacturer warranty notes",
          helper: "Explain how manufacturer warranties are handled for installed products or materials.",
          placeholder: "Explain how manufacturer warranties are handled for materials you install.",
        },
        warranty_contact_notes: {
          label: "Warranty / service contact",
          helper: "Explain how the customer should contact your company with a warranty or service question.",
          placeholder: "Enter the company name and contact details customers should use for warranty or service questions.",
        },
      },
    },
  },

  // Slice 2S · Live-path onboarding continuity — DocumentDefaultsFamilyCard UI shell
  family_card: {
    unsaved_pill: "Unsaved changes",
    storage_warning:
      "Draft backup is unavailable in this browser. Keep this page open until you save.",
    stale_banner: "A saved version changed after this draft was created.",
    stale_restore: "Restore my draft",
    stale_discard: "Discard old draft",
    conflict_banner: "Saved values changed in another tab or session.",
    load_latest: "Load latest saved values",
    overwrite: "Overwrite with my draft",
    other_tab_banner: "This draft changed in another tab.",
    load_newer: "Load newer draft",
    keep_this_tab: "Keep this tab's changes",
    saving: "Saving…",
    discard_draft: "Discard draft",
    discard_dialog_title: "Discard unsaved changes for this section?",
    discard_dialog_body:
      "This will restore the latest saved values for this section only.",
    keep_editing: "Keep editing",
    discard_changes: "Discard changes",
    overwrite_dialog_title: "Overwrite the newer saved values?",
    overwrite_dialog_body:
      "Someone else (or another tab) saved this section after you started editing. Overwriting will replace their changes with your draft.",
    cancel: "Cancel",
    keep_tab_dialog_title: "Keep this tab's changes?",
    keep_tab_dialog_body:
      "Another tab wrote a different draft for this section. Keeping this tab's changes will overwrite that draft.",
  },

  // Slice 2S · Guided Step 3 panel UI (buttons, dialogs, status, help panels)
  guided: {
    use_recommended: "Use recommended",
    edit_wording: "Edit wording",
    help_me_change_it: "Help me change it",
    set_later: "Set up later",
    back: "Back",
    continue: "Continue",
    save: "Save",
    cancel: "Cancel",
    confirm: "Confirm",
    reset: "Reset",
    close: "Close",
    unsaved_changes: "Unsaved changes",
    discard_draft: "Discard draft",
    handle_outside_closeout: "Handle closeout outside JobClose AI",
    handle_outside_warranty: "Handle warranties outside JobClose AI",
    use_recommended_closeout: "Use JobClose AI recommended closeout setup",
    use_recommended_warranty: "Use JobClose AI recommended warranty setup",
    recommended_wording: "JobClose AI recommended wording",
    recommended_contract_wording: "Recommended contract wording",
    recommended_invoice_wording: "Recommended invoice wording",
    recommended_customer_update_wording: "Recommended customer-update wording",
    recommended_closeout_wording: "Recommended closeout wording",
    recommended_warranty_wording: "Recommended warranty wording",
    company_incomplete: "Company profile is incomplete",
    company_incomplete_body:
      "Add your company name, phone, and email in Business Basics before you can use JobClose AI recommended wording.",
    add_company_info: "Add company information",
    tone: "Tone",
    length: "Length",
    length_shorter: "Shorter",
    length_standard: "Standard",
    length_more_detailed: "More detailed",
    tone_friendly: "Friendly",
    tone_professional: "Professional",
    tone_straightforward: "Straightforward",
    optional_clauses: "Optional clauses",
    optional_emphasis: "Optional emphasis",
    compare_changes: "Compare changes",
    preview_updated: "Preview updated wording",
    conflict_newer_saved: "A newer saved version exists on the server.",
    conflict_choose_reconcile: "Choose how to reconcile:",
    conflict_newer_other_tab: "Newer unsaved changes exist in another tab.",
    conflict_load_latest: "Load latest saved values",
    conflict_keep_editing: "Keep editing",
    conflict_overwrite: "Overwrite with my changes",
    add_trade_override: "Add trade override",
    remove: "Remove",
    trade_overrides_toggle: "Use trade-specific overrides",
    custom_period: "Custom period",
    saving: "Saving…",
    status: {
      recommended: "Recommended",
      customized: "Customized",
      legacy: "Legacy",
      later: "Set up later",
      outside: "Handled outside JobClose AI",
    },
    emphasis: {
      contract: {
        scheduling: "Stronger scheduling protection",
        concealed: "Stronger concealed-condition protection",
        customer_responsibility: "Stronger customer-responsibility wording",
        materials: "Stronger material-selection wording",
        cleanup: "Stronger cleanup wording",
      },
      invoice: {
        amount_due: "Clearer amount-due wording",
        overdue: "Stronger overdue-balance wording",
        instructions: "More detailed payment instructions",
        thank_you: "Warmer customer thank-you wording",
      },
      customer_update: {
        clearer_next_steps: "Clearer next steps",
        warmer_delay: "Warmer delay wording",
        text_message_style: "More concise text-message style",
        warmer_review_referral: "Warmer review and referral wording",
      },
      closeout: {
        clearer_final_next_steps: "Clearer final next steps",
        stronger_punch_list_clarity: "Stronger punch-list clarity",
        warmer_completion: "Warmer completion message",
        clearer_ack: "Clearer customer acknowledgment",
      },
      warranty: {
        clearer_workmanship_coverage: "Clearer workmanship coverage",
        stronger_exclusion_clarity: "Stronger exclusion clarity",
        clearer_manufacturer_distinction: "Clearer manufacturer-warranty distinction",
        clearer_warranty_request_steps: "Clearer warranty-request steps",
      },
    },
  },

  nav: {
    dashboard: "Dashboard",
    customers: "Customers",
    projects: "Projects",
    job_calendar: "Job Calendar",
    estimates: "Estimates",
    proposals: "Proposals",
    contracts: "Contracts",
    invoices: "Invoices",
    customer_updates: "Customer Updates",
    change_orders: "Change Orders",
    work_orders: "Work Orders",
    selections: "Selections",
    closeout: "Closeout",
    company_profile: "Company Profile",
    document_defaults: "Document Defaults",
    closeout_defaults: "Closeout Defaults",
    pricing_settings: "Pricing Settings",
    account: "Account / Subscription",
    meet_levi: "Meet Levi",
    company_and_settings: "Company & Settings",
    daily_primary_work: "Daily · Primary work",
    attention: "Attention",
    help_company: "Help · Company",
    custom_job_with_levi: "Custom Job with Levi",
    estimates_awaiting: "Estimates awaiting review",
    proposals_awaiting: "Proposals awaiting response",
    contracts_awaiting: "Contracts awaiting signature",
    invoices_unpaid: "Unpaid invoices",
    invoices: "Invoices",
    closeout_warranty: "Closeout / Warranty",
    job_workflow: "Job workflow",
    customer_jobsite: "Customer & jobsite",
    setup: "Setup",
    help: "Help",
    set_up_your_business: "Set Up Your Business",
    continue_setup: "Continue Setup",
    review_setup: "Review Setup",
    sign_out: "Sign out",
    home: "Home",
    customers_short: "Customers",
    projects_short: "Projects",
    more_short: "More",
    levi_short: "Levi",
    levi_open: "Open Levi",
    mobile_bottom_nav: "Primary navigation",
  },
  common: {
    back: "Back",
    continue: "Continue",
    save: "Save",
    cancel: "Cancel",
    edit: "Edit",
  },

  // Slice 2D4 · Core app-language launch coverage. Shared actions +
  // page-level chrome dictionary for the 14 authorized routes.
  actions: {
    new: "New",
    create: "Create",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    view: "View",
    close: "Close",
    back: "Back",
    send: "Send",
    print: "Print",
    download: "Download",
    email: "Email",
    approve: "Approve",
    generate: "Generate",
    mark_paid: "Mark Paid",
    add: "Add",
    open: "Open",
  },
  status: {
    loading: "Loading…",
    saving: "Saving…",
    saved: "Saved",
    not_found: "Not found",
  },
  pages: {
    dashboard: {
      title: (name) => `Welcome back, ${name}.`,
      welcome_fallback_name: "there",
      subtitle_default: "Your JobClose AI workspace.",
    },
    customers: {
      title: "Customers",
      subtitle: "Every contact JobClose AI tracks for your business — homeowners, property managers, and business accounts.",
      new_title: "New customer",
      new_subtitle: "Individuals or businesses.",
      detail_loading_title: "Customer",
      detail_loading_subtitle: "Loading…",
      detail_not_found_subtitle: "Not found",
      new_btn: "New Customer",
      empty_title: "No customers yet",
      empty_body: "Add homeowners, property managers, or business accounts to start creating jobs.",
    },
    projects: {
      title: "Projects",
      subtitle: "Track each job from estimate to closeout.",
      new_btn: "New Project",
      empty_title: "No projects yet",
      empty_body: "Start a job to see it here.",
      detail_loading_title: "Project",
      detail_loading_subtitle: "Loading…",
    },
    estimates: {
      title: "Estimates",
      subtitle: "Draft, review, send.",
      new_btn: "New Estimate",
      empty_title: "No estimates yet",
      empty_body: "Draft your first estimate from a project.",
      detail_loading_title: "Estimate",
      detail_loading_subtitle: "Loading…",
    },
    proposals: {
      title: "Proposals",
      subtitle: "Customer-facing proposals with your terms.",
      detail_loading_title: "Proposal",
      detail_loading_subtitle: "Loading…",
      empty_title: "No proposals yet",
      empty_body: "Generate a proposal from an approved estimate.",
    },
    contracts: {
      title: "Contracts",
      subtitle: "Contract-ready drafts. Always have your attorney review.",
      detail_loading_title: "Contract",
      detail_loading_subtitle: "Loading…",
      empty_title: "No contracts yet",
      empty_body: "Generate a contract from an accepted proposal.",
    },
    invoices: {
      title: "Invoices",
      subtitle: "Deposit · Progress · Final · Change order.",
      new_btn: "New Invoice",
      new_title: "New invoice",
      detail_loading_title: "Invoice",
      detail_loading_subtitle: "Loading…",
      empty_title: "No invoices yet",
      empty_body: "Create one from a signed contract or directly here.",
    },
    customer_updates: {
      title: "Customer Updates",
      subtitle: "Bilingual messages — English & Spanish.",
      empty_title: "No updates yet",
      empty_body: "Send your first customer update from a project.",
    },
    work_orders: {
      title: "Work Orders / Crew Packets",
      subtitle: "Crew-facing packets. Scope, quantities, and tasks — no pricing, cost, or margin.",
      detail_loading_title: "Crew Packet",
      detail_loading_subtitle: "Loading…",
      empty_title: "No work orders yet",
      empty_body: "Generate a crew packet from a project.",
    },
    closeout: {
      title: "Closeout / Warranty Packets",
      subtitle: "Give customers a clean final packet with completion notes, warranty expectations, maintenance guidance, and next steps.",
      detail_loading_title: "Closeout Packet",
      detail_loading_subtitle: "Loading…",
      empty_title: "No closeout packets yet",
      empty_body: "Generate a closeout packet when a job wraps.",
    },
    company_profile: {
      title: "Company Profile",
      subtitle: "Contractor identity + branding used on your customer-facing documents.",
      loading_subtitle: "Setup · Contractor identity + branding",
    },
    pricing: {
      title: "Pricing Settings",
      subtitle: "Maintenance for your saved pricing. Initial setup lives in the Contractor Setup Wizard.",
      loading_title: "Pricing Settings",
      loading_subtitle: "Loading…",
    },
    account: {
      title: "Account & Subscription",
      subtitle: "From estimate to contract to invoice — without losing control of your pricing.",
    },
    meet_levi: {
      title: "Meet Levi",
      subtitle: "Your coworker at JobClose AI.",
      identity_line: "Your coworker at JobClose AI.",
      open_assistant: "Meet Levi",
      does_label: "What Levi does",
      does_not_label: "What Levi does NOT do",
      where_label: "Where to find Levi",
    },
  },
  // Slice 2D4 continuation · Enumerated form / list / status labels.
  invoice_types: {
    deposit: "Deposit",
    progress: "Progress",
    final: "Final",
    change_order: "Change order",
  },
  invoice_form: {
    project: "Project",
    choose_project: "Choose project…",
    type: "Type",
    amount: "Amount",
    due_date: "Due date",
    customer_language: "Customer language",
    notes: "Notes",
    cancel: "Cancel",
    create: "Create",
    toast_choose_project: "Select a project",
    toast_created: "Invoice created",
    toast_create_failed: "Could not create",
    columns: {
      customer: "Customer",
      type: "Type",
      amount: "Amount",
      status: "Status",
      created: "Created",
    },
  },
  updates_form: {
    generate_heading: "Generate update",
    project: "Project",
    choose_project: "Choose project…",
    type: "Type",
    language: "Language",
    custom_note: "Custom note (optional)",
    preview: "Preview",
    cancel: "Cancel",
    generate: "Generate",
    empty_dash: "—",
    new_btn: "New Update",
    email_update: "Email update",
  },
  update_type_labels: {
    estimate_received: "Estimate received",
    proposal_sent: "Proposal sent",
    contract_ready: "Contract ready",
    deposit_invoice_sent: "Deposit invoice sent",
    project_scheduled: "Project scheduled",
    project_delay: "Project delay",
    change_order_needed: "Change order needed",
    final_invoice_sent: "Final invoice sent",
    thank_you_review: "Thank you / review request",
  },
  contracts_list_page: {
    view_proposals_btn: "View proposals",
    columns: {
      customer: "Customer",
      total: "Total",
      status: "Status",
      created: "Created",
    },
    status_labels: {
      draft: "Draft",
      sent: "Sent",
      signed: "Signed",
      voided: "Voided",
      expired: "Expired",
    },
  },
  contract_detail: {
    back: "Back",
    preview_customer_view: "Preview Customer View",
    print: "Print",
    download_pdf: "Download PDF",
    copy_customer_link: "Copy customer link",
    email_link_to_customer: "Email link to customer",
    create_deposit_invoice: "Create Deposit Invoice",
    sign_contract_btn: "Sign Contract",
    sign_input_placeholder: "Type full legal name to sign",
    subtitle_prefix: (customerName) => `Contract · ${customerName}`,
    // Prompts + toasts
    toast_sign_missing_name: "Enter the customer's full legal name to sign",
    toast_signed: "Contract marked signed.",
    toast_sign_failed: "Could not sign",
    toast_deposit_created: "Deposit invoice created",
    toast_deposit_failed: "Could not create invoice",
    toast_link_copied: "Public link copied — share with your customer",
    prompt_copy_link: "Copy this link:",
    prompt_email_to: "Send contract email to:",
    toast_email_mock: "Email queued (mock sent)",
    toast_email_sent: "Email sent",
    toast_email_failed: "Could not send email",
    toast_downloaded: (filename) => `Downloaded ${filename}`,
    toast_pdf_failed: "Could not generate PDF",
  },
  contract_doc: {
    your_company_fallback: "Your Company",
    kind_label: "Contract",
    agreement_with: (name) => `Agreement with ${name}`,
    date_label: "Date",
    status_label: "Status",
    section_scope: "Approved scope",
    section_terms: "Contract terms",
    section_change_orders: "Change orders",
    section_customer_responsibilities: "Customer responsibilities",
    section_exclusions: "Exclusions",
    contract_price: "Contract price",
    deposit_due_prefix: "Deposit due",
    balance_prefix: "Balance",
    includes_allowances: "Includes allowances",
    tm_reserve_line: (amount) => `Possible T&M reserve: ${amount} — not included in firm total`,
    tm_section_title: "Possible Additional Work (Time & Materials)",
    tm_col_item: "Item",
    tm_col_qty: "Qty",
    tm_col_est_total: "Est. total",
    tm_legend: "These items are estimates only and may be billed if the work becomes necessary.",
    contractor: "Contractor",
    customer_signature: "Customer signature",
    signed_on: (date) => `Signed ${date}`,
    legal_disclaimer_heading: "Legal disclaimer · Aviso legal",
    // Status enum labels rendered inline in the doc header.
    status_labels: {
      draft: "Draft",
      sent: "Sent",
      signed: "Signed",
      voided: "Voided",
      expired: "Expired",
    },
  },
  invoice_detail: {
    back: "Back",
    print: "Print",
    download_pdf: "Download PDF",
    email_link_to_customer: "Email link to customer",
    mark_paid: "Mark Paid",
    subtitle: (type, status) => `${type} · ${status}`,
    toast_marked_paid: "Invoice marked paid",
    toast_mark_paid_failed: "Failed",
    prompt_email_to: "Send invoice email to:",
    toast_email_mock: "Email queued (mock sent)",
    toast_email_sent: "Email sent",
    toast_email_failed: "Could not send email",
    toast_downloaded: (filename) => `Downloaded ${filename}`,
    toast_pdf_failed: "Could not generate PDF",
  },
  invoice_doc: {
    your_company_fallback: "Your Company",
    kind_label: "Invoice",
    bill_to: "Bill to",
    type_label: "Type",
    issued_label: "Issued",
    due_label: "Due",
    total_due: "Total due",
    pay_online_placeholder: "Pay online (placeholder)",
    notes_heading: "Notes",
    // Invoice enums (invoice_type + payment_status). Displayed on the
    // customer-facing doc so they are keyed by doc.language.
    invoice_type_labels: {
      deposit: "Deposit",
      progress: "Progress",
      final: "Final",
      change_order: "Change order",
    },
    payment_status_labels: {
      pending: "Pending",
      sent: "Sent",
      unpaid: "Unpaid",
      partial: "Partial",
      paid: "Paid",
      overdue: "Overdue",
      void: "Void",
      refunded: "Refunded",
    },
    empty_dash: "—",
  },
  customer_updates_page: {
    prompt_email_to: "Send customer update email to:",
    toast_choose_project: "Choose a project",
    toast_created: "Customer update generated",
    toast_create_failed: "Could not create",
    toast_email_mock: "Email queued (mock sent)",
    toast_email_sent: "Email sent",
    toast_email_failed: "Could not send email",
  },
  customer_preview: {
    title_proposal: "Customer preview · Proposal",
    title_contract: "Customer preview · Contract",
    copy_btn: "Copy preview as plain text",
    copy_title: "Copy preview as plain text",
    readonly_notice: "Read-only preview · no email sent",
    secure_link_note: "This is what your customer sees on the secure link.",
    toast_copied: "Preview copied.",
    prompt_copy: "Copy this preview:",
    sr_dialog_desc: (kind) => `Read-only customer view of the ${kind}. No data is changed.`,
  },
  public_docs: {
    secure_link: "Secure customer link",
    loading: "Loading…",
    link_expired_body: "The link has expired. Please ask the contractor for a fresh link.",
    link_missing_body: "We couldn't find what this link points to. Please double-check the URL with your contractor.",
    powered_by_line: "Powered by JobClose AI · Built by a contractor, for contractors.",
    privacy_link: "Privacy",
    terms_link: "Terms",
    print_save_pdf: "Print / Save PDF",
    link_expires_prefix: (date) => `Link expires ${date}.`,
    // Proposal-specific customer-facing labels.
    proposal_header_line: "Proposal from",
    proposal_kind_label: "Proposal",
    proposal_status_label: "Status",
    proposal_scope_of_work: "Scope of work",
    proposal_included: "Included",
    proposal_excluded: "Excluded",
    proposal_pricing: "Pricing",
    proposal_item: "Item",
    proposal_qty: "Qty",
    proposal_total: "Total",
    proposal_subtotal: "Subtotal",
    proposal_tax: "Tax",
    proposal_deposit: "Deposit",
    proposal_payment_schedule: "Payment schedule",
    proposal_timeline: "Timeline",
    proposal_next_steps: "Next steps",
    proposal_next_steps_fallback: "Reply to your contractor to approve this proposal.",
    proposal_status_labels: {
      draft: "Draft",
      sent: "Sent",
      approved: "Approved",
      expired: "Expired",
      revoked: "Revoked",
    },
    // Contract-specific customer-facing labels.
    contract_header_line: "Contract from",
    contract_kind_label: "Contract",
    contract_agreement_with: (name) => `Agreement with ${name}`,
    contract_from_prefix: "From",
    contract_issued_prefix: "Issued",
    contract_awaiting_signature: "Awaiting signature",
    contract_signed: "Signed",
    contract_section_scope: "Approved scope",
    contract_section_terms: "Contract terms",
    contract_section_change_orders: "Change orders",
    contract_section_customer_responsibilities: "Customer responsibilities",
    contract_section_exclusions: "Exclusions",
    contract_price: "Contract price",
    contract_deposit_due_prefix: "Deposit due",
    contract_balance_prefix: "Balance",
    contract_signature_heading: "Customer signature",
    contract_sign_input_label: "Type your full legal name to sign",
    contract_sign_input_placeholder: "e.g. Jane M. Smith",
    contract_sign_btn: "Sign Contract",
    contract_sign_submitting: "Submitting…",
    contract_signed_on: (date) => `Signed ${date}`,
    contract_err_missing_name: "Please type your full legal name.",
    contract_err_sign_failed: "Could not submit signature.",
    contract_legal_disclaimer_heading: "Legal disclaimer · Aviso legal",
    // Phase 10 · PR-1 — Public change-order review + sign copy (EN)
    co_header_line: "Change Order from",
    co_kind_label: "Change Order",
    co_number_prefix: "Change Order",
    co_awaiting_signature: "Awaiting your signature",
    co_partially_signed: "Awaiting final signature",
    co_fully_executed: "Fully executed",
    co_section_scope_summary: "What is changing",
    co_financial_delta: "Change to contract price",
    co_revised_contract_prefix: "New contract total",
    co_schedule_delta: "Schedule change",
    co_days_added_prefix: "Days added",
    co_new_completion_prefix: "New target completion",
    co_signature_heading: "Your electronic signature",
    co_consent_language: "By typing your full legal name below, you agree that (1) your typed name constitutes your electronic signature, (2) you consent to conduct this transaction electronically, (3) this electronic signature has the same legal effect as a handwritten signature, and (4) a copy of the completed document will be delivered to you for your records.",
    co_sign_input_label: "Type your full legal name to sign this change order",
    co_sign_input_placeholder: "e.g. Jane M. Smith",
    co_sign_btn: "Sign Change Order",
    co_err_missing_name: "Please type your full legal name.",
    co_err_sign_failed: "Could not submit signature.",
    // Phase 1 shared customer-document standard · Public invoice
    // (`/i/:token`). View / record / delivery only — never a Pay Now.
    invoice_kind_label: "Invoice",
    invoice_bill_to: "Bill to",
    invoice_type_label: "Type",
    invoice_due_label: "Due",
    invoice_line_items: "Line items",
    invoice_amount_due: "Amount due",
    invoice_balance_due: "Balance due",
    invoice_payment_instructions: "Payment instructions",
    invoice_notes_heading: "Notes",
    invoice_type_labels: {
      deposit: "Deposit",
      progress: "Progress",
      final: "Final",
      change_order: "Change order",
    },
    invoice_status_labels: {
      pending: "Pending",
      sent: "Sent",
      unpaid: "Unpaid",
      partial: "Partial",
      paid: "Paid",
      overdue: "Overdue",
      void: "Void",
      refunded: "Refunded",
    },
  },
  customers_form: {
    type_label: "Type",
    individual: "Individual / Homeowner",
    business: "Business / Organization",
    business_name: "Business name",
    first_name: "First name",
    last_name: "Last name",
    phone: "Phone",
    email: "Email",
    save_customer: "Save customer",
    saving: "Saving…",
    cancel: "Cancel",
    search_placeholder: "Search by name, business, phone, or email…",
    loading: "Loading…",
    empty_note: "No customers yet. Add your first customer to get started.",
    no_contact_yet: "No contact info yet",
    showing_recent: (shown, total) => `Showing your ${shown} most recent · ${total} total`,
    showing_search: (shown) => `${shown} match${shown === 1 ? "" : "es"}`,
    hidden_hint: (n) => `${n} more · search to find any customer`,
    project_count_short: (n) => `${n} project${n === 1 ? "" : "s"}`,
  },
  projects_list: {
    empty_title: "No projects yet",
    empty_body_short: "Start a new estimate to create your first project.",
    start_estimate: "Start Estimate",
    drafts_heading: "Draft Estimates",
    drafts_help: "You started these but never finished. Resume to add the rest of the details, or delete if you no longer need them.",
    resume: "Resume",
    delete: "Delete draft",
    keep_draft: "Keep draft",
    deleting: "Deleting…",
    untitled_customer: "Untitled",
    columns: {
      customer: "Customer",
      job: "Job",
      started: "Started",
      actions: "Actions",
      address: "Address",
      created: "Created",
    },
    active_heading: "Active Projects",
    active_empty_title: "No active projects yet",
    active_empty_body_drafts_present: "Resume a draft above or start a fresh estimate.",
    active_empty_body_no_drafts: 'Click "New Estimate" to start one.',
    open: "Open",
    search_placeholder: "Search by customer, job, or address…",
    helper_body: "Open a project to view its estimates, proposals, contracts, invoices, customer updates, and crew packets in one place.",
    toast_draft_deleted: "Draft deleted",
    toast_draft_delete_failed: "Could not delete draft",
    delete_dialog_title: (name) => `Delete draft for "${name}"?`,
    delete_dialog_body: "This cannot be undone. Any notes, photos, or files attached to this draft will be permanently removed.",
  },
  work_orders: {
    loading_body: "Loading crew packets...",
    goto_projects: "Go to projects",
    columns: {
      customer_project: "Customer / Project",
      linked_estimate: "Linked estimate",
      generated: "Generated",
      status: "Status",
    },
    view_packet: "View packet",
    crew_safe_headline: "Crew-safe packet.",
    crew_safe_body: "Scope and quantities only. Pricing, cost, markup, margin, supplier, SKU, Double Check, and AI advisory data are intentionally excluded.",
    sections: {
      job_info: "Job info",
      scope: "Scope",
      materials: "Materials needed",
      tasks: "Tasks",
      access_notes: "Access notes",
      safety_notes: "Safety notes",
      schedule_notes: "Schedule notes",
      crew_assigned: "Crew assigned",
    },
    save_changes: "Save changes",
    saving: "Saving...",
    back_link: "All crew packets",
  },
  closeout: {
    loading_body: "Loading closeout packets...",
    back_link: "All packets",
  },
  // ----- Slice 2S · language-continuity keys (top-level namespaces
  // so useLang()/t() consumers reference them as `login.*`, `signup.*`,
  // `job_detail.*`, etc.  Matches the ES tree below key-for-key.) -----
  login: {
    hero_overline: "Built by a contractor, for contractors",
    hero_headline: "Bring your voice notes. Keep your pricing. Ship real quotes.",
    hero_body: "Your pricing. AI support. Final approval by you.",
    copyright: "JobClose AI",
    panel_overline: "Sign in",
    panel_heading: "Welcome back.",
    email_label: "Email",
    email_placeholder: "you@company.com",
    password_label: "Password",
    password_placeholder: "Your password",
    submit: "Sign in",
    submitting: "Signing in...",
    error_default: "Sign in failed. Please try again.",
  },
  signup: {
    hero_overline: "From voice note to proposal",
    hero_headline: "Turn scope conversations into approved proposals.",
    hero_body:
      "Your pricing profile drives every AI suggestion. You have final approval on every line.",
    copyright: "JobClose AI",
    panel_overline: "Create your account",
    panel_heading: "Start in two minutes.",
    already_have: "Already have an account?",
    go_login: "Sign in",
    full_name_placeholder: "Your full name",
    company_placeholder: "Company name",
    email_placeholder: "Work email",
    password_placeholder: "Choose a password (min 6 chars)",
    submit: "Create account",
    submitting: "Creating account...",
    error_default: "Could not create account. Please try again.",
  },
  job_detail: {
    not_available: "This job isn't available.",
    customer: "Customer",
    property: "Property",
    type: "Type",
    status: "Status",
    linked_estimate: "Linked estimate",
    created: "Created",
    updated: "Updated",
    loading: "Loading job...",
    back_link: "All jobs",
  },
  material_price_book: {
    title: "Material Price Book",
    subtitle:
      "Your saved material costs — used by Levi to suggest realistic pricing.",
    contractor_internal_prefix: "Contractor-internal.",
    contractor_internal_body:
      "This is your reference — customers never see these numbers directly.",
    filter_trade: "Trade",
    filter_system: "System type",
    filter_supplier: "Supplier",
    filter_all_trades: "All trades",
    filter_all_systems: "All system types",
    filter_all_suppliers: "All suppliers",
    search_placeholder: "Search material name, SKU, or supplier...",
    empty_title: "No materials in your Price Book yet",
    empty_body:
      "Add the materials you use most so Levi can quote from your real numbers, not internet averages.",
    add_item: "Add material",
    column_material: "Material",
    column_unit: "Unit",
    column_supplier: "Supplier",
    column_cost: "Your cost",
    column_updated: "Updated",
    column_actions: "Actions",
    edit: "Edit",
    remove: "Remove",
    save: "Save",
    cancel: "Cancel",
    saving: "Saving...",
    confirm_remove_title: "Remove this material?",
    confirm_remove_body:
      "This won't affect existing estimates that already used it.",
    confirm_remove_action: "Remove material",
    toast_saved: "Material saved.",
    toast_removed: "Material removed.",
    toast_save_failed: "Could not save. Please try again.",
    // Slice 2S · deep interaction keys
    filter_brand: "Brand",
    filter_all_brands: "All brands",
    filter_status: "Status",
    filter_active_only: "Active only",
    filter_archived_only: "Archived only",
    filter_all_status: "All",
    filter_search: "Search",
    search_hint: "product / brand / SKU",
    loading: "Loading...",
    empty_body_short: "Your Material Price Book is empty",
    empty_hint: "Add materials to speed up estimates and keep costs consistent.",
    col_product: "Product",
    col_trade_system: "Trade / System",
    col_supplier_brand: "Supplier / Brand",
    col_cost: "Cost",
    col_waste_pct: "Waste %",
    col_unit: "Unit",
    col_status: "Status",
    col_updated: "Updated",
    row_archived: "Archived",
    row_active: "Active",
    row_edit: "Edit",
    row_archive: "Archive",
    row_restore: "Restore",
    modal_add_title: "Add material",
    modal_edit_title: "Edit material",
    field_trade_required: "Trade *",
    field_trade_select: "Select trade...",
    field_system_type: "System type",
    field_category: "Category",
    field_supplier_vendor: "Supplier / Vendor",
    field_brand_manufacturer: "Brand / Manufacturer",
    field_product_required: "Product / Spec *",
    field_sku_optional: "SKU (optional)",
    field_sku_placeholder: "Supplier SKU",
    field_unit_of_measure: "Unit of measure",
    field_coverage_per_unit: "Coverage per unit",
    field_contractor_cost_required: "Contractor cost *",
    field_default_waste_pct: "Default waste %",
    field_markup_optional: "Markup % (optional)",
    field_active: "Active",
    field_notes: "Notes",
    field_notes_placeholder: "Internal notes (never shown to customer)",
    modal_save_changes: "Save changes",
    validation_trade_product_required: "Trade and product are required",
    toast_updated: "Material updated",
    toast_added: "Material added",
    toast_archived: "Archived",
    toast_archive_failed: "Archive failed",
    toast_reactivated: "Re-activated",
    toast_reactivate_failed: "Could not re-activate",
    banner_contractor_only_body:
      "Supplier, brand, SKU, contractor cost, waste %, markup, and coverage stay on your side only — none of these fields appear on the customer's proposal, contract, PDF, or public link.",
    placeholder_system_type: "e.g. Asphalt Shingle",
    placeholder_category: "e.g. Shingles, Fasteners",
    placeholder_supplier: "e.g. ABC Supply",
    placeholder_brand: "e.g. GAF",
    placeholder_product: "e.g. Timberline HDZ",
    placeholder_unit: "e.g. sq, lf, gal, each",
    placeholder_coverage: "e.g. 33.3 sf per bundle",
    placeholder_markup: "inherit profile default",
    active_in_use: "In use",
    confirm_archive_prompt: (product) =>
      `Archive "${product}"? It won't be deleted — you can toggle Active back on later.`,
  },
  closeout_defaults_page: {
    title: "Closeout Defaults",
    subtitle:
      "Default warranty, punch-list, and review-request copy applied to new closeout packets. You can still edit any packet individually.",
    save: "Save defaults",
    saving: "Saving...",
    toast_saved: "Closeout defaults saved.",
    toast_failed: "Could not save. Please try again.",
  },
  document_defaults_page: {
    title: "Document Defaults",
    subtitle:
      "Default terms and disclaimers used when generating proposals, contracts, invoices, and change orders. You can still edit any document individually.",
    save: "Save defaults",
    saving: "Saving...",
    toast_saved: "Document defaults saved.",
    toast_failed: "Could not save. Please try again.",
  },
  onboarding: {
    save_exit: "Save & Exit",
    back: "Back",
    next: "Next",
    continue: "Continue",
    finish: "Finish",
    skip: "Skip",
    skip_for_now: "Skip for now",
    set_up_later_title: "Set up this section later?",
    set_up_later_body:
      "You can come back and finish this from Setup anytime. Levi will keep flagging what's still missing.",
    set_up_later_confirm: "Set up later",
    set_up_later_cancel: "Keep going",
    saving: "Saving...",
    saved: "Saved.",
    save_failed: "Could not save. Please try again.",
    required_field: "This field is required.",
    loading: "Loading your setup...",
    step_label: (n, total, name) => `Step ${n} of ${total} · ${name}`,
    step_welcome: "Welcome",
    step_business_trades: "Business & Trades",
    step_company_profile: "Company Profile",
    step_trade_setup: "Trade Setup",
    step_pricing_setup: "Pricing Setup",
    step_documents: "Documents & Customer Defaults",
    welcome_headline: "Welcome to JobClose AI.",
    welcome_body:
      "In the next few minutes we'll set up your business, trades, and pricing so Levi can help you quote real jobs with your real numbers.",
    business_trades_headline: "Tell us about your business.",
    business_trades_body:
      "This shows up on customer-facing proposals, contracts, and invoices. You can polish the rest (address, license, brand) any time in Company Profile.",
    trade_pick_headline: "Which trades does your crew handle?",
    trade_pick_body:
      "Pick every trade your crew handles — JobClose AI uses these to pull the right pricing rules and Levi advisory hints.",
    pricing_setup_headline: "Default pricing style",
    pricing_setup_body:
      "These are the numbers JobClose AI uses when a line item doesn't override them. You can revise anytime under Pricing Settings.",
    documents_headline: "Documents & Customer Defaults",
    documents_body:
      "How your proposals, contracts, and invoices look and speak to customers.",
    documents_complete: "Documents & Customer Defaults complete",
    quickstart_step1_overline: "Company Basics",
    quickstart_step1_headline: "Tell us about your business.",
    quickstart_step2_overline: "Pricing Basics",
    quickstart_step2_headline: "How do you like to price?",
    quickstart_company_logo_optional: "Company logo (optional)",
  },
  account: {
    current_plan: "Current plan",
    status: "Status",
    cycle: "Cycle",
    monthly: "Monthly",
    annual: "Annual",
    save_two_months: "save 2 months",
    available_plans: "Available plans",
    additional_users: "Additional internal users: $20/mo each.",
    setup_packages: "Setup packages & onboarding",
    setup_packages_body: "Setup packages are reviewed before onboarding so your pricing profile, team, and workflow are configured correctly.",
    no_surprise_fees: "Setup is always reviewed before onboarding. No surprise setup fees.",
    contact_sales: "Contact sales",
    subscribe: "Subscribe",
    current_plan_action: "Current plan",
    redirecting: "Redirecting...",
    customer_doc_lang_heading: "Customer document language",
    customer_doc_lang_body: "Default language used when generating customer-facing documents (proposals, contracts, invoices, updates). You can still change the language on any individual document. Independent from the app language in the top bar.",
    // Slice 2R · Stripe subscription surface
    manage_billing: "Manage billing",
    renews_on: "Next renewal",
    cancels_on: "Cancels on",
    set_to_cancel: "Set to cancel at period end",
    launch_offer_active: "Launch offer",
    launch_offer_badge: "Launch offer",
    launch_offer_headline: "JobClose AI launch pricing",
    launch_offer_body:
      "$129/month · 2 full users included · +$20/month per additional user.",
    launch_offer_price_note: "$129/mo · 2 users included",
    setup_fee_separate: "Onboarding is a separate one-time purchase.",
    // Slice 2S · additional Account-page keys.
    most_popular_badge: "MOST POPULAR",
    setup_pkgs_heading: "Setup packages & onboarding",
    setup_pkgs_body:
      "Every setup package is reviewed with your team before onboarding so your pricing profile, trades, and workflow are configured correctly.",
    no_surprise_fees_chip: "No surprise setup fees",
    setup_pkg_basic_name: "Basic Setup",
    setup_pkg_basic_body:
      "Right-fit for solo contractors — company profile, one trade, and pricing defaults.",
    setup_pkg_pro_name: "Pro Setup",
    setup_pkg_pro_body:
      "Team Contractor tier — full pricing profile, multi-trade setup, and document defaults.",
    setup_pkg_premium_name: "Premium Setup",
    setup_pkg_premium_body:
      "Growth tier — full pricing profile, multi-crew setup, and price book seeding.",
    setup_pkg_custom_name: "Custom Setup",
    setup_pkg_custom_body: "Larger teams — quoted upfront with your ops lead.",
    starting_at_setup: "starting at $499 setup",
    quoted_upfront: "quoted upfront",
    website_starter_heading: "Optional Add-On · Website Starter",
    website_starter_body:
      "Simple contractor-branded website with your services, service areas, and JobClose AI contact form. Advanced edits and custom copywriting are quoted separately.",
    status_labels: {
      trial: "Trial",
      active_test_mode: "Active (test mode)",
      active_placeholder: "Active",
      active: "Active",
      trialing: "Trialing",
      past_due: "Past due",
      canceled: "Canceled",
      incomplete: "Incomplete",
      incomplete_expired: "Expired",
      unpaid: "Unpaid",
      paused: "Paused",
    },
  },
  dashboard: {
    brand_promise_headline: "From voice note to proposal. Powered by AI. Controlled by you.",
    brand_promise_overline: "Brand promise",
    brand_bullets: [
      "· Your pricing profile drives every AI suggestion.",
      "· Green, yellow, and red estimate safeguards.",
      "· Bilingual customer updates (EN / ES).",
      "· Contract-ready drafts — never legal advice.",
    ],
    recent_projects: "Recent projects",
    active_projects_h2: "Active Projects",
    view_all: "View all",
    kpi_active_projects: "Active Projects",
    kpi_draft_estimates: "Draft Estimates",
    kpi_sent_proposals: "Sent Proposals",
    kpi_open_invoices: "Open Invoices",
    kpi_contracts: "Contracts",
    kpi_customer_updates: "Customer Updates",
    paid_revenue: "Paid revenue",
    paid_revenue_hint: "Across all paid invoices",
    empty_body: "Kick off your first project from the job walk — voice notes, photos, measurements — then let AI organize your scope.",
    start_new_estimate: "Start a new estimate",
    new_estimate: "New Estimate",
    pricing_profile_ready: "Pricing profile ready",
    verified_on: "Verified on",
    review_pricing_profile: "Review pricing profile",
    lifecycle_swipe_hint: "Swipe",
    custom_job_shortcut_overline: "Shortcut",
    custom_job_shortcut_title: "Custom Job with Levi",
    custom_job_shortcut_body: "Activate Levi Vision for odd jobs & one-off work",
    custom_job_eyebrow: "Custom Job with Levi",
    custom_job_title: "Have a job out of your normal scope?",
    custom_job_body: "Levi will help you organize the scope and recommend a price range for the work. You review it, adjust it, and decide the final price.",
    custom_job_cta: "Start a Custom Job",
    custom_job_alt: "A contractor showing Levi a fallen tree — the kind of unusual, out-of-scope job Levi helps organize.",
    attention_overline: "Today · What needs your attention",
    attention_headline: "What needs your attention",
    attention_composed_hint: "Composed from your existing data",
    attention_pill_draft: "Draft",
    attention_pill_awaiting_response: "Awaiting response",
    attention_pill_awaiting_signature: "Awaiting signature",
    attention_pill_open_invoice: "Open invoice",
    attention_estimate_title: (name) => `${name} · draft estimate`,
    attention_estimate_title_generic: "Draft estimate ready for review",
    attention_estimate_hint: "Ready for your review",
    attention_proposal_title: (name) => `${name} · proposal`,
    attention_proposal_title_generic: "Proposal awaiting customer response",
    attention_proposal_hint: "Sent — awaiting customer response",
    attention_contract_title: (name) => `${name} · contract`,
    attention_contract_title_generic: "Contract awaiting signature",
    attention_contract_hint: "Awaiting signature",
    attention_invoice_title: (name) => `${name} · invoice`,
    attention_invoice_title_generic: "Open invoice",
    attention_invoice_hint: "Unpaid",
    workflow_overline: "Full workflow",
    workflow_headline: "Estimate → Proposal → Contract → Invoice → Closeout",
    workflow_body: "JobClose AI is the full job workflow — not just estimates.",
    project_lifecycle_overline: "Project lifecycle",
    // Post-QA UX refinement — Project Lifecycle Continuity. Copy for
    // the inline multi-item chooser when a project has more than one
    // artifact at a given stage. Every stage stays scoped to THIS
    // project — the global lists remain the left-nav's job.
    lifecycle_count_items: (n) => `${n} in this project`,
    lifecycle_choose_one: "Choose one",
    lifecycle_cancel: "Cancel",
    status_live: "Live",
    status_coming_next: "Coming next",
    step_estimate: "Estimate",
    step_proposal: "Proposal",
    step_contract: "Contract",
    step_customer_updates: "Customer Updates",
    step_change_orders: "Change Orders",
    step_work_orders: "Work Orders",
    step_invoice: "Invoice",
    step_closeout: "Closeout / Warranty",
    levi_card_title: "Meet Levi, your coworker at JobClose AI",
    levi_card_teaser: "Your coworker at JobClose AI. Help with intake, estimates, and follow-up — always through workflows you've approved.",
    levi_card_learn_more: "Learn more",
  },
  // Slice 2D4 final pass · Company Profile / Pricing / Work Orders / Closeout.
  company_form: {
    loading_body: "Loading company profile…",
    section_identity: "Company identity",
    section_address: "Business address",
    section_license: "License & service area",
    section_trades: "Trade / service categories",
    section_branding: "Branding",
    company_name: "Company name",
    phone: "Phone",
    email: "Email",
    website: "Website",
    timezone: "Timezone",
    tz_auto: "— Auto-detect on save —",
    street: "Street",
    city: "City",
    state: "State",
    zip: "ZIP",
    license_number: "License number",
    license_expiration: "License expiration",
    license_type: "License type",
    service_area: "Service area",
    trade_categories: "Trade / service categories",
    add_custom_trade: "Add trade",
    add: "Add",
    logo: "Contractor logo",
    upload_logo: "Upload logo",
    replace_logo: "Replace logo",
    remove_logo: "Remove logo",
    primary_color: "Primary color",
    accent_color: "Accent color",
    save: "Save company profile",
    saving: "Saving…",
    cancel: "Cancel",
    toast_saved: "Company profile saved",
    toast_load_failed: "Could not load company profile",
    toast_logo_uploaded: "Logo uploaded",
    toast_logo_removed: "Logo removed",
    toast_logo_failed: "Logo upload failed",
    toast_remove_failed: "Remove failed",
    toast_trade_long: "Trade label too long",
    toast_primary_hex: "Primary color must be a hex like #0f2540",
    toast_accent_hex: "Accent color must be a hex like #ffb020",
    placeholder_company: "Your company name",
    placeholder_phone: "(555) 555-5555",
    placeholder_email: "office@company.com",
    placeholder_website: "https://www.company.com",
    placeholder_license: "e.g. General Contractor",
    placeholder_service_area: "San Antonio and surrounding 50 mi",
    placeholder_new_trade: "Add a custom trade (e.g., Gutters, Windows)",
  },
  pricing_form: {
    save_all: "Save pricing settings",
    saving: "Saving…",
    section_summary: "Pricing summary",
    section_labor: "Labor rates",
    section_materials: "Materials price book",
    section_rules: "Approved pricing rules",
    add_rule: "Add pricing rule",
    rule_name: "Rule name",
    trade: "Trade",
    system: "System",
    category: "Category",
    pricing_method: "Pricing method",
    unit: "Unit",
    installed_selling_price: "Complete installed selling price",
    labor: "Labor",
    materials: "Materials",
    markup: "Markup",
    subcontractor: "Subcontractor quote",
    flat_price: "Flat price",
    tm: "Time and materials",
    edit: "Edit",
    delete: "Delete",
    save: "Save",
    cancel: "Cancel",
    empty_title: "No approved pricing rules yet",
    empty_body: "Add pricing rules to make estimating faster.",
    toast_saved: "Pricing settings saved",
    toast_load_failed: "Could not load pricing settings",
    zero_price_warn: "Zero-price rule — confirm before saving.",
    unit_mismatch_warn: "Unit mismatch — verify quantity conversion.",
  },
  work_orders_form: {
    loading_body: "Loading crew packets...",
    empty_title: "No crew packets yet",
    empty_body: "Generate a crew packet from a project.",
    goto_projects: "Go to projects",
    columns: {
      customer_project: "Customer / Project",
      linked_estimate: "Linked estimate",
      generated: "Generated",
      status: "Status",
    },
    status: {
      draft: "Draft",
      ready: "Ready",
      sent: "Sent",
      completed: "Completed",
    },
    view_packet: "View packet",
    crew_safe_headline: "Crew-safe packet.",
    crew_safe_body: "Scope and quantities only. Pricing, cost, markup, margin, supplier, SKU, Double Check, and AI advisory data are intentionally excluded.",
    sections: {
      job_info: "Job info",
      scope: "Scope",
      materials: "Materials needed",
      tasks: "Tasks",
      access_notes: "Access notes",
      safety_notes: "Safety notes",
      schedule_notes: "Schedule notes",
      crew_assigned: "Crew assigned",
    },
    save_changes: "Save changes",
    saving: "Saving...",
    cancel: "Cancel",
    edit: "Edit",
    back: "All crew packets",
    toast_saved: "Crew packet saved",
    toast_load_failed: "Could not load crew packet",
  },
  closeout_form: {
    loading_body: "Loading closeout packets...",
    empty_title: "No closeout packets yet",
    empty_body: "Generate a closeout packet when a job wraps.",
    columns: {
      customer_project: "Customer / Project",
      generated: "Generated",
      status: "Status",
    },
    status: {
      draft: "Draft",
      ready: "Ready",
      sent: "Sent",
      completed: "Completed",
    },
    sections: {
      notes: "Notes",
      warranty_overview: "Warranty overview",
      maintenance: "Maintenance",
      next_steps: "Next steps",
    },
    view_packet: "View packet",
    save: "Save",
    cancel: "Cancel",
    edit: "Edit",
    back: "All packets",
    toast_saved: "Closeout packet saved",
    toast_load_failed: "Could not load closeout packet",
    manufacturer_warranty: "Manufacturer warranty notes",
    review_request: "Review request",
    referral_request: "Referral request",
    service_contact: "Warranty / service contact",
    field_terms: "Terms",
    field_care: "Care & maintenance",
    field_message: "Message",
    field_contact: "Contact instructions",
    final_punch: "Final punch list notes",
  },
  customer_notifications: {
    section_overline: "Customer notifications",
    title: "Automatically notify customers about important project updates",
    helper: "When enabled, Levi — your project guide — will email your customer on your behalf when the project reaches an important new stage. Delivery is handled by JobClose AI, and replies route back to your saved company contact details.",
    choice_yes: "Yes",
    choice_no: "No",
    toast_auto_sent: "Levi sent a customer notification for the new project stage on your behalf.",
    toast_auto_skipped_missing_email: "No notification was sent because this project does not have a valid customer email address.",
    toast_auto_failed: "The status was saved but Levi's customer notification could not be delivered. It stays recorded so you can retry.",
  },
  company_extra: {
    dba: "DBA / Trade name",
    line1: "Line 1",
    line2: "Line 2",
    license_state: "License state",
    no_logo: "No logo uploaded",
    branding_description: "Your logo and brand colors show on customer proposals, contracts, invoices, and closeout packets. The JobClose AI logo stays in the app chrome.",
    how_this_is_used: "How this is used",
    how_bullet_1: "· Company Profile only stores your info. Nothing else in the app changes yet.",
    how_bullet_2: "· Logo and colors will appear on customer PDFs in a later slice.",
    how_bullet_3: "· Cost, markup, pricing profile details, and internal notes stay private.",
    logo_hint: "PNG · JPG · WEBP · SVG · up to",
    mb_suffix: "MB",
  },
  pricing_extra: {
    maintenance_headline: "Pricing Settings · Maintenance mode.",
    maintenance_body: "Edit existing rates and rules here. To add a new roofing system with template starter rows, use the",
    setup_wizard: "Contractor Setup Wizard",
    company_name: "Company name",
    trade_primary: "Trade / Primary service",
    trade_primary_placeholder: "e.g. General Contractor, HVAC, Roofing",
    approved_overline: "Approved Trade Pricing",
    approved_heading: "Your saved trade pricing rules",
    approved_body: "The reusable installed-selling rules you approved in Trade Setup. Edits here update the same rule — no duplicates are created and no historical estimates are rewritten.",
    open_trade_setup: "Open Trade Setup",
    approved_empty_title: "No approved trade pricing yet",
    approved_empty_body_prefix: "Approve a system in",
    approved_empty_body_link: "Trade Setup",
    approved_empty_body_suffix: "and it will show up here for editing.",
    row_pricing: "Pricing",
    row_rate: "Rate",
    row_unit: "Unit",
    row_status: "Status",
    row_installed: "Complete installed price",
    row_installed_headline: "Complete installed price · final customer price",
    row_installed_body: "Materials included · Labor included · Overhead included · Profit included · No additional markup.",
    row_installed_note: "This approved rate will be used for future matching estimates. Existing estimates remain unchanged snapshots. Saved in your approved Pricing Settings — JobClose AI will not add another markup.",
    active: "Active",
    inactive: "Inactive",
    reusable: "Reusable",
    one_off: "One-off",
    edit: "Edit",
    save_changes: "Save changes",
    saving: "Saving…",
    cancel: "Cancel",
    style_overline: "Default pricing style",
    style_heading: "How do you usually price jobs?",
    style_body: "Pick the way you usually price work. You can still price each estimate line by unit price, flat price, or labor + materials — this just sets your default.",
    style_unit: "Unit / Production Pricing",
    style_flat: "Flat / Lump Sum",
    style_lm: "Labor + Materials",
    style_hybrid: "Hybrid / Depends on Job",
    job_defaults: "Job defaults",
    terms: "Terms",
    approved_rule_updated: "Approved rule updated",
    save_failed: "Save failed",
    loading: "Loading…",
  },
  wo_extra: {
    untitled_customer: "Untitled customer",
    project_default: "Project",
    no_address: "No address",
    no_scope: "No scope items on this packet.",
    no_materials: "No materials list attached.",
    add_task: "Add task",
    task_placeholder: "Describe a task...",
    access_placeholder: "Gate codes, parking, pets, homeowner preferences...",
    safety_placeholder: "PPE, hazards, fall protection...",
    schedule_placeholder: "Start date, expected duration, homeowner constraints...",
    crew_placeholder: "Lead, helpers, subs...",
    item: "Item",
    qty: "Qty",
    unit: "Unit",
    customer: "Customer",
    project: "Project",
    address: "Address",
    empty_body_full: "Generate a crew packet from any project that has an estimate. The packet gives your crew scope, quantities, tasks, and notes — without exposing your pricing.",
  },
  closeout_extra: {
    untitled_customer: "Untitled customer",
    project_default: "Project",
    job_summary: "Job summary",
    work_completed: "Work completed",
    final_invoice_status: "Final invoice status",
    address: "Address",
    invoice: "Invoice",
    completed: "Completed",
    created: "Created",
    open_packet: "Open packet",
    completion_placeholder: "Summary of what was completed, jobsite condition at handoff...",
    punch_placeholder: "Remaining items or notes from the final walkthrough...",
    warranty_placeholder: "Duration, scope, exclusions...",
    manufacturer_placeholder: "Product/material warranties, registration info...",
    maintenance_placeholder: "Care instructions, owner responsibilities...",
    review_placeholder: "Thank the customer and ask for a review...",
    referral_placeholder: "Ask if they know anyone who might need similar work...",
    contact_placeholder: "How the customer should reach you for warranty or service...",
  },
  // Slice 2D4 · Batch 1 — Estimates + Proposals static UI (contractor-facing).
  // Namespaces are additive. Contractor-entered content (scope, names,
  // prices, notes, legal templates) is preserved untranslated at the
  // call sites.
  estimates_form: {
    list_empty_title: "No estimates yet",
    list_empty_body: "Click \u201cNew Estimate\u201d to start.",
    col_customer: "Customer",
    col_lines: "Lines",
    col_total: "Total",
    col_status: "Status",
    col_created: "Created",

    // Slice 2J · Estimate Workflow Simplification and Reuse
    row_open_aria: (name) => `Open estimate for ${name}`,
    row_menu_aria: "Estimate row actions",
    row_duplicate: "Duplicate",
    row_duplicating: "Duplicating\u2026",
    row_delete_draft: "Delete draft",
    toast_duplicated: "Estimate duplicated. Opening the new draft\u2026",
    toast_duplicate_failed: "We couldn't duplicate that estimate. Try again.",
    toast_deleted: "Draft estimate deleted.",
    toast_delete_failed: "We couldn't delete that draft.",
    delete_dialog_title: (name) => `Delete this draft estimate for ${name}?`,
    delete_dialog_body: "This removes the draft. It doesn't touch the customer, the job, or your saved pricing. This can't be undone.",
    delete_dialog_cancel: "Keep draft",
    delete_dialog_confirm: "Delete draft",
    delete_dialog_deleting: "Deleting\u2026",
    reuse_cue_label: "Use details from this job so you don't retype them.",
    reuse_cue_action: "Prefill from project intake",
    reuse_cue_dismiss: "Not now",
    reuse_cue_dismiss_aria: "Dismiss the prefill-from-job cue",

    // Slice 2J amendment · Universal Edit Estimate + centered Levi ack.
    edit_estimate_btn: "Edit Estimate",
    edit_ack_line_1: "Your job information has been updated.",
    edit_ack_line_2: "I\u2019ll review the changes and help you with the next recommended step.",

    // Slice 2J correction · New Estimate intake simplification.
    wizard_step3_helper: "Talk through the job or type it. Voice walkthrough and typed notes both feed the same estimate — use whichever is easier.",
    wizard_job_notes_label: "Job notes & measurements",
    wizard_job_notes_placeholder: "Add measurements, square footage, dimensions, scope details, materials, access issues, customer requests, special conditions, or anything else that matters for this job.",
    wizard_job_notes_helper: "One field for measurements and typed job details \u2014 no need to decide where each detail belongs.",
    wizard_voice_walkthrough_label: "Voice Walkthrough",
    wizard_voice_walkthrough_help: "Talk through the job while you walk it. Your words are captured so Levi can update and improve your estimate.",
    wizard_voice_or_typed_helper: "Voice walkthrough and job notes both save with this estimate \u2014 use whichever is easier, or both.",

    detail_title_prefix: "Estimate",
    subtitle_back_title: "Back to job — view voice notes, description, and intake details.",
    back_to_job: "Back to job",
    back_to_job_title: "Return to the job / project intake — voice notes, description, photos.",
    back_short: "Back",
    customer_output_label: "Customer output",
    lang_english: "English",
    lang_spanish: "Espa\u00f1ol",
    save: "Save",
    saving: "Saving...",
    save_changes: "Save changes",
    save_estimate: "Save Estimate",
    generate_proposal: "Generate Proposal",
    regenerate_proposal: "Regenerate proposal",
    unsaved_changes: "Unsaved changes",
    all_saved: "All changes saved",
    last_updated: (ts) => `Last updated ${ts}`,

    drift_headline: "This estimate was updated after a proposal was sent.",
    drift_body: "Regenerate the proposal so the customer sees the latest numbers. Your existing proposal link still shows the old numbers until you send a new one.",

    locked_headline: "This estimate is locked",
    locked_body_default: "This estimate is connected to a proposal that has already been sent. Create a new estimate version to make changes without altering the customer's original document.",
    create_new_version: "Create new estimate version",
    creating_new_version: "Creating\u2026",

    account_pricing_headline: "Account pricing setup is incomplete",
    account_pricing_body: "You can still price this estimate line-by-line. To let JobClose AI auto-price future scopes, finish your saved trade pricing.",
    complete_pricing_setup: "Complete Pricing Setup",

    multi_headline: "We found more than one approved Roofing system. Which system applies to this job?",
    multi_installed_copy: "The approved rate already includes materials, labor, overhead, and profit. JobClose AI will not add another markup. Rate comes from your approved Pricing Settings.",
    multi_installed_prefix: "Complete installed price \u00b7 final customer price.",
    multi_use_system: "Use this system",
    multi_dismiss: "Dismiss \u2014 I'll pick manually in Roofing Simple Builder.",
    multi_price_per: (price, unit) => `$${price} per ${unit}`,

    unpriced_headline: "This estimate still contains unpriced rows",
    unpriced_add_prices: "Add prices below, then Generate Proposal.",
    unpriced_lines: (n) => `${n} line item${n === 1 ? "" : "s"} need material or labor pricing. `,
    unpriced_need_material: (n) => `${n} require material cost. `,

    needs_qty_headline: (n) => `Enter a quantity for ${n} approved-pricing line${n === 1 ? "" : "s"}.`,
    needs_qty_body: "The price is already approved \u2014 enter the job quantity to compute the line total.",

    ai_organize_title: "Organize Scope with AI",
    ai_organize_body_idle: "Turn voice notes & job walk into a structured scope and suggested line items.",
    ai_organize_body_running: "Working...",
    ai_check_title: "Run AI Estimate Check",
    ai_check_body_idle: "Green / Yellow / Red review for missed labor, materials, overhead and risk.",
    ai_check_body_running: "Reviewing...",

    // Slice 2I · Levi Unified Estimate Experience
    levi_coworker_overline: "Levi \u00b7 Estimate Check",
    levi_coworker_title: "Levi is ready to check this estimate",
    // Slice 2J correction · Unified Levi estimate-check copy.
    levi_estimate_check_cta: "Have Levi Check This Estimate",
    levi_estimate_check_running: "Levi is checking\u2026",
    levi_estimate_check_help: "Levi will look for missing scope, pricing gaps, materials, labor, cleanup, allowances, and other details that may need your attention.",
    levi_recommends: "Levi recommends",
    levi_you_decide: "You decide when to act.",
    levi_state_ready_to_organize: "Ready to organize this walkthrough.",
    levi_state_scope_organized_nothing_new: "Scope organized. Your approved pricing already covers it \u2014 no new lines needed.",
    levi_state_approved_pricing_matched: (name) => `Approved pricing matched: ${name}.`,
    levi_state_needs_pricing: (n) => `${n} line${n === 1 ? "" : "s"} still need pricing.`,
    levi_state_ready_for_double_check: "Levi is ready to check this estimate.",
    levi_state_double_check_findings: (r, y) => (r > 0 || y > 0)
      ? `Double Check found ${r} red \u00b7 ${y} yellow. Review before sending.`
      : "Double Check clear \u2014 no findings to review.",
    levi_state_organizer_failed: "That last AI step didn't complete. Your work is safe \u2014 try again.",
    levi_action_organize: "Organize the walkthrough",
    levi_action_review_missing: "Review missing pricing",
    levi_action_run_double_check: "Run Double Check",
    levi_action_retry: "Try again",
    levi_secondary_more: "More AI actions",
    levi_secondary_less: "Hide AI actions",
    levi_promise: "Levi never sends, approves, or edits the job without you.",

    double_check_overline: "Double Check",
    double_check_title: "Run Double Check",
    double_check_body_idle: "Deterministic pre-send review \u2014 flags missing material / labor / cleanup / demo, T&M without notes, allowance without disclosure, incomplete Price Book snapshots, and more. Advisory only; you can still generate a proposal.",
    double_check_jump_hint: "Jump to the findings panel below.",
    double_check_body_running: "Reviewing...",
    double_check_warnings: (n) => `${n} ${n === 1 ? "warning" : "warnings"} need review before sending.`,
    double_check_red_suffix: (n) => `${n} red`,
    double_check_yellow_suffix: (n) => `${n} yellow`,
    view_warnings: "View warnings",
    include_ai_advisory: "Include AI advisory",
    include_ai_advisory_body: "Off by default. When on, adds a trade-specific advisory pass on top of the deterministic review. AI advisory is contractor-internal only and never appears on customer proposals or PDFs.",

    photos_heading: "Job photos & documents",
    photos_body: "Upload site photos, measurement sheets, and PDFs. Levi will use them when checking and helping with this estimate.",

    builder_overline: "Estimate builder",
    builder_heading: "Line items",
    add_line: "Add line",
    empty_lines: "No line items. Click \u201cOrganize Scope with AI\u201d or \u201cAdd line\u201d.",

    totals_overline: "Totals",
    totals_no_priced: "No priced line items",
    totals_material_missing: "Total may be incomplete \u2014 material missing",
    totals_material_missing_title: "One or more rows still need material pricing \u2014 total likely below real cost.",
    totals_subtotal: "Subtotal",
    totals_tax_rate: "Tax rate",
    totals_tax: "Tax",
    totals_total: "Total",
    totals_deposit_pct: "Deposit %",
    totals_deposit: "Deposit",
    totals_balance: "Balance due",

    guard_hard_headline: "This estimate isn't ready to send yet.",
    guard_soft_headline: "This estimate may not be ready to send",
    guard_hard_body: "JobClose AI can't generate a proposal until every line item has pricing. Add the missing prices below, or finish trade pricing in the Contractor Setup Wizard.",
    guard_soft_body: "Review the issues below. Sending now will show the customer this proposal exactly as priced:",
    guard_soft_footnote: "AI should not replace your pricing \u2014 AI should protect your pricing.",
    guard_back_edit: "Back to edit pricing",
    guard_send_anyway: "Send anyway",
    guard_sending: "Sending\u2026",

    ack_title: "Review Double Check findings before sending",
    ack_intro_prefix: "Double Check found",
    ack_intro_finding: (n) => `${n} red finding${n === 1 ? "" : "s"}`,
    ack_intro_suffix: "on this estimate. Review each item below. You can still proceed after acknowledging.",
    ack_suggestion_prefix: "Suggestion:",
    ack_checkbox: "I have reviewed these red findings and want to proceed anyway.",
    ack_hint: "This acknowledgement is tied to the current version of the estimate. If you edit the estimate before generating a proposal, you'll be asked again.",
    ack_back: "Back to estimate",
    ack_confirm: "Acknowledge & generate proposal",
    ack_confirming: "Acknowledging\u2026",

    basis_title: "Convert pricing basis",
    basis_prompt_switch: (from, to) => `You're switching material pricing from ${from} to ${to}.`,
    basis_prompt_ratio: (basis, qty) => `How many ${basis} per ${qty}?`,
    basis_prompt_examples: "Examples: 3 bundles per square (shingles), 22 sq ft per box (LVP), 350 sq ft per gallon (paint).",
    basis_cancel: "Cancel",
    basis_apply: "Apply",

    pb_modal_title: "Insert from Materials Price Book",
    pb_modal_body: "Contractor-internal picker. Supplier, SKU, and cost stay on your side only.",
    pb_close: "Close",
    pb_all_trades: "All trades",
    pb_all_suppliers: "All suppliers",
    pb_all_brands: "All brands",
    pb_search_placeholder: "product / brand / SKU",
    pb_loading: "Loading\u2026",
    pb_empty_prefix: "No matching materials in your Price Book. Add materials at ",
    pb_empty_link: "Pricing Profile \u2192 Materials Price Book",
    pb_waste: (pct) => `${pct}% waste`,
    pb_use: "Use",
    pb_linked_prefix: "Linked:",
    pb_linked_fallback: "Price Book item",
    pb_change: "Change",
    pb_clear: "Clear",
    pb_insert: "Insert from Price Book",
    pb_disclosure: "Brand / product wording appears on the customer proposal only if you put it in the line name or description.",
    pb_snapshot_title: "Point-in-time snapshot from your Materials Price Book. Contractor-internal \u2014 never shown to customer.",

    line_approved: "Approved pricing",
    line_matched_settings: "Matched from your Pricing Settings",
    line_review_required: "Contractor review required",
    line_needs_pricing: "Needs pricing",
    line_needs_material: "Needs material pricing",
    line_needs_quantity: "Enter quantity",
    line_item_name_placeholder: "Item name",
    line_item_name_std_placeholder: "Item name (e.g., Install synthetic underlayment)",
    line_description_installed_placeholder: "Scope (visible to customer)",
    line_description_std_placeholder: "Description (visible to customer)",
    line_quantity: "Quantity",
    line_quantity_prefilled_placeholder: "Prefilled \u2014 edit for this job only",
    line_quantity_enter_placeholder: "Enter a quantity for this job",
    line_qty_select: "Select or enter the job quantity.",
    line_qty_default: "Job quantity for this line.",
    line_unit: "Unit",
    line_unit_locked_title: "Unit is locked to the approved Trade Setup selling unit.",
    line_unit_locked_body: "Locked \u2014 set in Trade Setup.",
    line_approved_price: "Approved unit price",
    line_approved_price_title: "Rate is your approved Trade Setup selling price. Change it in Trade Setup.",
    line_per_unit: (unit) => `per ${unit}`,
    line_total: "Line total",
    line_installed_prefix: "Complete installed price \u00b7 final customer price.",
    line_installed_body: "The approved rate already includes materials, labor, overhead, and profit. JobClose AI will not add another markup. Rate comes from your approved Pricing Settings.",
    line_installed_note_prefix: "Adjusting the quantity or line name changes",
    line_installed_note_this_job: "this job only",
    line_installed_note_suffix: ". To change the saved default for future jobs, open ",
    line_installed_note_link: "Trade Setup",
    line_from_job_meas: "job measurements",
    line_from_prefix: "From ",
    line_adjusted_from_prefix: "Adjusted from ",
    // Structured work-area provenance chip wording (Levi-Assisted
    // Measurement Review + Contractor-Controlled Quantity Staging).
    line_from_work_area: "approved measurement",
    line_use_approved_measurement: "Use an Approved Measurement",
    line_edited_suffix: " \u00b7 contractor-edited",
    line_converted_to: (val, unit) => ` \u00b7 converted to ${val} ${unit}`,
    line_needs_qty_title: "Enter the quantity for this job to compute the line total.",
    line_needs_pricing_title: "Add material or labor pricing before sending.",
    line_needs_material_title: "This row requires materials but the material total is still $0.",
    line_move_up: "Move line up",
    line_move_down: "Move line down",
    line_duplicate: "Duplicate line",
    line_remove: "Remove line",
    line_material: "Material",
    line_material_basis_title: "Pricing basis for this material (may differ from quantity unit)",
    line_material_qty_title: (basis) => `Quantity in ${basis}`,
    line_material_math_needed: (qty, unit) => `Material pricing not set (${qty} ${unit})`,
    line_material_math_priced: (mq, basis, cost, total) => `${mq} ${basis} \u00d7 ${cost} per ${basis} = ${total} material`,
    line_labor: "Labor",
    line_labor_only_row: "Labor-only row",
    line_labor_only_title: "This row has no materials \u2014 it charges labor / fee only.",
    line_labor_math_legacy: (total) => `Flat ${total} labor (legacy)`,
    line_labor_math_hour: (qty, rate, total) => `${qty} ${qty === 1 ? "hour" : "hours"} \u00d7 ${rate} per hour = ${total} labor`,
    line_labor_math_job: (total) => `Flat ${total} labor (per job)`,
    line_labor_math_basis: (qty, basis, rate, total) => `${qty} ${basis} \u00d7 ${rate} per ${basis} = ${total} labor`,
    line_qty_placeholder: "qty",
    line_rate_placeholder: "rate",
    line_qty_helper: "Job measurement & default pricing basis for this row.",
    line_markup: "Markup",
    line_per_hour: "per hour",
    line_per_job: "per job",
    line_per_basis: (b) => `per ${b}`,
    line_basis_per_unit: (u) => `per ${u}`,

    pm_label: "Pricing method",
    pm_standard: "Standard (labor + materials)",
    pm_standard_labor_only: "Standard (labor only)",
    pm_unit_price: "Unit price (qty \u00d7 unit price)",
    pm_flat: "Flat / lump sum",
    pm_qty: "Qty",
    pm_qty_labor_pkg: "Roof area / labor units",
    pm_unit_price_label: "Unit price ($)",
    pm_labor_pkg_rate: "Labor package rate ($)",
    pm_unit: "Unit",
    pm_unit_placeholder: "\u2014 unit \u2014",
    pm_line_total: "Line total",
    pm_flat_price: "Flat price ($)",
    pm_no_markup_default: "No re-markup is applied to unit-price lines \u2014 your unit price is treated as the final customer price.",
    pm_no_markup_labor_pkg: "No re-markup is applied. This labor package rate is treated as the final customer price for this line.",
    pm_no_markup_flat: "No re-markup is applied to flat / lump-sum lines.",
    pm_prefill: (source, qty, unit, converted) => `Pulled from ${source}: ${qty} ${unit || "square"}${converted ? " (converted from sq ft)" : ""}. Edit if needed.`,

    mib_headline: "Material pricing is incomplete.",
    mib_body_suffix: ". Review material costs, labor, markup, and totals before sending.",
    mib_unpriced: (n) => `${n} row${n === 1 ? "" : "s"} unpriced`,
    mib_need_material: (n) => `${n} row${n === 1 ? "" : "s"} need material pricing`,

    scope_banner_added_headline: (n) => `AI added ${n} suggested line item${n === 1 ? "" : "s"} to your estimate.`,
    scope_banner_added_body_prefix: "These are ",
    scope_banner_added_body_drafts: "suggested drafts",
    scope_banner_added_body_suffix: ". Review quantities, labor, materials, markup, and totals before sending. Rows still missing pricing are marked ",
    scope_banner_needs_pricing: "Needs pricing",
    scope_banner_added_body_dot: ".",
    scope_banner_empty_headline: "Scope organized \u2014 no line items were generated.",
    scope_banner_empty_body_prefix: "Next, click ",
    scope_banner_empty_body_add: "Add line",
    scope_banner_empty_body_mid: " in the Estimate Builder to add priced line items manually, or rerun ",
    scope_banner_empty_body_run: "Organize Scope with AI",
    scope_banner_empty_body_suffix: " after adding more detail to the job walk notes.",
    scope_banner_dismiss: "Dismiss",

    aiscope_overline: "Levi \u00b7 Scope Notes",
    aiscope_heading: "Organized scope",
    aiscope_block_scope: "Scope of work",
    aiscope_block_labor: "Labor assumptions",
    aiscope_block_material: "Material assumptions",
    aiscope_block_missing: "Missing questions",
    aiscope_block_risk: "Risk areas",
    aiscope_customer_desc: "Customer-friendly description",

    dc_sev_green: "Green",
    dc_sev_yellow: "Yellow",
    dc_sev_red: "Red",
    dc_stale_suffix: "\u00b7 stale",
    dc_overline: "Double Check",
    dc_heading: "Findings",
    dc_body: "Deterministic pre-send review. Contractor-internal only \u2014 nothing here appears on the customer proposal, contract, or PDF.",
    dc_last_run: (ts) => ` \u00b7 Last run ${ts}`,
    dc_rerun_run: "Run again",
    dc_rerun_stale: "Re-run Double Check",
    dc_rechecking: "Rechecking\u2026",
    dc_stale_banner: "The estimate has changed since the last Double Check. Re-run to refresh the findings before generating a proposal.",
    dc_ai_error_title: "AI advisory unavailable this run.",
    dc_ai_error_body: "Deterministic review is complete. You can re-run Double Check with AI advisory to try again.",
    dc_empty_green: "No issues detected. Estimate looks ready to send.",
    dc_ai_chip_title: "This advisory came from the AI layer, not the deterministic engine.",
    dc_suggestion_prefix: "Suggestion:",
    dc_ai_chip: "AI",

    // Deterministic Double Check finding templates (source-of-truth English
    // still lives on backend/checks.py; these keys let the contractor UI
    // render the same finding in the active UI language when it is not en).
    dc_finding: {
    missing_material: {
      title: (name) => `Material pricing missing on \u201c${name}\u201d`,
      detail: "This line item is flagged as requiring material but material_cost is $0. The estimate total is likely below real cost.",
      suggestion: "Open the line and enter the per-basis-unit material price, or insert it from your Materials Price Book.",
    },
    missing_labor: {
      title: (name) => `Labor missing on \u201c${name}\u201d`,
      detail: "Material has a cost but labor is $0 for this line. Sending as-is will under-price the job.",
      suggestion: "Add labor hours \u00d7 labor rate for this line, or explicitly note it is material-only in the description.",
    },
    missing_waste_factor: {
      title: (name) => `Waste factor may be missing on \u201c${name}\u201d`,
      detail: "The material quantity looks lower than what the linked Materials Price Book item's waste allowance would suggest.",
      suggestion: "Recalculate material_qty with the waste percentage from your Price Book, or set it explicitly if you know this job needs less.",
    },
    missing_cleanup: {
      title: "Cleanup line item is missing",
      detail: "This trade almost always includes final cleanup, but no line item on the estimate mentions it.",
      suggestion: "Add a cleanup / broom-sweep line so the customer sees it and you're paid for the time.",
    },
    missing_demo_disposal: {
      title: "Demo / disposal line item is missing",
      detail: "No line mentions tear-off, demo, disposal, dumpster, or haul-away. Dump fees alone can eat the margin on this kind of job.",
      suggestion: "Add a demo / disposal line with the dump fee + labor to tear off and haul away the old material.",
    },
    missing_access_notes: {
      title: "Access appears non-standard but no line covers it",
      detail: "The project notes mention stairs, multi-story, or tight access, but nothing on the estimate accounts for the extra time or equipment.",
      suggestion: "Add a line for scaffolding, extra labor, or access setup \u2014 or note in a line description that access adds time.",
    },
    missing_equipment_rental: {
      title: "Equipment / rental line may be missing",
      detail: "This trade typically requires a dumpster, lift, or scaffold. No line mentions equipment or rental.",
      suggestion: "Add a line for the dumpster / lift / scaffold you plan to use, or roll it into an existing line's cost.",
    },
    tm_without_notes: {
      title: (name) => `T&M line \u201c${name}\u201d has no notes`,
      detail: "This line is priced as Time & Materials but has no notes or description explaining what the customer will be billed for.",
      suggestion: "Add a T&M explanation in the line's notes so the customer isn't surprised later, and so this doesn't read as part of the fixed bid.",
    },
    allowance_without_disclosure: {
      title: (name) => `Allowance line \u201c${name}\u201d lacks disclosure`,
      detail: "Allowance lines require a description telling the customer what selection is included and what happens if their pick costs more.",
      suggestion: "Add wording like 'includes up to $X for tile selection; overages billed at cost' in the description or notes.",
    },
    sub_quote_zero: {
      title: (name) => `Sub-quote line \u201c${name}\u201d has no amount`,
      detail: "This line is priced from a subcontractor quote but the amount is $0. The customer will see $0 for this scope.",
      suggestion: "Enter the sub's quoted amount, or remove the line if the sub isn't in scope yet.",
    },
    unusually_low_labor: {
      title: "Labor looks unusually low vs. material",
      detail: "Total labor on this estimate is under 10% of the total material cost. That's unusual for most trades \u2014 a labor line may be missing.",
      suggestion: "Sanity-check the labor hours on each line, or ensure a separate labor line exists for the work not tied to material.",
    },
    incomplete_scope: {
      title: "Scope looks thin for this job type",
      detail: (jobType, minMinusOne, lineCount) => `This job type (${jobType || "complex remodel"}) usually has more than ${minMinusOne ?? 3} line items. The current estimate has ${lineCount ?? 0}.`,
      suggestion: "Double-check that materials, labor, demo, cleanup, and permit/fees are each represented as line items.",
    },
    price_book_data_incomplete: {
      title: (name) => `Price Book snapshot on \u201c${name}\u201d is missing data`,
      detail: (missing) => `This line is linked to your Materials Price Book but the frozen snapshot is missing: ${(missing || []).join(", ")}.`,
      suggestion: "Re-open the Price Book picker and re-insert this item so the snapshot is complete, or clear the link and price it manually.",
    },
    // Category display labels used by the finding-card overline. Backend
    // sends `f.category` as a stable enum; the display label is translated.
    category: {
      missing_material: "Missing material",
      missing_labor: "Missing labor",
      missing_waste_factor: "Waste factor",
      missing_cleanup_demo_disposal: "Cleanup / disposal",
      missing_access_notes: "Access",
      missing_equipment_rental: "Equipment / rental",
      tm_without_notes: "T&M notes",
      allowance_without_disclosure: "Allowance",
      sub_quote_zero: "Sub-quote",
      unusually_low_labor: "Labor sanity",
      incomplete_scope: "Scope",
      price_book_data_incomplete: "Price Book",
      Pricing: "Pricing",
      missing_profile_rules: "Missing profile rules",
    },
    },

    aicheck_overline: "AI Estimate Check",
    aicheck_heading: "Findings",
    aicheck_suggestion_prefix: "Suggestion:",
    aicheck_pricing_safety_title: "Pricing safety",
    aicheck_pricing_safety_suggestion: "Add material and labor pricing to each line item, or remove unused rows, before generating a proposal.",
    aicheck_missing_rules_title: "Pricing profile is missing material rules",
    aicheck_missing_rules_detail: (trade) => `Your pricing profile does not include standard material rules${trade ? ` for ${trade}` : ""}. AI cannot reliably seed material costs without them.`,
    aicheck_missing_rules_suggestion: "Complete Pricing Setup \u2192 add material rules / per-unit costs (e.g., shingles per square, underlayment, drip edge, ridge cap, fasteners, pipe boots, dump fees). Then rerun Organize Scope with AI.",

    // Issue strings for the proposal-guard dialog (also fed into AI Check
    // panel red findings). These preserve counts + math.
    issue_no_items: "This estimate has no line items.",
    issue_none_ready: "No line items are ready to send \u2014 add pricing or a quantity.",
    issue_unpriced: (n) => `${n} line item${n === 1 ? "" : "s"} still need pricing (material and/or labor).`,
    issue_needs_qty: (n) => `${n} approved-pricing line${n === 1 ? "" : "s"} need${n === 1 ? "s" : ""} a quantity.`,
    issue_needs_material: (n) => `${n} line item${n === 1 ? "" : "s"} still need material pricing \u2014 the total may be far below real cost.`,
    issue_missing_name: (n) => `${n} line item${n === 1 ? "" : "s"} ${n === 1 ? "is" : "are"} missing a name.`,
    issue_total_zero: "Estimate total is $0.00 after pricing \u2014 review tax rate and line totals.",
    issue_placeholders: (fields) => `Unresolved placeholder detected in your saved defaults (${fields}). Fix it in Document Basics before sending \u2014 the customer would otherwise see raw text like "[Company]".`,
    issue_proposal_not_created: "Proposal not created \u2014 this estimate still contains unpriced items.",
    issue_hardblock_fallback: "This estimate still contains unpriced rows.",

    // Toasts
    toast_project_deleted: "Project for this estimate was deleted.",
    toast_not_found: "Estimate not found \u2014 it may have been deleted.",
    toast_load_failed: "Could not load estimate.",
    toast_matched: (system) => `Matched ${system} from your Pricing Settings. Review the line below.`,
    toast_staged: (system) => `Staged ${system}.`,
    toast_stage_failed: "Could not stage the selected system.",
    toast_line_removed: (name) => `Line "${(name || "Untitled").slice(0, 40)}" removed`,
    toast_undo: "Undo",
    toast_saved: "Estimate saved",
    toast_locked: "This estimate is locked \u2014 create a new version.",
    toast_save_failed: "Save failed",
    toast_new_version: (v) => `New estimate v${v} created`,
    toast_new_version_failed: "Could not create a new version",
    toast_no_walkthrough: "No walk-through notes to organize. Click Back to job \u2192 add voice or typed notes \u2192 return here and re-run.",
    toast_organize_empty: "Organize Scope ran but couldn't find anything to price. Try adding more walkthrough detail on the project page, then re-run.",
    toast_organize_added: (n) => `AI added ${n} suggested line items \u2014 review pricing.`,
    toast_ai_scope_failed: "AI scope failed",
    toast_ai_check_done: "AI Estimate Check complete",
    toast_ai_check_failed: "AI check failed",
    toast_dc_red: (n) => `Double Check: ${n} red finding${n === 1 ? "" : "s"}`,
    toast_dc_yellow: (n) => `Double Check: ${n} yellow finding${n === 1 ? "" : "s"}`,
    toast_dc_green: "Double Check: looks good",
    toast_dc_failed: "Double Check failed",
    toast_save_before_send: "Could not save the estimate \u2014 try again.",
    toast_proposal_generated: "Proposal generated",
    toast_proposal_failed: "Could not create proposal",
    toast_ack_stale: "Estimate changed while dialog was open \u2014 re-run Double Check.",
    toast_ack_failed: "Could not save acknowledgement",
    toast_pb_load_failed: "Could not load Materials Price Book",
    toast_pb_linked: (product) => `Linked "${product}" from your Price Book`,

    // Display-only labels for stored enum values. Stored values remain
    // "draft" / "sent" / "approved" / "expired" / "revoked" in the DB.
    status_draft: "draft",
    status_sent: "sent",
    status_approved: "approved",
    status_expired: "expired",
    status_revoked: "revoked",
  },
  // Levi Complete Scope Orchestration — advisory manifest surface.
  // Runs automatically on estimate open; never mutates the estimate
  // without an explicit contractor click.
  scope_orchestrator: {
    overline: "Levi Scope Check",
    headline: "I found work that still needs attention.",
    all_priced_title: "Every recognized scope is on this estimate.",
    subhead: "Levi recommends. You decide. Nothing is added without your click.",
    recognized_in: (field) => `Recognized in ${field}`,
    recognized_from: "Recognized from",
    source_field: {
      job_description: "Job description",
      typed_notes:     "Typed notes",
      voice_transcript:"Voice walkthrough",
      measurements:    "Project intake",
      labor_notes:     "Labor notes",
      material_notes:  "Material notes",
    },
    count_priced: (n) => `${n} priced`,
    count_needs_review: (n) => `${n} to review`,
    count_trade_not_setup: (n) => `${n} needs setup`,
    count_unsupported_capability: (n) => `${n} manual pricing`,
    count_unresolved: (n) => `${n} unresolved`,
    state: {
      priced:                 "Priced",
      needs_review:           "Needs review",
      trade_not_setup:        "Pricing setup incomplete",
      unsupported_capability: "Manual pricing required",
      unresolved:             "Not on estimate yet",
    },
    guidance: {
      trade_not_setup:        (trade) => `I found ${trade} work, but your ${trade} pricing setup is not complete.`,
      unsupported_capability: (label) => `I found ${label} work. JobClose AI doesn\u2019t automate pricing for this work yet, but you can still price it manually for this job.`,
    },
    set_up_trade: (trade) => `Set Up ${trade}`,
    price_this_job_only: "Price This Job Only",
    skip_for_now: "Skip For Now",
    needs_review_hint: "Open the line below to confirm quantity or pricing.",
    review_line: "Review this line",
    load_failed: "Couldn\u2019t reach the scope check.",
    action_failed: "Couldn\u2019t update this scope. Try again.",
  },
  // Levi advisory panel + AI recommendation templates (contractor-side only).
  levi: {
    name_label: "Levi \u00b7 JobClose AI",
    alt_text: "Levi, the JobClose AI mascot",
    headline_warning: "Levi sniffed out things to review",
    headline_info_with_items: "Levi has a couple of gentle nudges",
    headline_info_standing_by: "Levi is standing by",
    headline_success: "Levi says this one looks clean",
    what_is_title: "What is Levi?",
    show_notes: "Show Levi's notes",
    hide_notes: "Hide Levi's notes",
    empty_hint: "Nothing to sniff at right now.",
    learn_more: "Meet Levi",
    // Advisory messages
    advisory_no_line_items_with_notes: "Your walk-through notes are ready. Click Organize Scope with AI above to turn them into priced line items.",
    advisory_no_line_items_without_notes: "Add walk-through notes on the project page (click Back to job), then click Organize Scope with AI to build the estimate.",
    advisory_organize_returned_nothing: "Organize Scope with AI ran but couldn't find anything to price. Try adding more detail to the walk-through notes on the project page, then re-run it.",
    advisory_double_check_never_run: "Want me to sniff this over before you send it? Run a quick Double Check.",
    advisory_double_check_stale: "You've changed things since the last Double Check. Want to re-run it?",
    advisory_double_check_findings: "I sniffed out a few things to review before you send this.",
    advisory_missing_material_pricing: "Looks like material pricing may still be missing on a few lines.",
    advisory_notes_edited_after_scope: "You edited job walk notes after the last Organize Scope run. If you want the estimate to reflect those notes, rerun Organize Scope with AI.",
    advisory_all_clean: "This one looks pretty clean. No red flags sniffed out.",
    // Action button labels
    action_back_to_job: "Back to job",
    action_run_organize: "Organize Scope with AI",
    action_run_double_check: "Run Double Check",
    action_view_warnings: "View warnings",
    action_jump_missing: "Jump to first missing",
  },
  // Photo/document uploader (contractor-facing).
  photo_uploader: {
    uploading: "Uploading...",
    drag_prompt: "Drag & drop photos / PDFs here, or",
    choose_files: "Choose files",
    format_hint: (mb) => `JPG \u00b7 PNG \u00b7 HEIC \u00b7 WebP \u00b7 PDF \u00b7 up to ${mb} MB each`,
    toast_no_project: "Project not saved yet.",
    toast_uploaded: (name) => `Uploaded ${name}`,
    toast_over_limit: (name, mb) => `${name} is over ${mb} MB \u2014 skipped.`,
    toast_failed: (name) => `Failed: ${name}`,
    toast_removed: (name) => `Removed ${name || "file"}`,
    toast_remove_failed: "Could not remove file",
    tooltip_remove: "Remove",
  },
  // Roofing Simple Builder (contractor-facing, contractor-internal).
  roofing_builder: {
    overline: "Roofing \u00b7 optional",
    title: "Roofing Simple Builder",
    helper: "Pick the roof system + how you want to price this specific job. Not roofing? Leave this collapsed \u2014 the standard estimate editor still works normally.",
    approved_label: "Your approved Roofing systems (from Pricing Settings)",
    approved_loading: "Loading approved systems\u2026",
    approved_empty_prefix: "You don't have any approved Roofing systems yet.",
    approved_empty_link: "Open Trade Setup",
    approved_empty_suffix: " to approve one and it will appear here.",
    approved_price_prefix: "Approved price \u00b7 ",
    approved_price_per: (unit) => `per ${unit}`,
    approved_flat_prefix: "Approved flat price \u00b7 ",
    approved_reusable_prefix: "Approved reusable rule \u00b7 ",
    use_approved: "Use approved pricing",
    installed_headline: "Complete installed price \u00b7 final customer price",
    installed_body: "Materials included \u00b7 Labor included \u00b7 Overhead included \u00b7 Profit included \u00b7 No additional markup.",
    installed_note: "Rate comes from your approved Pricing Settings. JobClose AI will not add another markup.",
    approved_this_job_prefix: "Adjusting the quantity, unit price, or notes on the added line changes ",
    approved_this_job_bold: "this job only",
    approved_this_job_suffix: ". To change the saved default for future jobs, open ",
    approved_this_job_link: "Trade Setup",
    q_system: "What type of roof installation are you estimating?",
    choose_system: "\u2014 Choose a roof system \u2014",
    q_pricing: "How do you want to price this roof?",
    pricing_radiogroup_label: "Estimate pricing style",
    labor_pkg_hint_with_system_prefix: "Add a single roofer-friendly labor package line \u2014 ",
    labor_pkg_hint_with_system_suffix: ". Enter quantity \u00d7 rate. Total is set by the contractor; no extra markup is added.",
    labor_pkg_hint_no_system_prefix: "Pick a roof system above to enable the roofer-friendly labor package button. This inserts ",
    labor_pkg_hint_no_system_bold: "one",
    labor_pkg_hint_no_system_suffix: " line \u2014 not a broken-out tear-off / install / flashing / disposal breakdown.",
    labor_pkg_already_added: "Labor package already added \u2014 jump to line",
    labor_pkg_add: "Add roofing labor package",
    detailed_hint_bold: "Detailed mode.",
    detailed_hint_body: " Use the standard line-item editor below to add tear-off, install, flashing, disposal, and materials as separate rows. Every existing feature \u2014 Slice F unit pricing, Materials Price Book insert, AI organizer, Double Check \u2014 still works.",
    labor_package_description_help: "This labor package is intended to include standard tear-off, installation of the approved roofing system, normal crew labor, and cleanup unless excluded or broken out below.",
    // Roof system labels
    system_asphalt_shingle: "Asphalt shingle roof replacement",
    system_tpo: "TPO / flat roof system",
    system_standing_seam: "Standing seam metal roof",
    system_tile: "Tile roof",
    system_mod_bit: "Modified bitumen / flat roof",
    system_coating: "Roof coating system",
    system_repair: "Roof repair only",
    system_other: "Other / custom roofing scope",
    // Pricing style labels + descs
    style_labor_package_materials_label: "Labor package + materials",
    style_labor_package_materials_desc: "One labor package for the roof + materials the contractor adds.",
    style_all_in_per_square_label: "All-in price per roofing square",
    style_all_in_per_square_desc: "One rate per roofing square covers everything.",
    style_all_in_per_sqft_label: "All-in price per sq ft",
    style_all_in_per_sqft_desc: "One rate per sq ft covers everything.",
    style_flat_lump_sum_label: "Flat / lump sum",
    style_flat_lump_sum_desc: "One price for the approved roofing scope.",
    style_detailed_label: "Detailed line-by-line breakdown",
    style_detailed_desc: "Break tear-off, install, flashing, disposal, materials into rows.",
    // Toasts
    toast_already_added: (name) => `${name} already on this estimate \u2014 jumped to it.`,
    toast_added_with_prefill: (name, qty, unitDisplay, source, converted) => `${name} added. Pulled ${qty} ${unitDisplay} from ${source}${converted ? " (converted)" : ""}. Enter the labor package rate below.`,
    toast_added_no_prefill: (name) => `${name} added. Enter roof area and labor package rate below.`,
    toast_approved_added_prefill: (displayName, sourceText, sourceLabel) => `Added ${displayName} using approved pricing. Prefilled ${sourceText} from ${sourceLabel}.`,
    toast_approved_added: (displayName) => `Added ${displayName} using approved pricing. Enter the quantity below.`,
    toast_approved_duplicate: (displayName) => `${displayName} is already on this estimate.`,
  },
  proposals_form: {
    list_empty_title: "No proposals yet",
    list_empty_body: "Create an estimate, then click \"Generate Proposal\".",
    list_empty_action: "View estimates",
    col_customer: "Customer",
    col_total: "Total",
    col_status: "Status",
    col_created: "Created",

    detail_title_prefix: "Proposal",
    back: "Back",
    preview_customer: "Preview Customer View",
    print: "Print",
    download_pdf: "Download PDF",
    copy_link: "Copy customer link",
    email_link: "Email link to customer",
    mark_approved: "Mark Approved",
    contract_lang_label: "Contract",
    lang_english: "English",
    lang_spanish: "Espa\u00f1ol",
    draft_contract: "Draft Contract",

    fallback_company: "Your Company",
    header_overline: "Proposal",
    header_date: "Date:",
    header_status: "Status:",

    section_scope: "Scope of work",
    section_included: "Included",
    section_excluded: "Excluded",
    section_pricing: "Pricing",
    col_item: "Item",
    col_qty: "Qty",
    col_unit: "Unit",
    allowance_badge: "Allowance",
    tfoot_subtotal: "Subtotal",
    tfoot_tax: "Tax",
    tfoot_total: "Total",
    tfoot_deposit: "Deposit",
    tfoot_balance: "Balance due",
    tm_reserve_prefix: "Possible T&M reserve",
    tm_reserve_suffix: "\u00b7 not included in firm total",
    allowance_note_prefix: "Allowance:",
    allowance_note_body: " Allowance items are placeholder amounts. Final cost may vary based on selections or conditions and will be handled through a written change order.",
    tm_section_heading: "Possible Additional Work (Time & Materials)",
    tm_col_estimate: "Est. total",
    tm_footnote: "These items are estimates only and may be billed if the work becomes necessary.",
    section_payment: "Payment schedule",
    section_timeline: "Timeline",
    section_next_steps: "Next steps",

    // Toasts + prompts
    prompt_copy_link: "Copy this link:",
    prompt_email_to: "Send proposal email to:",
    toast_public_link_copied: "Public link copied \u2014 share with your customer",
    toast_email_queued: "Email queued (mock sent)",
    toast_email_sent: "Email sent",
    toast_email_failed: "Could not send email",
    toast_downloaded: (filename) => `Downloaded ${filename}`,
    toast_pdf_failed: "Could not generate PDF",
    toast_approved: "Proposal approved. Ready to draft contract.",
    toast_approve_failed: "Could not approve",
    toast_contract_created: "Contract draft created",
    toast_contract_failed: "Could not create contract",

    // Display-only labels for stored proposal statuses.
    status_draft: "draft",
    status_sent: "sent",
    status_approved: "approved",
    status_expired: "expired",
    status_revoked: "revoked",
  },
  // Slice VI-02 · Contractor Measurement Decision and Promotion Delta (EN).
  vi02: {
    section: {
      title: "Levi's Suggested Measurements",
      loading: "Loading Levi's suggestions…",
      pending_count: "{count} pending your review",
      empty_recommend_video: "No Levi suggestions yet for this project. A guided video walkthrough helps Levi propose measurements you can review here.",
      show_resolved: "Show resolved ({count})",
      hide_resolved: "Hide resolved",
      hierarchy_note: "Video and voice walkthroughs are Levi's primary source. Photos support the video. Your decision is final.",
    },
    state: {
      levi_preliminary: "Preliminary — awaiting your review",
      accepted_levi: "Accepted",
      contractor_corrected: "Corrected & accepted",
      contractor_rejected: "Rejected",
      recapture_requested: "Recapture requested",
      // Contractor-facing wording for a candidate the contractor
      // reviewed and consciously deferred. The underlying technical
      // state remains left_preliminary; only the label is friendlier.
      left_preliminary: "Decide later",
    },
    evidence: {
      video: "Video",
      video_and_voice: "Video + Voice",
      voice: "Voice",
      photo: "Photo",
      calculation: "Calculation",
      unknown_provenance: "Provenance unknown",
    },
    confidence: {
      high: "high confidence",
      medium: "medium confidence",
      low: "low confidence",
    },
    values: {
      levi_proposed: "Levi proposed",
      contractor_approved: "You approved",
    },
    candidate: { unnamed: "Unlabeled measurement" },
    blocked: {
      awaiting_contractor_decision: "Not yet trusted — decide to promote.",
      contractor_rejected: "You rejected this — not eligible.",
      recapture_requested: "Waiting on additional evidence.",
      contractor_deferred: "You chose to decide later — not eligible until decided.",
      superseded: "Superseded by a newer decision.",
      not_promoted: "Not promoted to Work Areas yet.",
    },
    actions: {
      accept: "Accept",
      correct: "Correct",
      reject: "Reject",
      recapture: "Request recapture",
      leave_preliminary: "Decide later",
      toggle_details: "Toggle details",
      cancel: "Cancel",
    },
    correct: {
      title: "Enter your measured value",
      original_preserved_note: "Levi's original proposal will be preserved for reference.",
      approved_value: "Your value",
      approved_unit: "Unit",
      reason_placeholder: "Optional note (why the correction)",
      confirm: "Save correction",
    },
    confirm: {
      accept_title: "Accept Levi's proposal?",
      accept_message: "This will promote Levi's value into Work Areas as a trusted measurement. It can be corrected later.",
      reject_title: "Reject this measurement?",
      reject_message: "The candidate will be preserved for history but will not be usable downstream.",
      recapture_title: "Request recapture?",
      recapture_message: "Levi will explain what additional evidence is needed. A guided video is preferred; a supporting photo is also welcome.",
      leave_title: "Decide later?",
      leave_message: "You can come back to decide anytime. The candidate remains excluded from trusted measurements.",
      // Slice VI-02 pre-QA alignment · Correct final confirmation.
      correct_title: "Save this correction?",
      correct_original_label: "Levi proposed",
      correct_new_label: "Your corrected value",
      correct_context_label: "Measurement",
      correct_becomes_trusted: "Your corrected value will become the trusted project measurement.",
      correct_no_estimate: "No estimate, line item, price, scope, contract, or invoice will be changed.",
      correct_confirm_label: "Save correction",
    },
    recapture: {
      guidance_title: "Levi's recapture guidance",
      prefer_video_note: "A short guided video is preferred; a close-up photo can also help.",
    },
    details: {
      dimension_kind: "Dimension type",
      session_ref: "From Video Walk",
      evidence_list: "Evidence",
    },
    toast: {
      accept_success: "Accepted and added to Work Areas.",
      correct_success: "Correction saved and added to Work Areas.",
      reject_success: "Marked as rejected.",
      recapture_success: "Recapture requested.",
      "leave-preliminary_success": "Left preliminary.",
    },
    errors: {
      invalid_value: "Enter a valid non-negative value.",
      invalid_unit: "Choose a valid unit.",
      decision_locked: "This measurement's decision is already final.",
      accept_failed: "Could not accept — please try again.",
      correct_failed: "Could not save correction.",
      reject_failed: "Could not reject.",
      recapture_failed: "Could not request recapture.",
      "leave-preliminary_failed": "Could not defer.",
      generic: "Something went wrong. Please try again.",
    },
  },
  // Slice VI-03 · Contractor-Reviewed Levi Estimate Suggestions Delta (EN).
  vi03: {
    section: {
      title: "Levi's suggested scope",
      loading: "Loading suggestions…",
      empty: "No suggestions yet. Suggestions appear after you approve Levi's measurements in Work Areas.",
      item_count: "{count} suggestions",
      accepted_count: "{count} accepted, ready to commit",
      committed_note: "These suggestions have been committed to the estimate.",
      invariant_note: "Nothing enters the estimate until you commit selected items.",
      refresh: "Refresh from approved Work Areas",
    },
    state: {
      levi_preliminary: "Awaiting your review",
      contractor_accepted: "Accepted",
      contractor_edited: "Edited & accepted",
      contractor_rejected: "Rejected",
      committed_to_estimate: "Committed to estimate",
    },
    item: {
      from_work_area: "From Work Area",
      levi_proposed: "Levi proposed",
      your_edit: "Your edit",
    },
    edit: { quantity: "Qty", unit: "Unit", unit_price: "Unit price",
            save: "Save edit" },
    actions: { accept: "Accept", edit: "Edit", reject: "Reject",
               reset: "Reset", commit: "Commit to estimate",
               cancel: "Cancel" },
    commit: {
      title: "Commit selected suggestions to this estimate?",
      summary: "{count} accepted suggestion(s) will become new line items on {estimate}.",
      becomes_lines: "Selected suggestions become new estimate line items.",
      no_downstream: "No proposal, contract, invoice, work order, schedule, or customer message will be created.",
      confirm: "Commit",
    },
    toast: { committed: "Suggestions committed to the estimate." },
    errors: {
      prepare_failed: "Could not load suggestions.",
      commit_failed: "Could not commit — please try again.",
      no_committable_items: "Accept at least one suggestion before committing.",
      estimate_not_found: "That estimate could not be found.",
      project_mismatch: "That estimate belongs to another project.",
      suggestion_not_committable: "This suggestion is no longer editable.",
      item_state_locked: "That item's state can no longer change.",
      item_already_committed: "That item is already on the estimate.",
      no_editable_fields: "Enter a value or unit to edit.",
      generic: "Something went wrong. Please try again.",
    },
  },
  // Slice 2H · Measurement Intelligence Foundation — Work Areas (EN).
  work_areas: {
    card_title: "Job Measurements",
    card_subtitle: "Measure rooms, exterior areas, roof sections, objects, or anything else needed for this job.",
    measure_action: "Start Measuring",
    empty_state: "No measurements have been added yet.",
    empty_state_optional: "Already have your measurements? You can continue using your own quantities in the estimate.",
    future_methods_note: "More ways to measure with Levi will be available later.",
    chooser_title: "What would you like to measure?",
    chooser_cancel: "Cancel",
    chooser: {
      interior_room: "Interior Room",
      exterior_area: "Exterior Area",
      roof_section: "Roof Section",
      elevation: "Wall / Elevation",
      object: "Object",
      linear_measurement: "Linear Measurement",
      something_else: "Something Else",
    },
    types: {
      interior_room: "Interior Room",
      exterior_area: "Exterior Area",
      roof_section: "Roof Section",
      elevation: "Wall / Elevation",
      commercial_space: "Commercial Space",
      mechanical_area: "Mechanical Area",
      linear_run: "Linear Measurement",
      object: "Object",
      custom: "Something Else",
    },
    openings: { door: "Door", window: "Window", opening: "Opening", other: "Other" },
    sources: {
      manual: "Entered manually",
      apple_roomplan: "Scanned with iPhone",
      camera_assisted: "Added from photos",
      voice_assisted: "Added from voice",
      bluetooth_device: "Added from device",
      plan_import: "Added from plan",
      drone: "Drone",
      other: "Other",
    },
    units: { ft: "ft", in: "in", sqft: "sq ft", linear_ft: "linear ft", count: "count", each: "each", ft_in: "ft/in" },
    status: { approved: "Approved", review_required: "Ready to Review" },
    form: {
      name: "Measurement Name", name_placeholder: "e.g. Living Room, Front Roof, Backyard Tree",
      name_placeholders: {
        interior_room: "e.g. Living Room, Kitchen, Bedroom",
        exterior_area: "e.g. Front Yard, Patio, Driveway",
        roof_section: "e.g. Front Roof, Garage Roof, Porch Roof",
        elevation: "e.g. Front Elevation, North Wall, Garage Wall",
        object: "e.g. Backyard Tree, Water Heater, Equipment Pad",
        linear_measurement: "e.g. Backyard Fence, Living Room Baseboard, Front Gutter",
        something_else: "e.g. Custom Area or Item",
      },
      change_type: "Change",
      area_type: "Measurement Type",
      length_ft: "Length (ft)", width_ft: "Width (ft)", height_ft: "Height (ft)",
      total_length_ft: "Total length (ft)", count: "Count",
      openings: "Openings \u2014 doors, windows, and similar areas", add_opening: "Add opening",
      opening_label: "Label", opening_width: "Width (ft)", opening_height: "Height (ft)", opening_qty: "Qty",
      custom_dimensions: "Measurements", add_custom: "Add Measurement",
      custom_label_placeholder: "e.g. Height, Width, Diameter",
      notes: "Notes for Levi",
      approved_at_label: "Approved",
      cancel: "Cancel", save_draft: "Save Measurements", save_review: "Save Measurements",
      no_mutation_notice: "Saving or approving measurements does not change your estimate or any other part of the job. You decide when and how to use them.",
    },
    actions: { edit: "Edit Measurements", approve_and_save: "Approve Measurements", delete_confirm: "Remove this measurement?" },
    calc: {
      preview_title: "Calculated values",
      floor_area: "Floor area", ceiling_area: "Ceiling area", perimeter: "Perimeter",
      gross_wall_area: "Gross wall area", total_opening_area: "Total opening area",
      net_wall_area: "Net wall area", linear_feet: "Linear feet", run_wall_area: "Run wall area",
    },
    saved_ready_hint: "These measurements are ready if you choose to use them later while estimating.",
    validation: { openings_exceed_walls: "Opening area exceeds gross wall area. Correct the openings before approving." },
    toast: { saved_draft: "Measurements saved.", saved_review: "Measurements updated \u2014 ready to review.", approved: "Measurements approved.", deleted: "Measurement removed." },
    // Levi-Assisted Measurement Review + Contractor-Controlled Quantity
    // Staging (EN). Additive UI concepts — nothing that changes an
    // estimate silently. Contractor confirms every action.
    review_summary: {
      approved_count: (n) => `${n} approved`,
      review_required_count: (n) => `${n} needs review`,
      ready_hint: "Approved measurements are available to use in the estimate.",
    },
    staging: {
      picker_open: "Use an Approved Measurement",
      picker_title: "Use an approved measurement",
      picker_subtitle: "Only approved measurements from this project are listed. Levi recommends. You decide.",
      no_approved: "You have no approved measurements yet on this project.",
      no_approved_hint: "Save and approve a measurement first, then it will appear here.",
      choose_measurement: "Choose a measurement",
      choose_calc: "Choose a calculated value",
      review_title: "Review before using",
      review_source: "From measurement",
      current_quantity: "Current quantity on this line",
      current_quantity_none: "No quantity on this line yet",
      proposed_quantity: "Proposed quantity",
      proposed_hint: "You can edit this before using it. It only affects this line, this job.",
      edit_before_using: "Edit Before Using",
      cancel_edit: "Cancel Edit",
      use_proposed: "Use Proposed Quantity",
      keep_existing: "Keep Existing Quantity",
      cancel_skip: "Cancel / Skip",
      replace_warning: "This line already has a quantity you entered or edited. Replacing it will overwrite that value on this line only.",
      staging_now: "Staging measurement\u2026",
      staged_ok: "Measurement staged onto this line.",
      already_staged: "This measurement is already on this line.",
      kept_existing: "Existing quantity kept.",
    },
    errors: {
      save_failed: "Couldn\u2019t save these measurements. Try again in a moment.",
      approve_failed: "Couldn\u2019t approve these measurements. Try again in a moment.",
      delete_failed: "Couldn\u2019t remove this measurement.",
      work_area_name_required: "A measurement name is required.",
      invalid_area_type: "Choose what you\u2019re measuring.",
      invalid_measurement_mode: "Choose a valid measurement type.",
      invalid_source: "Invalid measurement source.",
      invalid_length: "Length must be zero or a positive number.",
      invalid_width: "Width must be zero or a positive number.",
      invalid_height: "Height must be zero or a positive number.",
      invalid_total_length: "Total length must be zero or a positive number.",
      invalid_opening_type: "Invalid opening type.",
      invalid_opening_width: "Opening width must be zero or a positive number.",
      invalid_opening_height: "Opening height must be zero or a positive number.",
      invalid_opening_quantity: "Opening quantity must be zero or a positive number.",
      custom_dimension_label_required: "Every measurement needs a label.",
      invalid_unit: "Invalid unit.",
      opening_area_exceeds_wall_area: "Opening area exceeds gross wall area.",
      // Staging-specific error codes returned by the backend.
      work_area_not_found: "That measurement could not be found.",
      work_area_not_approved: "That measurement is not approved yet.",
      project_mismatch: "That measurement belongs to a different project.",
      invalid_calc_field: "That calculated value is not available on this measurement.",
      invalid_calc_value: "That calculated value is missing on this measurement.",
      estimate_line_not_found: "That estimate line could not be found.",
      estimate_not_found: "That estimate could not be found.",
      replacement_confirmation_required: "This line already has a quantity you entered. Confirm before replacing.",
      staging_failed: "Couldn\u2019t use this measurement. Try again in a moment.",
    },
  },

  project_detail: {
    fallback_title: "Project",
    loading: "Loading...",
    no_address: "No address",
    all_projects: "All projects",
    resuming_draft: "Resuming your draft",
    still_draft: "This project is still a draft",
    finish_working: "Working...",
    finish_action: "Finish & Create Estimate",
    crew_packet_title: "Crew Packet",
    crew_packet_body_live: "Open the live field packet — always reflects current project info.",
    crew_packet_open: "Open Crew Packet",
    closeout_title: "Closeout / Warranty",
    closeout_body_existing: "Open the existing closeout packet for this project.",
    closeout_body_idle: "Generate a customer-ready closeout packet with warranty, maintenance, and next-step notes.",
    closeout_open: "Open Closeout Packet",
    closeout_generate: "Generate Closeout Packet",
    closeout_working: "Working...",
    job_details: "Job details",
    edit_notes: "Edit job walk notes",
    edit_notes_title: "Correct or add to the original job walk notes captured during intake. Does NOT rewrite estimate line items.",
    label_customer_phone: "Customer phone",
    label_customer_email: "Customer email",
    label_timeline: "Timeline",
    label_created: "Created",
    label_description: "Description",
    customer: "Customer",
    photos_docs: "Photos & documents",
    pcv_working_docs_overline: "Working Docs",
    pcv_working_docs_headline: "Estimates · Proposals · Contracts",
    pcv_job_intel_overline: "Job Intelligence",
    pcv_job_intel_headline: "Vision · Measurements · Levi notes",
    pcv_job_outcome_overline: "Job Outcome",
    pcv_job_outcome_headline: "Invoices · Profitability · Closeout",
    pcv_pill_ask_levi: "Ask Levi",
    notes: "Notes",
    typed_notes_prefix: "Typed notes:",
    measurements_prefix: "Measurements:",
    job_notes_prefix: "Job notes & measurements:",
    material_prefix: "Material:",
    labor_prefix: "Labor:",
    special_prefix: "Special:",
    concerns_prefix: "Concerns:",
    related_estimates: "Estimates",
    related_proposals: "Proposals",
    related_contracts: "Contracts",
    related_invoices: "Invoices",
    related_updates: "Customer Updates",
    toast_notes_saved: "Job walk notes saved.",
    toast_notes_failed: "Could not save job walk notes.",
    toast_draft_finished: "Draft promoted to active. Continue with your estimate.",
    toast_draft_failed: "Could not finish draft",
    toast_crew_estimate_first: "Create an estimate before generating a crew packet.",
    toast_crew_generated: "Crew packet generated",
    toast_crew_failed: "Could not generate crew packet",
    toast_closeout_existing: "Opening existing closeout packet",
    toast_closeout_generated: "Closeout packet generated",
    toast_closeout_failed: "Could not generate closeout packet",
    draft_body: "Add any missing photos or notes below, then promote it to an active project and continue your estimate.",
    related_none_yet: "None yet.",
    related_new: "+ New",
    status_labels: {
      draft: "Draft",
      sent: "Sent",
      approved: "Approved",
      expired: "Expired",
      revoked: "Revoked",
      signed: "Signed",
      ready: "Ready",
      active: "Active",
    },
  },
  voice_notes: {
    title: "Voice Notes",
    help: "Record or upload a job-walk audio note. Whisper transcribes it, you review and edit, then keep it as a project note or append it to an estimate. Voice notes stay internal — they never appear on customer proposals, contracts, invoices, or crew packets.",
    record: "Record",
    stop: "Stop",
    upload_audio: "Upload audio",
    format_hint: (mb) => `MP3 · M4A · WAV · WEBM · OGG · up to ${mb} MB`,
    loading: "Loading voice notes…",
    empty: "No voice notes yet. Record or upload your first one above.",
    exceeds_limit: (mb) => `Audio exceeds ${mb} MB limit.`,
    audio_uploaded: "Audio uploaded. Transcribing…",
    transcript_ready: "Transcript ready.",
    upload_failed: "Voice note upload failed",
    recording_unsupported: "Recording is not supported in this browser.",
    recording_prompt: "Recording… click Stop when done.",
    mic_denied: "Microphone access denied.",
    confirm_delete: "Delete this voice note? This cannot be undone.",
    deleted: "Voice note deleted.",
    delete_failed: "Delete failed",
    transcript_saved: "Transcript saved.",
    save_failed: "Save failed",
    transcript_refreshed: "Transcript refreshed.",
    transcription_failed: "Transcription failed",
    appended_success: "Appended to estimate notes.",
    append_failed: "Append failed",
    status_uploaded: "Uploaded",
    status_transcribing: "Transcribing…",
    status_transcribed: "Transcribed",
    status_failed: "Failed",
    fallback_filename: "Voice note",
    badge_recorded: "Recorded",
    linked_suffix: " · appended to an estimate",
    error_prefix: "Error:",
    aria_toggle: "Toggle voice note",
    aria_delete: "Delete voice note",
    retry_transcription: "Retry transcription",
    transcribe_now: "Transcribe now",
    transcript_label: "Transcript (editable)",
    transcript_placeholder: "Transcript…",
    save_edit: "Save edit",
    retranscribe_title: "Re-run Whisper on the original audio (does not overwrite your edits)",
    retranscribe: "Re-transcribe",
    estimate_option: (short, n) => `Estimate ${short} · ${n} lines`,
    dirty_title: "Save your edits first",
    append_to_estimate: "Append to estimate notes",
    internal_footer: "Voice notes stay internal. They never appear on customer proposals, contracts, invoices, closeout packets, or crew packets.",
  },
  // Slice 2E1 · Levi AI Estimate (Foundation)
  levi_estimate: {
    // Workflow chooser on /app/estimates/new
    chooser_heading: "What are you estimating today?",
    chooser_help: "Pick the workflow that fits this job. You can always switch later.",
    chooser_approved_title: "Full Project Estimate",
    chooser_approved_body: "Best for roofing, HVAC, restoration, remodeling, flooring, painting, fencing, concrete, and other standard contractor projects using your saved pricing and detailed estimate workflow.",
    chooser_approved_action: "Start Full Estimate",
    chooser_levi_title: "Levi One-Off Job",
    chooser_levi_badge: "New",
    chooser_levi_body: "Best for unusual, custom, or small jobs that don't fit your normal pricing. Examples: tree removal · haul-away · shed demolition · small handyman jobs · miscellaneous custom work.",
    chooser_levi_action: "Start One-Off Job",
    chooser_levi_notice: "Levi One-Off Job is intended for unusual or custom jobs. For complete roofing systems, HVAC replacements, restoration projects, remodeling projects, and other standard trade work, use Full Project Estimate for the most accurate results.",
    // Confirmation dialog
    before_title: "Is Levi the right tool for this job?",
    before_body: "Levi One-Off Job is designed for unusual or custom work. If you're estimating a complete roofing system, HVAC replacement, restoration project, remodeling project, or another standard trade project, you'll usually get a more accurate estimate using Full Project Estimate with your saved pricing.",
    before_continue: "Start One-Off Job",
    before_switch: "Go to Full Project Estimate",
    // Smart-redirect banner (server-detected full-trade project)
    smart_title: "I think this looks like a full trade project.",
    smart_body: "You'll usually receive a more accurate estimate using your Full Project Estimate workflow with your saved pricing.",
    smart_switch: "Switch to Full Project Estimate (recommended)",
    smart_continue: "Continue with Levi Anyway",
    // Header
    page_title: "Custom Job with Levi",
    page_subtitle: "For custom work that doesn't normally fit your scope.",
    back_to_new_estimate: "Back to New Estimate",
    // V-P2 · Custom Job with Levi hero (approved 2026-08-22)
    hero_eyebrow: "CUSTOM JOB WITH LEVI",
    hero_headline: "A job outside your normal scope?",
    hero_body: "Sometimes a customer asks you to take on work that isn't part of your everyday trade. Show Levi the job, tell him what you're seeing, and he'll help organize the scope and give you a recommendation to review.",
    hero_primary_cta: "Show Levi the Job",
    hero_talk_cta: "Talk It Through",
    hero_photos_cta: "Add Photos / Plans",
    hero_image_alt: "Levi in a JobClose AI cap and blue polo holding a video camera at a residential framing site.",
    // V-P2 · Three-step explainer (replaces prior four-card block)
    how_overline: "How it works",
    how_step1_title: "Show Levi the Job",
    how_step1_body: "Use Vision, photos, or plans to show Levi what you're looking at.",
    how_step2_title: "Talk It Through",
    how_step2_body: "Tell Levi what needs to be done, measurements you know, constraints, or anything else that matters.",
    how_step3_title: "Review Levi's Recommendation",
    how_step3_body: "Levi organizes the information and gives you a recommendation. Review it, adjust it, or ask Levi to revise it.",
    how_footer: "Levi recommends. You decide.",
    // V-P2 · Contractor-review safety panel
    privacy_message: "Only you see Levi's recommendation. Nothing goes to your customer until you finish the normal estimate flow.",
    // Step 1 · Show Levi the job
    step1_heading: "Show Levi the job.",
    step1_help: "For custom work that doesn't normally fit your scope, give Levi whatever you have. Use Vision, add photos or plans, record a voice note, or describe the job below.",
    vision_cta_title: "Turn on Vision & walk Levi through it",
    vision_cta_body: "Walk the job in real time. Talk Levi through what you see and give him your measurements. Vision uses your device camera — we'll ask for permission if needed.",
    vision_cta_button: "Turn on Vision",
    vision_hide: "Hide Vision",
    vision_preparing: "Preparing your Custom Job session…",
    vision_continuity_hint: "Everything you capture here stays with this job. If you accept Levi's Recommendation, the walk moves into the resulting project automatically.",
    recommended_range_label: "Levi's recommended range",
    suggested_working_price_label: "Suggested working price",
    recommendation_hint: "This is Levi's recommendation, not your saved pricing. Review and edit before you use it.",
    drivers_label: "What Levi weighed",
    needs_more_info_overline: "Levi needs one more thing",
    needs_more_info_generic: "Can you tell me a bit more about this job?",
    needs_more_info_hint: "Answer above (or in Revise) and Levi will re-run the recommendation.",
    cannot_price_overline: "Not enough to price responsibly",
    cannot_price_headline: "Levi can help scope this — he doesn't have enough reliable information to recommend a price yet.",
    cannot_price_body: "Add a couple more photos, a voice note, or type a description with sizes, and try again.",
    section_photos: "Photos & Plans",
    section_photos_hint: "JPEG · PNG · WEBP · PDF · up to 12 MB each.",
    add_photos_btn: "Add Photos / Plans",
    delete_photo: "Remove photo",
    section_voice: "Voice notes",
    section_voice_hint: "Recorded or uploaded audio. Whisper transcribes automatically.",
    record_btn: "Record Description",
    stop_btn: "Stop",
    upload_audio_btn: "Upload audio",
    section_description: "Description",
    description_placeholder: "Describe the job in plain language — what needs to happen, any timing, any constraints…",
    section_followup: "Levi asked",
    followup_answer_placeholder: "Type your answer to Levi's question…",
    save_answer: "Save answer",
    // Actions
    analyze_btn: "Get Levi's Recommendation",
    analyzing: "Levi is thinking…",
    analyze_disabled_hint: "Show Levi the job to get started.",
    // Step 3 · Recommendation
    recommendation_heading: "Here's what I'd recommend based on what you've shown me.",
    recommendation_note: "This is an AI-recommended starting estimate. Review and adjust it before sending it to your customer.",
    job_type_label: "Job type",
    scope_summary_label: "Proposed scope",
    explanation_label: "Why Levi picked this price",
    // Step 4 · Actions
    action_use: "Use Estimate",
    action_edit_price: "Edit Price",
    action_revise: "Ask Levi to Revise",
    action_cancel: "Cancel",
    // Edit price dialog
    edit_price_title: "Edit the estimate price",
    edit_price_body: "You can enter any amount. Levi's suggestion is a starting point — not a final price.",
    edit_price_input_label: "New amount (USD)",
    edit_price_confirm: "Save price",
    edit_price_dismiss: "Keep Levi's price",
    // Revise dialog
    revise_title: "Ask Levi to revise",
    revise_body: "Tell Levi what's wrong or what to change, then he'll re-run the analysis with your note.",
    revise_placeholder: 'e.g. "The tree is much larger — 60 ft not 30 ft." or "Add debris haul-off."',
    revise_confirm: "Send to Levi",
    revise_dismiss: "Never mind",
    // Cancel dialog
    cancel_title: "Discard this Levi session?",
    cancel_body: "Photos, voice notes, and the recommendation will be deleted. This can't be undone.",
    cancel_confirm: "Discard session",
    cancel_dismiss: "Keep working",
    // Use dialog — customer + address
    use_dialog_title: "Create a draft estimate from Levi's recommendation",
    use_dialog_body: "Enter the customer info to save this as a normal draft estimate. You can edit everything after.",
    use_dialog_customer_name: "Customer name",
    use_dialog_customer_email: "Customer email",
    use_dialog_customer_phone: "Customer phone",
    use_dialog_job_address: "Job address",
    use_dialog_amount: "Estimate amount",
    use_dialog_confirm: "Create draft estimate",
    use_dialog_dismiss: "Not yet",
    use_missing_customer: "Customer name is required.",
    // Toasts
    toast_photo_uploaded: "Photo added.",
    toast_photo_failed: "Photo upload failed.",
    toast_photo_deleted: "Photo removed.",
    toast_voice_uploaded: "Voice note added — transcribing…",
    toast_voice_failed: "Voice note upload failed.",
    toast_answer_saved: "Answer saved.",
    toast_analyze_failed: "Levi couldn't produce a recommendation.",
    toast_used: "Draft estimate created.",
    toast_use_failed: "Could not create the draft estimate.",
    toast_cancelled: "Levi session discarded.",
    // Voice status labels (mirror the voice-notes card)
    voice_status_transcribing: "Transcribing…",
    voice_status_transcribed: "Transcribed",
    voice_status_failed: "Failed",
    voice_status_uploaded: "Uploaded",
    empty_state_photos: "No photos yet.",
    empty_state_voice: "No voice notes yet.",
    // Internal note pinned above the recommendation
    internal_note: "Only you see this recommendation. Nothing is sent to the customer until you finish the normal estimate flow.",
    // Vision Panel · Slice 2E3 — presents the four locked backend reasoning
    // sections (what_i_see / possible_concerns / questions_before_pricing /
    // often_forgotten) in contractor-to-contractor voice. Backend keys are
    // NOT renamed; these strings are display copy only.
    vision_panel_title: "Levi's take on this job",
    vision_panel_intro: "Here's how I'm reading this one — quick, honest, contractor to contractor.",
    vision_panel_what_i_see_heading: "What I See",
    vision_panel_what_i_see_help: "Here's what caught my attention.",
    vision_panel_what_i_see_empty: "I don't have enough visible information to confirm anything from this photo yet.",
    vision_panel_possible_concerns_heading: "What I'd Want to Verify",
    vision_panel_possible_concerns_help: "A few things may need a closer look.",
    vision_panel_possible_concerns_empty: "No additional concerns were identified from the available information.",
    vision_panel_questions_heading: "Questions Before Pricing",
    vision_panel_questions_help: "Here's what I'd confirm before putting a number on it.",
    vision_panel_questions_empty: "No additional questions were generated.",
    vision_panel_forgotten_heading: "Don't Forget",
    vision_panel_forgotten_help: "Items that are easy to miss when building the scope.",
    vision_panel_forgotten_empty: "No additional reminders were generated.",
    vision_panel_loading_title: "Levi is reviewing the job…",
    vision_panel_loading_body: "Give him a moment to look through the photos and notes.",
    vision_panel_error_title: "Levi couldn't review this job right now.",
    vision_panel_error_body: "Your photos and job details are still here. Try again when you're ready.",
    // Reusable Levi Working Environment — identity, alt text, and the
    // seven product-state status captions. These strings are the
    // Working Environment's own presentation copy; they do not
    // duplicate or rename any backend contract key.
    working_env_identity_title: "Levi, your coworker at JobClose AI",
    working_env_visual_alt: "Levi, your coworker at JobClose AI",
    working_env_status_ready: "Levi is ready when you are.",
    working_env_status_listening: "Levi is looking things over. Ask when you're ready.",
    working_env_status_reviewing: "Levi is reviewing the job…",
    working_env_status_needs_information: "Levi needs one more thing before he prices it.",
    working_env_status_waiting_for_contractor: "Take a look at Levi's read on this before deciding.",
    working_env_status_recommendation_ready: "Levi's recommendation is ready for you to review.",
    working_env_status_recoverable_error: "Levi couldn't review this job right now — your inputs are safe.",
  },
  cohort_trade_setup: {
    intro: "Choose a system, pick how you normally price it, enter your reusable contractor price, and approve. Approved pricing is used for future matching estimates and never changes existing snapshots.",
    save: "Save",
    save_and_approve: "Save and approve",
    approved: "Approved",
    approved_per_job: "Approved · Per-job pricing",
    not_started: "Not started",
    in_progress: "Setup in progress",
    custom_title: "Custom / One-Off system",
    custom_placeholder: "Describe the custom system",
    custom_reusable: "Reusable",
    custom_one_off: "One-off (this job only)",
    custom_add: "Add custom system",
    saved_toast: "Saved",
    approved_toast: "Approved as reusable pricing",
    err_incomplete: "Please fill in the required fields before approving.",
    err_not_saved: "Save your changes before approving.",
    err_generic: "Save failed. Please try again.",
  },
  email_common: {
    recipient_override_error:
      "Recipient can't be overridden — the customer's saved email is used.",
  },
  schedule: {
    card_overline: "Project Schedule",
    add_btn: "Schedule work",
    add_assignee: "Add new assignee",
    assignee_none: "— Unassigned —",
    assignee_save: "Save",
    assignee_name_placeholder: "e.g. Roofing Crew A",
    unassigned: "Unassigned",
    loading: "Loading schedule…",
    empty_state: "No scheduled work yet. Add the first item to get started.",
    next_up: "Next up",
    upcoming: "Upcoming",
    edit: "Edit",
    duplicate: "Duplicate",
    delete: "Delete",
    cancel: "Cancel",
    save_create: "Create",
    save_edit: "Save changes",
    saving: "Saving…",
    dialog_create_title: "Schedule work",
    dialog_edit_title: "Edit scheduled work",
    field_title: "Title",
    title_placeholder: "e.g. Roof tear-off day 1",
    field_assignee: "Assignee",
    field_all_day: "All-day",
    field_start: "Starts",
    field_end: "Ends",
    field_recurrence: "Repeats",
    field_status: "Status",
    field_reminder_minutes: "Reminder (minutes before)",
    reminder_placeholder: "Leave blank for no reminder",
    field_internal_notes: "Internal notes",
    field_customer_notes: "Customer-facing notes (optional)",
    customer_notes_placeholder: "These stay on the schedule record — nothing is sent to the customer.",
    delete_title: "Delete this scheduled item?",
    delete_body: (title) => `This will remove "${title || "the scheduled item"}" from the project schedule. This cannot be undone.`,
    toast_created: "Scheduled item added.",
    toast_updated: "Scheduled item updated.",
    toast_deleted: "Scheduled item deleted.",
    toast_duplicated: "Duplicated.",
    toast_assignee_created: "Assignee added.",
    toast_save_failed: "Could not save — please try again.",
    toast_delete_failed: "Could not delete — please try again.",
    toast_duplicate_failed: "Could not duplicate — please try again.",
    toast_assignee_failed: "Could not add assignee — please try again.",
    conflict_heading: "Heads up — this overlaps other work",
    conflict_never_blocks: "You can still save. Levi recommends; you decide.",
    conflict_unnamed: "Untitled item",
    conflict_kind: {
      same_assignee: "same assignee is booked",
      same_project: "another item on this project",
    },
    status: {
      scheduled: "Scheduled",
      in_progress: "In progress",
      completed: "Completed",
      canceled: "Canceled",
    },
    recurrence_rules: {
      none: "Doesn't repeat",
      daily: "Daily",
      weekly: "Weekly",
      monthly: "Monthly",
    },
    assignee_kind: {
      self: "Me",
      individual: "Individual",
      crew: "Crew",
      subcontractor: "Subcontractor",
    },
    errors: {
      title_required: "Please add a title.",
      times_required: "Please set a start and end time.",
    },
    // Slice 2L · Company Job Calendar
    company: {
      page_title: "Job Calendar",
      page_subtitle: "Every scheduled job across your company. Read-only here — open a job to edit its schedule.",
      view_month: "Month",
      view_agenda: "Agenda",
      today: "Today",
      prev: "Previous",
      next: "Next",
      loading: "Loading your calendar…",
      error: "Could not load the calendar — please retry.",
      retry: "Retry",
      empty_month: "Nothing scheduled this month yet.",
      empty_agenda: "Nothing scheduled in the next 30 days.",
      empty_hint: "Open a project and use its Schedule card to add scheduled work.",
      show_canceled: "Show canceled",
      hide_canceled: "Hide canceled",
      more_events: (n) => `+${n} more`,
      overlap_badge: "Conflict",
      overlap_tooltip: "Same crew is booked on another job at this time.",
      open_project: "Open project",
      customer_untitled: "Untitled project",
      unassigned: "Unassigned",
      weekday_short: {
        sun: "Sun", mon: "Mon", tue: "Tue", wed: "Wed",
        thu: "Thu", fri: "Fri", sat: "Sat",
      },
      section_today: "Today",
      section_this_week: "This week",
      section_upcoming: "Later",
      count_today: (n) => `${n} today`,
      count_this_week: (n) => `${n} this week`,
      filter_assignee_label: "Filter by assignee",
      filter_assignee_all: "All assignees",
      filter_status_label: "Filter by status",
      filter_status_all: "All statuses",
      empty_filtered: "No scheduled work matches these filters.",
      levi: {
        section_overline: "Levi",
        section_title: "Levi's read of your calendar",
        section_subtitle: "Everything below comes from your recorded schedule. Levi never invents dates.",
        empty_state: "Nothing is on the calendar yet. Open a project to add scheduled work.",
        today_count: (n) => `You have ${n} scheduled item${n === 1 ? "" : "s"} on today's calendar.`,
        today_empty: "Nothing is on today's calendar.",
        this_week_count: (n) => `${n} scheduled item${n === 1 ? "" : "s"} in the next 7 days.`,
        conflict_pairs: (n) => `${n} crew conflict${n === 1 ? "" : "s"} detected — same assignee booked twice.`,
        overdue_in_progress: (n) => `${n} in-progress job${n === 1 ? " is" : "s are"} past the scheduled end date.`,
        next_visit_row: (customer, when) => `Next visit for ${customer} is ${when}.`,
      },
    },
  },
  crew_packet: {
    title_fallback: "Crew Packet",
    subtitle_fallback: "Live project view",
    live_banner: "This packet always reflects the current project. Changes to the project appear here immediately — no snapshot is stored.",
    print: "Print",
    refresh: "Refresh",
    back: "Back to project",
    loading: "Loading crew packet…",
    error: "Could not load the crew packet — please retry.",
    no_address: "No address on file",
    job_name_prefix: "Job:",
    untitled_event: "Scheduled work",
    untitled_line: "Untitled scope line",
    untitled_area: "Untitled work area",
    not_scheduled: "Not Scheduled",
    unassigned: "Unassigned",
    section_job: "Job",
    section_schedule: "Schedule",
    section_scope: "Organized Scope",
    section_work_areas: "Work Areas",
    section_measurements: "Measurements",
    section_photos: "Photos",
    section_safety: "Safety notes",
    section_materials: "Material notes",
    section_labor: "Labor notes",
    section_internal: "Internal crew notes",
    scope_empty: "No organized scope yet. Add an estimate to populate the scope list.",
    work_areas_empty: "No approved work areas yet.",
    measurements_empty: "No measurements captured yet.",
    photos_empty: "No photos attached to this project yet.",
    safety_empty: "No safety notes captured.",
    materials_empty: "No material notes captured.",
    labor_empty: "No labor notes captured.",
    internal_empty: "No internal crew notes captured.",
    urgency: {
      emergency: "Emergency", urgent: "Urgent",
      normal: "Normal", planned: "Planned",
    },
    status: {
      scheduled: "Scheduled", in_progress: "In progress",
      completed: "Completed", canceled: "Canceled",
    },
    assignee_kind: {
      self: "Me", individual: "Individual",
      crew: "Crew", subcontractor: "Subcontractor",
    },
    area_type: {
      interior_room: "Interior room", exterior_area: "Exterior area",
      roof_section: "Roof section", elevation: "Elevation",
      commercial_space: "Commercial space", mechanical_area: "Mechanical area",
      linear_run: "Linear run", object: "Object", custom: "Custom",
    },
  },
  material_takeoff: {
    card_overline: "Material Takeoff",
    open_new: "Start Takeoff",
    open_draft: "Open Draft",
    open_approved: "View Approved",
    loading: "Loading…",
    page_title: "Material Takeoff",
    page_subtitle: "Levi recommends. You decide.",
    back: "Back to project",
    picker_title: "How do you want to build this takeoff?",
    picker_intro: "Pick the entry path. Levi uses whatever project information is already available and never blocks you if the Price Book is incomplete.",
    editor_overline: "Draft takeoff",
    items_heading: "Materials",
    items_empty: "No materials on this takeoff yet. Add one to get started.",
    guidance_from_project: {
      heading: "Levi couldn't build a material plan from this project yet.",
      body: "To build from your project, Levi needs organized estimate scope or priced estimate line items. Once those exist, running Build from Project will produce a full plan. In the meantime you can pick one of the paths below.",
      action_continue_estimate: "Continue working on Estimate",
      action_build_from_measurements: "Build from Measurements",
      action_start_manual: "Start Manual Takeoff",
    },
    add_item: "Add material line",
    remove: "Remove",
    save_draft: "Save Draft",
    run_double_check: "Run Double Check",
    approve_takeoff: "Approve Takeoff",
    revise_takeoff: "Revise Takeoff",
    cancel: "Cancel",
    field_name: "Line label",
    field_product: "Product / description",
    field_qty: "Quantity",
    field_waste_pct: "Waste %",
    field_contingency_pct: "Contingency %",
    field_unit_price: "Unit price ($)",
    calc_required_waste: "Required waste",
    calc_contingency: "Contingency",
    calc_surplus: "Recoverable surplus",
    calc_total: "Total to purchase",
    calc_line_total: "Line total:",
    price_missing_help: "Price missing — Levi kept the quantity plan but the total is not calculated.",
    alternatives_toggle: (n) => `${n} alternative${n === 1 ? "" : "s"} to consider`,
    alternative: "Alternative",
    dc_heading: "Double Check findings",
    dc_never_mutates: "Advisory only — the takeoff is never modified.",
    measurements_prompt_title: "Enter the measurement Levi should plan around",
    measurements_area_label: "Area (sq ft)",
    measurements_trade_label: "Trade",
    measurements_system_label: "System / product family",
    measurements_submit: "Continue",
    state: {
      none: "No Takeoff yet",
      draft: "Draft Takeoff",
      approved: "Approved Takeoff",
      draft_revision: "Draft Revision",
    },
    state_desc: {
      none: "Levi can start a new material plan from your project, your measurements, or an empty template.",
      draft: "Review the plan, run Double Check, then approve when ready.",
      approved: "This takeoff is locked. Start a revision if the plan needs changes.",
      draft_revision: "A revision draft is in progress. Approve it to lock a new version.",
    },
    status: {
      draft: "Draft",
      approved: "Approved",
    },
    entry: {
      from_project: {
        title: "Build from Project",
        body: "Uses approved scope, work areas, measurements, and your Price Book.",
      },
      from_measurements: {
        title: "Build from Measurements",
        body: "Start from a measurement — Levi will suggest materials.",
      },
      manual: {
        title: "Manual · Help me build",
        body: "Enter what you know. Levi can help complete and optimize the plan.",
      },
    },
    trade: {
      roofing: "Roofing", drywall: "Drywall",
      siding: "Siding", flooring: "Flooring",
    },
    source: {
      price_book: "From your Price Book",
      project_scope: "From Project Scope",
      levi_suggestion: "Levi Suggestion",
      contractor_added: "Contractor Added",
      price_missing: "Price Missing",
    },
    toast_created: "Takeoff draft created.",
    toast_saved: "Draft saved.",
    toast_dc_ran: (n) => `Double Check finished. ${n} finding${n === 1 ? "" : "s"}.`,
    toast_approved: "Takeoff approved.",
    toast_revised: "Revision draft created.",
    toast_create_failed: "Could not create the takeoff — please retry.",
    toast_save_failed: "Could not save the draft — please retry.",
    toast_dc_failed: "Could not run Double Check — please retry.",
    toast_approve_failed: "Could not approve — please retry.",
    toast_revise_failed: "Could not start a revision — please retry.",
  },
  profitability: {
    // Slice 2N · Job Profitability Intelligence (PASS 2).
    card_overline: "Profitability",
    page_overline: "Profitability",
    page_title: "Profitability",
    back: "Back to project",
    loading: "Loading…",
    saving: "Saving…",
    starting: "Starting…",
    cancel: "Cancel",
    value_unavailable: "Not available yet",
    value_no_estimate: "Needs a contract-linked estimate",
    hint: {
      needs_estimate:
        "Add or link an estimate to establish the revenue basis.",
      enter_expected_margin_below:
        "Enter an expected margin below.",
      profit_reason: {
        no_confirmed_actual_costs:
          "Confirm at least one actual cost entry before actual profit is calculated.",
        no_supported_revenue:
          "Revenue basis isn't supported yet.",
      },
    },
    open_start: "Start tracking",
    open_workspace: "Open workspace",
    strip_revenue: "Revised contract",
    strip_expected_profit: "Expected profit",
    strip_actual_profit: "Actual profit",
    first_use_overline: "Optional job control",
    first_use_title: "Track this job's profitability?",
    first_use_body:
      "Profitability tracking is optional. Levi records your expected margin, tracks costs as you enter them, and shows where the job actually landed. It never changes your estimate, contract, or invoices. You decide when to start and confirm.",
    start_tracking: "Start profitability tracking",
    missing_data_heading: "A few things to keep in mind",
    missing_flag: {
      no_contract_linked_estimate:
        "No contract-linked estimate found — revenue basis is limited.",
      no_expected_margin: "Expected margin hasn't been entered yet.",
      unconfirmed_cost_entries:
        "Some cost entries are still unconfirmed and don't count toward confirmed actuals.",
      no_cost_entries_recorded: "No cost entries recorded yet.",
      no_confirmed_actual_costs:
        "Confirm at least one actual cost entry before actual profit becomes a supported result.",
    },
    state: {
      not_started: "Not started",
      projected_only: "Projected only",
      partially_tracked: "Partially tracked",
      actuals_in_progress: "Actual costs in progress",
      actuals_confirmed: "Actual profitability confirmed",
    },
    state_desc: {
      not_started:
        "Profitability tracking hasn't started for this job. Open the workspace to enter an expected margin.",
      projected_only:
        "Expected margin recorded. No actual costs have been entered yet.",
      partially_tracked:
        "Some costs are recorded but none are confirmed yet.",
      actuals_in_progress:
        "Confirmed actual costs are landing. Keep entering costs until the job is closed out.",
      actuals_confirmed:
        "Final profitability has been confirmed by you. Later corrections are tracked separately.",
    },
    section: {
      overview_overline: "Snapshot",
      overview_title: "Overview",
      revenue_overline: "Revenue",
      revenue_title: "Revenue",
      costs_overline: "Costs",
      costs_title: "Costs",
      entries_overline: "Cost entries",
      entries_title: "Cost entries & supporting documents",
      entries_subtitle:
        "Add costs as you incur them. Confirm each entry so it counts toward your actual profit. Excluded entries stay visible but don't count.",
      levi_overline: "Levi's review",
      levi_title: "Levi's review",
      levi_subtitle:
        "Levi reads only the numbers you've recorded. He never invents totals, changes your entries, or confirms profitability on your behalf.",
      improve_overline: "Improve the next job",
      improve_title: "Improve the next job",
      improve_subtitle:
        "Practical takeaways from this job. Nothing here changes your pricing settings or future estimates.",
      margin_overline: "Expected margin",
      margin_title: "Your expected margin",
      margin_subtitle:
        "Enter the margin percentage you expected for this job. Levi uses it to project expected profit and implied expected cost. You can update this anytime.",
      confirm_overline: "Final confirmation",
      confirm_title: "Confirm actual profitability",
      confirm_subtitle:
        "Confirming freezes the current snapshot for your records. Later corrections keep the historical snapshot intact.",
    },
    ov: {
      original_contract: "Original contract",
      approved_changes: "Approved changes",
      approved_changes_unsupported: "Not tracked in this repository yet",
      revised_contract: "Revised contract",
      expected_margin: "Expected margin",
      expected_margin_hint: "Enter an expected margin below",
      expected_profit: "Expected profit",
      implied_cost: "Implied expected cost",
      committed_total: "Committed costs",
      actual_confirmed: "Actual confirmed costs",
      actual_profit: "Actual profit",
      actual_margin: "Actual margin",
      remaining_vs_confirmed: "Remaining vs confirmed",
    },
    rev: {
      original_contract: "Original contract",
      approved_changes: "Approved changes",
      revised_contract: "Revised contract",
      invoiced: "Invoiced",
      collected: "Collected",
      remaining_receivable: "Remaining receivable",
      source_label: "Revenue basis",
      source: {
        contract_linked_estimate: "Contract-linked estimate",
        estimate_direct: "Latest project estimate fallback",
        none: "No supported estimate found",
      },
      source_fallback_note:
        "no signed contract yet; using the most recent project estimate.",
      approved_changes_unsupported_title: "Approved changes not tracked yet",
      approved_changes_unsupported_body:
        "This repository doesn't yet have a reliable way to separate approved change orders from other approved proposals. Rather than guess, we show the original contract as the known revenue basis and leave change-order tracking to a later authorized update.",
    },
    costs: {
      committed_total: "Committed",
      actual_confirmed: "Confirmed actual",
      unconfirmed_count: "Unconfirmed entries",
      excluded_count: "Excluded entries",
      category_col: "Category",
      total_col: "Total",
      unconfirmed_hint:
        "Some cost entries are still unconfirmed and are not included in the confirmed actual total above. Confirm each one when you're ready.",
    },
    category: {
      materials: "Materials",
      labor: "Labor",
      subcontractors: "Subcontractors",
      equipment_rentals: "Equipment & rentals",
      disposal: "Disposal",
      permits_fees: "Permits & fees",
      delivery_travel: "Delivery & travel",
      other_direct: "Other direct job costs",
    },
    classification: {
      actual: "Actual",
      committed: "Committed",
    },
    entry: {
      add_manual: "Add manual cost",
      add_document: "Add from document",
      empty: "No cost entries recorded yet.",
      form_title_add: "Add a cost entry",
      form_title_edit: "Edit cost entry",
      form_title_correction: "Correct confirmed entry",
      field_amount: "Amount ($)",
      field_category: "Category",
      field_classification: "Actual or Committed",
      field_vendor: "Vendor / payee",
      field_incurred_date: "Incurred date",
      field_description: "Description",
      field_correction_reason: "Reason for correction",
      correction_placeholder: "e.g. supplier credit received",
      save_add: "Save cost entry",
      save_edit: "Save changes",
      confirm: "Confirm",
      correct: "Correct",
      edit: "Edit",
      exclude: "Exclude",
      restore: "Restore",
      linked_document: "Linked document",
      source_document_suggestion: "Document suggestion",
      confirmed_badge: "Confirmed",
      unconfirmed_badge: "Unconfirmed",
      excluded_badge: "Excluded",
      included_badge: "Included",
      corrected_badge: "Corrected",
      source_manual: "Manual",
    },
    doc: {
      form_title: "Add a document-linked cost suggestion",
      foundation_note:
        "PASS 1 foundation: this creates an unconfirmed suggestion tied to a project document. Levi does not extract values from the document yet. Fill in what you know and confirm the entry when you're ready.",
      field_document_id: "Source document ID",
      field_document_id_placeholder: "Paste the document's ID from your project files",
      save: "Save suggestion",
    },
    margin: {
      pct_label: "Expected margin (%)",
      source_label: "Assumption source (optional)",
      source_placeholder: "e.g. typical roofing margin, prior similar job",
      save: "Save expected margin",
      derived_note_prefix: "Levi projects",
      derived_note: (profit, cost) =>
        `${profit} of expected profit and ${cost} of implied expected cost from your entered margin.`,
    },
    levi: {
      confirmed_state:
        "You've confirmed this job's profitability. Later corrections are tracked separately from the confirmation snapshot.",
      in_progress:
        "Confirmed actual costs are landing. Levi is watching the confirmed total move toward the implied expected cost.",
      partial:
        "Some costs are recorded but none are confirmed yet. Confirming each one will begin counting toward actual profit.",
      projected_only:
        "Expected margin recorded but no actual costs yet. The projected numbers rely on your margin assumption.",
      not_started:
        "Profitability tracking hasn't started for this job. This section is optional and never blocks the rest of the project.",
      unconfirmed: (n) =>
        `${n} cost entr${n === 1 ? "y" : "ies"} still unconfirmed. They aren't included in the confirmed actual total.`,
      no_margin:
        "No expected margin recorded. Enter one to see expected profit and implied expected cost.",
      no_revenue:
        "No contract-linked estimate found. Revenue basis is limited until an estimate is linked.",
      approved_changes_unsupported:
        "Approved-change amounts aren't tracked in this repository yet. The original contract stands as the known revenue basis.",
      margin_below: (pct) =>
        `Actual margin is running ${pct} percentage points below your expected margin so far.`,
      margin_above: (pct) =>
        `Actual margin is running ${pct} percentage points above your expected margin so far.`,
      promise:
        "Levi never changes your revenue, cost entries, margin assumption, or confirmation state on his own.",
    },
    improve: {
      top_category: (name, amount, pct) =>
        `${name} was the largest cost bucket at ${amount} (${pct} % of confirmed actual costs).`,
      missing_categories: (list) =>
        `No cost recorded yet in: ${list}. If any of these applied on this job, adding them makes the next similar bid more accurate.`,
      margin_shortfall: (pct) =>
        `Actual margin came in ${pct} percentage points below your expected margin. Consider revisiting labor, subcontractor pricing, or overage assumptions before bidding a similar job.`,
      margin_surplus: (pct) =>
        `Actual margin came in ${pct} percentage points above your expected margin. Review whether this reflects a one-off saving or a genuine pricing headroom you can rely on.`,
      confirm_before_review:
        "Confirm the remaining cost entries before treating these takeaways as final.",
      not_yet:
        "Not enough confirmed data yet. Once you've recorded and confirmed a few costs, Levi will surface practical takeaways here.",
      contractor_control_note:
        "You decide whether any of this feeds into your future pricing. Nothing here updates your Materials Price Book or pricing profile automatically.",
    },
    confirm: {
      warnings_heading: "Before you confirm, note the following",
      action: "Confirm actual profitability",
      confirming: "Confirming…",
      needs_tracking: "Start tracking first, then confirm.",
      needs_supported_actual_profit:
        "Actual profit isn't supported yet. Confirm at least one included, actual cost entry with a supported revenue basis before you can confirm final profitability.",
      confirmed_state: "Actual profitability confirmed.",
      confirmed_at: "Confirmed",
      snapshot_note:
        "A snapshot of this moment has been saved for your records. Any later corrections are tracked separately.",
    },
    toast: {
      load_failed: "Couldn't load profitability data — please retry.",
      tracking_started: "Profitability tracking started.",
      tracking_start_failed: "Couldn't start tracking — please retry.",
      margin_saved: "Expected margin saved.",
      margin_save_failed: "Couldn't save expected margin — please retry.",
      invalid_margin:
        "Enter a margin between 0 and less than 100 percent.",
      invalid_amount: "Enter a valid amount.",
      entry_added: "Cost entry added.",
      entry_add_failed: "Couldn't add cost entry — please retry.",
      entry_saved: "Cost entry saved.",
      entry_save_failed: "Couldn't save cost entry — please retry.",
      needs_correction_flag:
        "This entry is confirmed. Use the Correct action to record a controlled correction.",
      entry_confirmed: "Cost entry confirmed.",
      entry_confirm_failed: "Couldn't confirm cost entry — please retry.",
      entry_excluded: "Cost entry excluded.",
      entry_exclude_failed: "Couldn't exclude cost entry — please retry.",
      entry_restored: "Cost entry restored.",
      entry_restore_failed: "Couldn't restore cost entry — please retry.",
      missing_document: "Enter the source document ID.",
      invalid_document:
        "That document isn't linked to this project or isn't accessible.",
      suggestion_created: "Document-linked suggestion created (unconfirmed).",
      suggestion_failed:
        "Couldn't create the document suggestion — please retry.",
      profitability_confirmed: "Actual profitability confirmed.",
      tracking_not_started: "Start tracking before confirming.",
      no_supported_actual_profit:
        "Confirm at least one included, actual cost entry before you can confirm final profitability.",
      confirm_failed: "Couldn't confirm profitability — please retry.",
    },
  },
  // Slice 2P1 Pass B · contractor-facing sharing controls.
  sharing_card: {
    heading: "Customer Sharing",
    subheading: "You decide when this document is visible to the customer.",
    state_label: {
      private: "Private",
      approved: "Ready to Share",
      shared: "Shared",
      revoked: "Access Revoked",
      expired: "Expired",
    },
    levi_intro: "Levi note:",
    levi: {
      private: "Nothing has been shared with the customer yet.",
      approved: "This document is ready. You decide when to send it.",
      shared: "The customer can open this document using the active link.",
      revoked: "The previous customer link no longer works.",
      expired: "The customer link has expired. Regenerate it to share again.",
    },
    meta: {
      expires_prefix: "Expires",
      expires_none: "No expiration set",
      viewed_yes: "Customer opened the link",
      viewed_no: "Customer has not opened the link yet",
      signed_yes: "Contract signed by customer",
      signed_no: "Not signed yet",
    },
    action: {
      approve: "Ready to Share",
      approve_and_email: "Share with Customer · Email Link",
      copy: "Copy Link",
      email: "Email Link",
      regenerate: "Regenerate Link",
      revoke: "Revoke Access",
      share_again: "Share Again",
    },
    action_busy: {
      approve: "Approving…",
      copy: "Copying…",
      email: "Sending…",
      regenerate: "Regenerating…",
      revoke: "Revoking…",
      share_again: "Restoring…",
    },
    confirm: {
      approve_email_title: "Share this document with the customer?",
      approve_email_body:
        "This will approve the document for customer access and email the link to {email}.",
      regenerate_title: "Regenerate the customer link?",
      regenerate_body:
        "The previous customer link will stop working immediately. A new link will be issued.",
      revoke_title: "Revoke customer access?",
      revoke_body:
        "The customer's current link will stop working immediately. You can share again later.",
      share_again_title: "Share this document again?",
      share_again_body:
        "This will approve the document and issue a fresh customer link.",
      cancel: "Cancel",
      confirm: "Confirm",
      email_prompt_label: "Email link to",
    },
    toast: {
      approved: "Marked ready to share.",
      approve_failed: "Couldn't mark ready to share — please retry.",
      copied: "Customer link copied.",
      copy_failed: "Couldn't copy the link — copy it manually below.",
      emailed: "Link sent to {email}.",
      emailed_mock: "Link queued in mock mode — {email}.",
      email_failed: "Couldn't send the email. Nothing was sent.",
      regenerated: "New customer link issued.",
      regenerate_failed: "Couldn't regenerate the link — please retry.",
      revoked: "Customer access revoked.",
      revoke_failed: "Couldn't revoke access — please retry.",
      share_again_done: "Document is ready to share again.",
    },
    history: {
      heading: "Recent activity",
      empty: "No sharing activity yet.",
      event: {
        approved: "Ready to Share",
        shared: "Shared",
        regenerated: "Link regenerated",
        link_sent: "Sent by email",
        first_viewed: "Customer opened the link",
        signed: "Contract signed",
        revoked: "Access revoked",
      },
    },
    preference: {
      no_customer_access:
        "Customer access is disabled for this job. Sharing controls are unavailable.",
      email_only:
        "Email is the preferred delivery method for this customer.",
      sms_only:
        "SMS delivery isn't available yet — use email or copy the link instead.",
      portal_and_email:
        "This customer prefers email. Only the link tools below are available.",
    },
    load_error: "Couldn't load the sharing status. Please refresh.",
  },
  // Slice 2P1 Pass C · customer-facing Closeout Packet view + Levi
  // guidance + closeout share-email strings. All customer-facing.
  public_closeout: {
    overline: "Project closeout",
    title: "Your project closeout",
    jobsite: "Jobsite",
    project_type: "Project type",
    completion_date: "Completion date",
    invoice_status: "Invoice status",
    work_summary: "What was completed",
    completion_notes: "Notes from your contractor",
    scope: "Work delivered",
    workmanship_warranty: "Workmanship warranty",
    manufacturer_warranty: "Manufacturer warranty",
    maintenance_notes: "Care & maintenance",
    warranty_contact: "Warranty & service contact",
    review_request: "A note from your contractor",
    referral_request: "Referrals",
    final_punch_list: "Final punch list",
    footer_note:
      "This project closeout was shared with you by your contractor. Contact your contractor directly with any questions.",
  },
  levi_customer_guide: {
    welcome:
      "Hi, I'm Levi. Your contractor has shared this project closeout with you.",
    framing:
      "You're seeing only what your contractor chose to share. Internal costs, notes, and drafts are not included.",
    contact:
      "If you have questions, please contact your contractor directly.",
  },
  email_closeout: {
    subject_prefix: "Your project closeout is ready",
    cta: "View project closeout",
  },
  // Slice 2Q · Levi-Guided Job Closeout (contractor-facing).
  project_closeout: {
    heading: "Project Closeout",
    subheading: "Levi helps you prepare. You decide when to close.",
    state: {
      in_progress: "In progress",
      needs_attention: "Needs attention",
      ready_to_close: "Ready to close",
      closed: "Closed",
    },
    levi_intro: "Levi's read on this project:",
    levi_disclaimer: "Levi recommends. The contractor decides.",
    levi: {
      progress: "The project is still moving. Come back when the crew wraps up.",
      warn: "A few items look thin. Review the checklist before you close.",
      missing: "Some closeout items are missing. Filling them in helps your customer and your records.",
      ready: "Everything I can check looks ready. Close when you're satisfied.",
      closed: "This project is closed. Historical records are still available.",
    },
    item: {
      work_summary: "Work completion summary",
      final_photos: "Final photos",
      punch_list: "Punch-list / open work orders",
      workmanship_warranty: "Workmanship warranty",
      manufacturer_warranty: "Manufacturer warranty",
      final_invoice: "Final invoice / payment",
      closeout_docs: "Customer-facing closeout documents",
    },
    status: {
      ok: "Ready", warn: "Needs review", missing: "Missing", na: "Not applicable",
    },
    action: { close: "Close Project", reopen: "Reopen Project" },
    non_blocking_note: "Warnings do not block closing — you'll confirm.",
    closed_at_prefix: "Closed on",
    confirm: {
      close_title: "Close this project?",
      close_body:
        "Internal records stay available. Customer access continues to be controlled through the existing sharing lifecycle. Closing does not send any customer message.",
      warnings_prefix: "You have",
      warnings_suffix: "checklist items that may need attention. You can still close.",
      cancel: "Cancel",
      close_confirm: "Close project",
      reopen_title: "Reopen this project?",
      reopen_body:
        "Reopening returns the project to active state. Historical closeout information is preserved.",
      reopen_confirm: "Reopen project",
    },
    toast: {
      closed: "Project closed.",
      close_failed: "Couldn't close the project — please retry.",
      reopened: "Project reopened.",
      reopen_failed: "Couldn't reopen the project — please retry.",
    },
  },
  // Slice 2T · Levi 1.0 Project Snapshot (Project Workspace card).
  levi_project_snapshot: {
    header_kicker: "Levi",
    header: "Here's what I see on this project.",
    section_recommends: "Levi recommends",
    section_also_noticed: "Also noticed",
    section_warnings: "Worth a closer look",
    ask_btn: "Ask Levi about anything on this project",
    rec: {
      all_clean: "Everything looks good on this project right now.",
      no_estimate: "This project doesn't have an estimate yet.",
      estimate_empty: "The estimate is here but there are no line items yet.",
      estimate_unpriced: ({ count, names } = {}) => {
        const n = Number(count || 0);
        const list = Array.isArray(names) && names.length > 0 ? ` (${names.join(", ")})` : "";
        if (n === 1) return `I found 1 item that still needs pricing${list}.`;
        return `I found ${n} items that still need pricing${list}.`;
      },
      dc_not_run: "This estimate hasn't been Double Checked yet.",
      dc_findings_red: ({ red } = {}) => {
        const n = Number(red || 0);
        if (n === 1) return "Double Check flagged 1 red item — worth reviewing before sharing.";
        return `Double Check flagged ${n} red items — worth reviewing before sharing.`;
      },
      dc_findings_yellow: ({ yellow } = {}) => {
        const n = Number(yellow || 0);
        if (n === 1) return "Double Check flagged 1 yellow item worth a look.";
        return `Double Check flagged ${n} yellow items worth a look.`;
      },
      dc_stale: "The estimate has changed since the last Double Check — worth running it again.",
      no_proposal: "You're ready to draft a proposal whenever you are.",
      proposal_awaiting: "The proposal was shared, but the customer hasn't responded yet.",
      approved_no_contract: "The proposal was approved — the contract is next.",
      contract_awaiting_signature: "Contract sent — waiting on the signature.",
      signed_no_invoice: "The contract is signed — time to invoice.",
      invoice_payment_due: "There's an unpaid invoice on this project.",
      paid_no_closeout: "Everything's paid. Generate the closeout packet whenever you're ready.",
      customer_no_docs_shared: "Nothing has been shared with the customer yet.",
      project_closed: "This project is closed. Nice work.",
      customer_contact_missing: "I have the customer's name, but I still need an email or phone number.",
      no_narrative: "I still need a little more detail about the work.",
    },
    action: {
      open_estimate: "Open estimate",
      create_estimate: "Start estimate",
      edit_intake_notes: "Edit intake notes",
      jump_missing_pricing: "Fix missing pricing",
      run_double_check: "Open Double Check",
      open_shared_proposal: "Open proposal",
      open_pending_contract: "Open contract",
      open_unpaid_invoice: "Open invoice",
      new_proposal: "Draft proposal",
      new_contract: "Draft contract",
      new_invoice: "Create invoice",
      generate_closeout: "Generate closeout packet",
    },
    followup: {
      scope: "Ask Levi about the scope on this project.",
      customer_status: "Ask Levi where things stand with the customer.",
    },
  },
  // Slice 2T · Levi 1.0 Full Project Intake Coach.
  levi_intake_coach: {
    header_kicker: "Levi",
    empty_hint: "I'll start helping as soon as you add a customer name — that's all I need.",
    ask_btn: "Ask Levi",
    status: {
      no_customer_name: "Add a customer name and I'll start helping you gather the rest.",
      ready: "You have enough information to get started.",
      need_more_detail: "I still need a little more detail about the work.",
      address_but_no_email: "You added the job address, but the customer email is still missing.",
      contact_missing: "The description looks good, but I still need an email or phone number.",
    },
    also: {
      no_job_address: "The job address is still missing.",
      no_phone: "A phone number would help if you need to reach the customer quickly.",
      have_voice_no_typed: "Your voice notes are captured — feel free to type any specifics too.",
      no_contact_yet: "Add an email or phone number so we can reach the customer.",
    },
  },
  // Slice 2U · Read-Only Support Account View.
  support: {
    header_kicker: "JobClose Customer Support",
    header_body_read_only:
      "Read-Only Support View. You can see what the contractor is experiencing but cannot change any contractor record.",
    viewed_by: "Viewed by",
    read_only: "Read-Only Support View",
    refreshed_at: "Refreshed",
    refresh: "Refresh",
    back_to_search: "Back to search",
    unknown: "Unknown",
    yes: "Yes",
    no: "No",
    close: "Close",
    loading: "Loading…",
    footer_no_mutations:
      "This view is read-only. Every listed action opens a contractor's own workflow — support cannot mutate contractor records from this workspace.",
    search: {
      title: "Contractor Search",
      subtitle: "Locate a contractor account to open a read-only support view.",
      label: "Search by email, company name, or contractor name",
      placeholder: "e.g. acme, jsmith@acme.com, John Smith",
      submit: "Search",
      searching: "Searching…",
      error_min_length: "Please enter at least 2 characters.",
      error_generic: "The search failed. Please try again.",
      results_count: ({ count } = {}) =>
        Number(count || 0) === 1 ? "1 result" : `${count} results`,
      results_none: "No contractors matched your search.",
      col_name: "Contractor",
      col_company: "Company",
      col_email: "Email",
      col_joined: "Joined",
      col_onboarding: "Onboarding",
      onboarding_complete: "Completed",
      onboarding_incomplete: "In progress",
      open_account: "Open account",
    },
    tenant_context: {
      supporting: "Supporting",
      owner: "Owner",
      tenant_id: "Tenant ID",
      viewed_by: "Viewed by",
    },
    account: {
      title: "Support Account View",
      loading: "Loading contractor account…",
      identity: "Account identity",
      subscription: "Subscription & entitlement",
      aggregates: "Projects & documents",
      sharing: "Customer document sharing",
      projects: "Projects",
      project_summary: "Project diagnostic summary",
      diagnostics: "Diagnostics",
      timeline: "Account activity timeline",
    },
    identity: {
      full_name: "Contractor",
      company: "Company",
      email: "Email",
      phone: "Phone",
      created: "Created",
      preferred_lang: "App language",
      onboarding: "Onboarding",
      trades: "Trades",
      trades_none: "None selected",
    },
    subscription: {
      none: "No subscription record has been created for this contractor yet.",
      tier: "Tier",
      status: "Status",
      billing_cycle: "Billing cycle",
      current_period_end: "Current period ends",
      trial_end: "Trial ends",
      launch_offer: "Launch offer locked",
      cancel_at_period_end: "Cancel at period end",
      updated: "Updated",
    },
    aggregates: {
      projects_total: "Projects",
      projects_draft: "Draft",
      projects_active: "Active",
      projects_closed: "Closed",
      estimates: "Estimates",
      proposals: "Proposals",
      contracts: "Contracts",
      invoices_total: "Invoices",
      invoices_paid: "Invoices paid",
      invoices_unpaid: "Invoices unpaid",
      work_areas: "Work areas",
      voice_failed: "Voice notes failed",
      dc_red: "Double Check red",
      dc_yellow: "Double Check yellow",
      dc_ran: "Estimates with Double Check",
    },
    sharing: {
      kind: "Document kind",
      tokens_hidden: "Sharing tokens are never shown here — the contractor remains in control of customer access.",
    },
    projects: {
      none: "This contractor has no projects yet.",
      customer: "Customer",
      job_type: "Job",
      address: "Address",
      status: "Status",
      updated: "Updated",
      diagnose: "Diagnose",
      photos_count: "Photos",
      estimates: "Estimates",
      docs: "Documents",
    },
    diagnostics: {
      email_total: "Emails logged",
      email_failed: "Emails failed",
      email_queued: "Emails queued",
      voice_failed: "Voice notes failed",
      voice_pending: "Voice notes pending",
      login_failed: "Login failed",
      login_success: "Login success",
      password_failed: "Password change failed",
    },
    timeline: {
      none: "No timeline milestones are available yet.",
      milestone: {
        account_created: "Account created",
        onboarding_completed: "Onboarding completed",
        pricing_setup_completed: "Pricing setup completed",
        first_project_created: "First project created",
        first_estimate_created: "First estimate created",
        first_proposal_shared: "First proposal shared",
        first_contract_signed: "First contract signed",
        first_invoice_created: "First invoice created",
        last_meaningful_activity: "Last meaningful activity",
      },
    },
  },
  // Slice VI-01 · Vision Intelligence & Guided Job Capture Foundation.
  // Contractor-facing wording never uses the internal phrase
  // "measurement candidate" — every visible string uses
  // "Levi measurement" or "preliminary measurement".
  vi01: {
    overline: "Levi's review",
    loading: "Levi is preparing your review…",
    empty_headline: "Levi is standing by.",
    empty_body:
      "Once you finalize this Video Job Walk, Levi will summarize what he saw, what he measured, and where he is uncertain.",
    headline: ({ work_areas, measurements, observations }) => {
      const parts = [];
      if (work_areas > 0) {
        parts.push(`I identified ${work_areas} work area${work_areas === 1 ? "" : "s"}.`);
      }
      if (measurements > 0) {
        parts.push(
          `I recorded ${measurements} preliminary measurement${measurements === 1 ? "" : "s"}.`
        );
      }
      if (observations > 0) {
        parts.push(
          `I noticed ${observations} observation${observations === 1 ? "" : "s"} worth reviewing with you.`
        );
      }
      return parts.length ? parts.join(" ") : "Here is what I gathered from your walk.";
    },
    confidence_summary: ({ high, uncertain }) => {
      if (high > 0 && uncertain > 0) {
        return `I am highly confident in ${high} measurement${high === 1 ? "" : "s"}, and ${uncertain} still need${uncertain === 1 ? "s" : ""} another look.`;
      }
      if (high > 0) return `I am highly confident in ${high} measurement${high === 1 ? "" : "s"}.`;
      if (uncertain > 0) return `${uncertain} measurement${uncertain === 1 ? "" : "s"} still need${uncertain === 1 ? "s" : ""} another look.`;
      return "";
    },
    review_prompt: "Would you like to review the measurements first, or go through what I noticed?",
    choice_measurements: "Review measurements first",
    choice_observations: "Walk through observations",
    measurements_section_title: "Levi measurements — preliminary",
    measurements_empty: "Levi did not record any preliminary measurements on this walk.",
    observations_section_title: "What Levi noticed",
    observations_empty: "Levi did not add any observations on this walk.",
    preliminary_measurement_label: ({ label }) => `${label} — preliminary`,
    confidence_high: "High confidence",
    confidence_moderate: "Moderate confidence",
    confidence_low: "Low confidence",
    evidence_at: "at",
    decision_notice:
      "These findings are preliminary. Review and approval controls will be added in the next measurement-decision step. For now Levi's measurements stay recorded and preliminary — nothing is drafted, sent, or promoted without you.",
    show_evidence_panel: "Show structured evidence",
    hide_evidence_panel: "Hide structured evidence",
    evidence_panel_source: "Source",
    evidence_panel_policy: "Policy version",
    evidence_panel_footnote:
      "This panel is for auditing. Levi's spoken review above is the primary experience.",
    coaching: {
      heading: "Heads-up from Levi",
      dismiss_aria: "Dismiss this coaching prompt",
      move_slower: "Try panning a little more slowly so Levi can follow along.",
      hold_steady: "Try holding the camera steadier for a moment.",
      improve_lighting: "It's a little dark in here — flip on a light or add a work light if you can.",
      move_farther_back: "Step back a bit so Levi can see the whole area.",
      show_opposite_wall: "Show the opposite wall too so Levi can measure the full room.",
      include_both_corners: "Include both corners in the frame so Levi has a reference length.",
      capture_ceiling: "Point the camera up at the ceiling for a moment if it's part of the scope.",
      provide_scale_reference: "Give Levi a scale reference — a tape measure, a piece of trim, or your hand.",
      revisit_obstructed_area: "That area was obstructed — could you revisit it from another angle?",
      clarify_ambiguity: "Say that part again — Levi wasn't sure which item you were describing.",
    },
  },
  // Slice LEVI-VOICE-01 · Levi voice preferences (EN).
  levi_voice: {
    overline: "Levi's voice",
    title: "Voice preferences",
    description: "Choose how Levi sounds when he speaks to you. Changes apply immediately.",
    tts_enabled_label: "Speak Levi's replies out loud",
    language_label: "Voice language",
    language_option_en: "English",
    language_option_es: "Spanish",
    gender_label: "Voice character",
    gender_option_friendly_female: "Friendly female",
    gender_option_friendly_male: "Friendly male",
    gender_option_no_preference: "No preference",
    rate_label: "Speaking rate",
    rate_slower: "Slower",
    rate_default: "Normal",
    rate_faster: "Faster",
    preview_button: "Hear a sample",
    disabled_note: "Voice is off. Turn it on above to hear Levi.",
  },
  universal_search: {
    placeholder: "Search customers, projects, addresses…",
    searching: "Searching…",
    clear: "Clear search",
    no_results: (q) => `No matches for "${q}"`,
    customer_hint: "Customer",
    other_projects: "Other matching projects",
  },
  status: {
    draft: "Draft",
    active: "Active",
    in_progress: "In progress",
    scheduled: "Scheduled",
    completed: "Completed",
    archived: "Archived",
    sent: "Sent",
    approved: "Approved",
    declined: "Declined",
    awaiting_signature: "Awaiting signature",
    signed: "Signed",
    paid: "Paid",
    unpaid: "Unpaid",
    overdue: "Overdue",
  },
  company_settings: {
    title: "Company & Settings",
    subtitle: "Everything that shapes how JobClose AI works for your business.",
    company_overline: "Company",
    company_title: "Company Profile",
    company_body: "Name, address, phone, logo, license, time zone.",
    documents_overline: "Documents",
    documents_title: "Document Defaults",
    documents_body: "Proposals, contracts, invoices, updates, warranty, closeout.",
    closeout_overline: "Closeout & Warranty",
    closeout_title: "Closeout Defaults",
    closeout_body: "Warranty periods, sign-off flow, closeout packet.",
    pricing_overline: "Pricing · Foundational",
    pricing_title: "Pricing Settings",
    pricing_body: "Your approved pricing lives here. All-in by Unit, by Job or System, Learn From My Estimates, and trade setup.",
    levi_overline: "Levi",
    levi_title: "Levi Settings · Voice · Persona · Language",
    levi_body: "Professional or Warm, gender, language, speaking rate — preview any change before saving.",
    account_overline: "Account & Billing",
    account_title: "Account · Subscription",
    account_body: "Stripe manages card data. JobClose never handles it.",
    website_overline: "Grow your business",
    website_headline: "Need a professional website?",
    website_product: "Contractor Website Starter · starting at $499",
    website_body: "A polished JobClose-crafted website customized for your business.",
    website_bullet_customized: "Customized for your business",
    website_bullet_hosting: "One year of hosting included",
    website_bullet_domain: "You purchase and own your own domain",
    website_cta: "Learn more",
    website_placeholder_label: "Placeholder — PO review required",
    footer_tagline: "Your business. Your pricing. Powered by Levi.",
  },
};

const ES = {
  brand: "Configura tu negocio",
  brand_tagline:
    "Configúralo una vez. Reutilízalo en trabajos futuros. Ajústalo cuando quieras.",
  welcome_hi: "Bienvenido",
  welcome_body_intro:
    "JobClose AI está preparando tu configuración. Esto nos ayuda a:",
  welcome_bullets: [
    "Organizar la información del trabajo por oficio",
    "Reutilizar los datos de tu negocio en cada trabajo",
    "Preparar estimaciones y documentos para el cliente",
    "Aplicar las preferencias que guardas aquí",
  ],
  welcome_continue: "Continuar a Negocio y oficios",
  welcome_active_company: "Empresa activa",
  step_labels: {
    business_and_trades: "Negocio y oficios",
    trade_setup: "Configuración de oficios",
    documents_and_customer_defaults: "Documentos y preferencias del cliente",
    review_and_ready: "Revisar y finalizar",
  },
  progress_label: (done, total) => `${done} de ${total} pasos completados`,
  next_step_label: (label) => `Siguiente: ${label}`,
  step1_title: "Negocio y oficios",
  step1_subtitle:
    "Los datos de tu negocio, los oficios que ofreces y el orden de configuración.",
  section_business: "Información del negocio",
  section_trades: "Oficios",
  section_trade_order: "Orden de configuración de oficios",
  section_trade_order_help:
    "Elige el orden en que quieres configurar tus oficios. Puedes cambiarlo después.",
  section_logo: "Logotipo de la empresa",
  section_time_zone: "Zona horaria",
  labels: {
    business_name: "Nombre del negocio",
    business_phone: "Teléfono del negocio",
    business_email: "Correo del negocio",
    business_zip: "Código postal del negocio",
    business_address: "Dirección del negocio (opcional)",
    business_city: "Ciudad",
    business_state: "Estado",
    website: "Sitio web (opcional)",
    service_area: "Área de servicio (opcional)",
    license: "Licencia / credenciales (opcional)",
    logo: "Logotipo de la empresa (opcional)",
    time_zone: "Zona horaria",
    trades: "Oficios que ofreces",
  },
  time_zone_detected: (label) => `Detectada: ${label} — cámbiala si es necesario.`,
  logo_replace: "Reemplazar logotipo",
  logo_remove: "Quitar logotipo",
  logo_remove_confirm: "¿Quitar el logotipo actual?",
  logo_types: "PNG, JPG, WebP o SVG (máx. 2 MB).",
  trade_status: {
    in_progress: "Configuración en curso",
    later: "Configurar después",
    ready: "Listo",
  },
  trades_hint:
    "Selecciona todos los oficios que ofreces. No necesitas elegir uno principal.",
  save: "Guardar Paso 1",
  saving: "Guardando…",
  saved: "Guardado",
  save_failed:
    "No se pudo guardar. Tus entradas se conservan — intenta de nuevo.",
  back_to_dashboard: "Volver al panel",
  future_step_title: "Próximamente",
  future_step_body: (label) =>
    `${label} aún no está disponible. Podrás completarlo en el siguiente paso de Configura tu negocio.`,
  step1_complete_banner:
    "Paso 1 completado — sigue: Configuración de oficios.",
  required_hint: "Requerido",
  errors: {
    business_name_required: "El nombre del negocio es obligatorio.",
    business_phone_required: "El teléfono del negocio es obligatorio.",
    business_email_required: "El correo del negocio es obligatorio.",
    business_zip_required: "El código postal del negocio es obligatorio.",
    at_least_one_trade: "Selecciona al menos un oficio.",
  },

  review_title: "Revisar y finalizar",
  review_intro:
    "Tómate un momento para revisar todo lo que configuraste. Aquí no se agregan nuevas opciones.",
  review_section_company: "Perfil de la empresa",
  review_section_trades: "Oficios activos",
  review_section_documents: "Preferencias de documentos",
  review_company_no_name: "Aún no hay nombre de empresa",
  review_trades_none: "Aún no hay oficios seleccionados",
  review_pill_configured: "Configurado",
  review_pill_set_later: "Configurar después",
  review_pill_needs_attention: "Requiere atención",
  review_finish_setup: "Finalizar configuración",
  review_continue_later: "Continuar después",
  review_edit: "Editar",
  review_hint_complete:
    "La configuración está completa. Puedes revisar cualquier sección cuando quieras.",
  review_hint_ready:
    "Todo listo. Presiona Finalizar configuración para entrar al panel.",
  review_hint_blocked:
    "Cada familia de documentos debe estar Lista o marcada como Configurar después antes de finalizar.",
  family_names: {
    proposal: "Preferencias de propuesta",
    contract: "Preferencias de contrato",
    invoice: "Preferencias de factura",
    customer_update: "Preferencias de aviso al cliente",
    closeout: "Preferencias de cierre",
    warranty: "Preferencias de garantía",
  },

  // Slice 2S · Continuidad EN/ES — Extras de SetUpYourBusiness
  soyb: {
    toast_logo_uploaded: "Logotipo subido",
    toast_logo_upload_failed: "No se pudo subir el logotipo",
    toast_logo_remove_failed: "No se pudo quitar el logotipo",
    toast_finish_setup_failed:
      "No se pudo finalizar la configuración. Inténtalo de nuevo.",
    step2_subtitle:
      "Configura cómo cotizas y ejecutas cada tipo de trabajo. Puedes ajustarlo cuando quieras.",
    continue_to_trade_setup: "Continuar a Configuración de oficios",
    step1_blocked_title: "Negocio y oficios requiere atención",
    step1_blocked_body:
      "Corrige los campos resaltados abajo antes de continuar a Configuración de oficios. Tu información guardada no ha cambiado.",
    logo_upload: "Subir",
    future_step_default_label: "Siguiente paso",
    placeholder_street: "Calle (opcional)",
    trade_action: {
      edit_setup: "Editar configuración",
      resume_setup: "Reanudar configuración",
      continue_setup: "Continuar configuración",
    },
    title_status_only: "Solo cambiar estado",
    title_open_trade: "Abrir este oficio en Configuración de oficios",
    title_move_up: "Mover arriba (solo orden)",
    title_move_down: "Mover abajo (solo orden)",
    add_another_trade: "Agregar otro oficio",
    add_another_trade_hint:
      "¿Vas a ofrecer un oficio nuevo? Agrégalo en Configura tu negocio — tus oficios, precios e historial actuales se conservan sin cambios.",
    add_another_trade_cta_aria:
      "Abrir Configura tu negocio para agregar otro oficio",
    systems_optional_helper:
      "Solo necesitas configurar los sistemas que realmente instalas. Los sistemas que no utilizas pueden dejarse en blanco y no bloquearán tu configuración de precios.",
  },

  // Slice 2S · Continuidad EN/ES — Paso 3 (Documentos y preferencias del cliente)
  step3: {
    loading: "Cargando la configuración de documentos…",
    load_error: "No se pudo cargar el estado de la configuración de documentos",
    headline: "Documentos y preferencias del cliente",
    subtitle:
      "Define el texto estándar que JobClose AI usará para preparar futuras propuestas, contratos, facturas, avisos al cliente y paquetes de cierre. Puedes revisar y editar cada documento antes de enviarlo.",
    ready_helper_text:
      "Listo significa que revisaste esta sección y aceptas tu texto guardado o los valores integrados de JobClose AI. Aún podrás editar los documentos futuros antes de enviarlos.",
    needs_attention_pill: "Requiere atención",
    complete_title: "Documentos y preferencias del cliente completos",
    complete_body:
      "Tus familias de documentos están listas o marcadas para configurar después. Puedes actualizarlas cuando quieras.",
    continue_to_review: "Continuar a Revisar y finalizar",
    review_defaults: "Revisar preferencias de documentos",
    blocked_title: "Documentos y preferencias del cliente requiere atención",
    blocked_body:
      "Una de tus familias de documentos contiene información de ejemplo que podría aparecer en un documento del cliente. Corrígela antes de continuar a Revisar y finalizar.",
    fix_family: (name) => `Corregir ${name}`,
    current_status: "Estado actual:",
    mark_ready: "Marcar como listo",
    set_up_later: "Configurar después",
    dirty_blocker:
      "Guarda o descarta los cambios no guardados de esta sección antes de marcarla como lista.",
    conflict_blocker:
      "Resuelve la advertencia de borrador o conflicto antes de cambiar el estado de esta sección.",
    status_save_error:
      "No se pudo guardar el estado de configuración de esta sección.",
    later_dialog_title: "¿Configurar esta sección después?",
    later_dialog_body:
      "Puedes volver cuando quieras. JobClose AI usará sus preferencias integradas disponibles hasta que personalices esta sección.",
    later_dialog_keep: "Seguir configurando",
    later_dialog_confirm: "Configurar después",
    toast_save_recommended: "Guardado con el texto recomendado por JobClose AI.",
    toast_save_customized: "Se guardó tu texto personalizado.",
    toast_save_generic: "Configuración guardada.",
    toast_guided_partial:
      "No se pudo guardar por completo. Revisa la sección e inténtalo de nuevo.",
    toast_later_failed:
      "No se pudo guardar Configurar después para esta familia.",
    toast_outside_failed:
      "No se pudo guardar Gestionar fuera para esta familia.",
    explain_generic_error: "No se pudo guardar la configuración guiada.",
    family_status_labels: {
      not_started: "Sin iniciar",
      in_progress: "En curso",
      ready: "Listo",
      later: "Configurar después",
    },
    family_action_labels: {
      not_started: "Configurar",
      in_progress: "Continuar configuración",
      ready: "Editar configuración",
      later: "Configurar ahora",
    },
    family_titles: {
      proposal: "Preferencias de propuesta",
      contract: "Preferencias de contrato",
      invoice: "Preferencias de factura",
      customer_update: "Preferencias de aviso al cliente",
      closeout: "Preferencias de cierre",
      warranty: "Preferencias de garantía",
    },
    family_purposes: {
      proposal:
        "Texto estándar para futuras propuestas y condiciones de pago.",
      contract:
        "Términos de contrato estándar, descargos y lenguaje de aprobación del cliente.",
      invoice: "Notas estándar de factura e instrucciones de pago.",
      customer_update:
        "Firma, solicitud de reseña y solicitud de referidos estándar para avisos aprobados por el contratista.",
      closeout:
        "Texto estándar de mantenimiento, cierre, reseña y referidos para futuros paquetes de cierre.",
      warranty:
        "Términos estándar de garantía de mano de obra, manejo de garantía del fabricante y contacto para servicio de garantía.",
    },
  },

  // Slice 2S · Continuidad EN/ES — Traducciones de configuración del editor sin formato
  family_config: {
    proposal: {
      title: "Preferencias de propuesta",
      purpose: "Texto que se antepone a futuras propuestas. Las propuestas existentes no cambian.",
      snapshotNote: "Estos valores se usan solo para futuras propuestas. Las propuestas existentes permanecen como copias intactas y aún puedes editar el texto antes de enviar.",
      saveButtonLabel: "Guardar preferencias de propuesta",
      successMessage: "Preferencias de propuesta guardadas. Se aplicarán a futuras propuestas.",
      fields: {
        proposal_intro: {
          label: "Introducción de la propuesta",
          helper: "El primer párrafo que lee el cliente. Escríbelo con la voz de tu empresa.",
          placeholder: "Ingresa el párrafo inicial con el que deben comenzar las nuevas propuestas.",
        },
        proposal_footer: {
          label: "Notas de alcance / pie de propuesta",
          helper: "Notas estándar de alcance, reglas de órdenes de cambio, notas de garantía u otro texto que suele aparecer cerca del final de una propuesta.",
          placeholder: "Ingresa las notas de cierre o texto estándar que aparecen al final de tus propuestas.",
        },
        deposit_terms: {
          label: "Condiciones de depósito",
          helper: "Explica cuándo y cuánto debe pagar el cliente antes de que comience el trabajo.",
          placeholder: "Explica tu requisito de depósito con tus propias palabras.",
        },
        final_payment_terms: {
          label: "Condiciones del pago final",
          helper: "Explica cuándo vence el saldo restante.",
          placeholder: "Explica cuándo se espera el pago final.",
        },
        late_payment_note: {
          label: "Nota de pago tardío",
          helper: "Explica qué sucede cuando un saldo vencido queda sin pagar.",
          placeholder: "Explica tu política para saldos vencidos.",
        },
      },
    },
    contract: {
      title: "Preferencias de contrato",
      purpose: "Texto de contrato estándar que tu empresa ha revisado y aprobado. Los contratos existentes no cambian.",
      snapshotNote: "Estos valores se usan solo para futuros contratos. Los contratos existentes permanecen como copias intactas y aún puedes editar el texto antes de enviar.",
      saveButtonLabel: "Guardar preferencias de contrato",
      successMessage: "Preferencias de contrato guardadas. Se aplicarán a futuros contratos.",
      fields: {
        contract_terms: {
          label: "Términos del contrato",
          helper: "Los términos estándar de tu empresa para realizar el trabajo.",
          placeholder: "Ingresa los términos estándar de tu empresa.",
        },
        contractor_disclaimer: {
          label: "Descargo del contratista",
          helper: "Limitaciones, exclusiones y responsabilidades específicas de tu empresa u oficio.",
          placeholder: "Ingresa las limitaciones, exclusiones o responsabilidades específicas de tu oficio.",
        },
        customer_approval_language: {
          label: "Texto de aprobación del cliente",
          helper: "El reconocimiento que aparece cerca de la aprobación o firma del cliente.",
          placeholder: "Ingresa el reconocimiento que aparece encima de la línea de firma.",
        },
      },
    },
    invoice: {
      title: "Preferencias de factura",
      purpose: "Notas estándar e instrucciones de pago para futuras facturas. Las facturas existentes no cambian.",
      snapshotNote: "Estos valores se usan solo para futuras facturas. Las facturas existentes permanecen como copias intactas y aún puedes editar el texto antes de enviar.",
      saveButtonLabel: "Guardar preferencias de factura",
      successMessage: "Preferencias de factura guardadas. Se aplicarán a futuras facturas.",
      fields: {
        invoice_notes: {
          label: "Notas de factura",
          helper: "Plazos de pago, referencia de factura o notas generales que aparecen en las nuevas facturas.",
          placeholder: "Ingresa las notas estándar que aparecen en las nuevas facturas.",
        },
        payment_instructions: {
          label: "Instrucciones de pago",
          helper: "Explica cómo debe enviar el pago el cliente.",
          placeholder: "Ingresa el nombre de la empresa y los datos de contacto que los clientes deben usar para enviar el pago.",
        },
      },
    },
    customer_update: {
      title: "Preferencias de aviso al cliente",
      purpose: "Texto estándar usado en futuros avisos al cliente aprobados por el contratista. Los avisos existentes no cambian.",
      snapshotNote: "Estos valores se usan solo para futuros avisos al cliente. Los avisos existentes permanecen como copias intactas y aún puedes editar el texto antes de enviar.",
      saveButtonLabel: "Guardar preferencias de aviso al cliente",
      successMessage: "Preferencias de aviso al cliente guardadas. Se aplicarán a futuros avisos.",
      fields: {
        customer_update_signature: {
          label: "Firma / bloque de contacto",
          helper: "La firma de la empresa o el equipo que se coloca al final de un aviso al cliente.",
          placeholder: "Ingresa el nombre del equipo y el teléfono/correo que el cliente debe usar.",
        },
        review_request: {
          label: "Solicitud de reseña predeterminada",
          helper: "Texto opcional que solicita al cliente dejar una reseña.",
          placeholder: "Ingresa el texto que usas para pedir una reseña.",
        },
        referral_request: {
          label: "Solicitud de referidos predeterminada",
          helper: "Texto opcional que solicita al cliente referir a amigos, familiares o vecinos.",
          placeholder: "Ingresa el texto que usas para pedir referidos.",
        },
      },
    },
    closeout: {
      title: "Preferencias de cierre y garantía",
      purpose: "Texto de garantía, mantenimiento, servicio y agradecimiento para futuros paquetes de cierre. Los paquetes existentes no cambian.",
      snapshotNote: "Estos valores se usan solo para futuros paquetes de cierre. Los paquetes existentes permanecen como copias intactas y aún puedes editar el texto antes de enviar.",
      saveButtonLabel: "Guardar preferencias de cierre y garantía",
      successMessage: "Preferencias de cierre y garantía guardadas. Se aplicarán a futuros paquetes de cierre.",
      fields: {
        workmanship_warranty_terms: {
          label: "Términos de garantía de mano de obra",
          helper: "Explica qué cubre tu empresa en mano de obra y por cuánto tiempo.",
          placeholder: "Explica la duración y el alcance de tu garantía de mano de obra.",
        },
        manufacturer_warranty_notes: {
          label: "Notas de garantía del fabricante",
          helper: "Explica cómo se manejan las garantías del fabricante para los productos o materiales instalados.",
          placeholder: "Explica cómo se manejan las garantías del fabricante para los materiales que instalas.",
        },
        maintenance_notes: {
          label: "Notas de mantenimiento",
          helper: "Indica al cliente cómo mantener el trabajo completado y conservar las garantías aplicables.",
          placeholder: "Explica los pasos de cuidado que debe seguir el cliente tras completar el trabajo.",
        },
        warranty_contact_notes: {
          label: "Contacto de garantía / servicio",
          helper: "Explica cómo debe contactar el cliente a tu empresa por una consulta de garantía o servicio.",
          placeholder: "Ingresa el nombre de la empresa y los datos de contacto para consultas de garantía o servicio.",
        },
        closeout_notes_template: {
          label: "Plantilla de notas de cierre",
          helper: "Texto estándar de agradecimiento, finalización o cierre usado en los nuevos paquetes.",
          placeholder: "Ingresa el texto estándar de cierre usado en los nuevos paquetes.",
        },
        review_request: {
          label: "Solicitud de reseña tras finalización",
          helper: "Texto opcional que solicita una reseña después de completar el trabajo.",
          placeholder: "Ingresa el texto que usas para pedir una reseña tras la finalización.",
        },
        referral_request: {
          label: "Solicitud de referidos tras finalización",
          helper: "Texto opcional que solicita referidos después de completar el trabajo.",
          placeholder: "Ingresa el texto que usas para pedir referidos tras la finalización.",
        },
      },
    },
    warranty: {
      title: "Preferencias de garantía",
      purpose: "Términos estándar de garantía de mano de obra, manejo de garantía del fabricante y contacto para servicio de garantía para futuros documentos del proyecto. Los documentos de garantía existentes permanecen como copias intactas.",
      snapshotNote: "Estos valores se usan solo para futuros documentos del proyecto. Los documentos de garantía existentes permanecen como copias intactas y aún puedes editar el texto antes de enviar.",
      saveButtonLabel: "Guardar preferencias de garantía",
      successMessage: "Preferencias de garantía guardadas. Se aplicarán a futuros documentos del proyecto.",
      fields: {
        workmanship_warranty_terms: {
          label: "Términos de garantía de mano de obra",
          helper: "Explica qué cubre tu empresa en mano de obra y por cuánto tiempo.",
          placeholder: "Explica la duración y el alcance de tu garantía de mano de obra.",
        },
        manufacturer_warranty_notes: {
          label: "Notas de garantía del fabricante",
          helper: "Explica cómo se manejan las garantías del fabricante para los productos o materiales instalados.",
          placeholder: "Explica cómo se manejan las garantías del fabricante para los materiales que instalas.",
        },
        warranty_contact_notes: {
          label: "Contacto de garantía / servicio",
          helper: "Explica cómo debe contactar el cliente a tu empresa por una consulta de garantía o servicio.",
          placeholder: "Ingresa el nombre de la empresa y los datos de contacto para consultas de garantía o servicio.",
        },
      },
    },
  },

  // Slice 2S · Continuidad EN/ES — Cubierta de DocumentDefaultsFamilyCard
  family_card: {
    unsaved_pill: "Cambios no guardados",
    storage_warning:
      "La copia de seguridad del borrador no está disponible en este navegador. Mantén esta página abierta hasta guardar.",
    stale_banner:
      "Una versión guardada cambió después de crear este borrador.",
    stale_restore: "Restaurar mi borrador",
    stale_discard: "Descartar borrador antiguo",
    conflict_banner:
      "Los valores guardados cambiaron en otra pestaña o sesión.",
    load_latest: "Cargar los últimos valores guardados",
    overwrite: "Sobrescribir con mi borrador",
    other_tab_banner: "Este borrador cambió en otra pestaña.",
    load_newer: "Cargar borrador más reciente",
    keep_this_tab: "Conservar los cambios de esta pestaña",
    saving: "Guardando…",
    discard_draft: "Descartar borrador",
    discard_dialog_title:
      "¿Descartar los cambios no guardados de esta sección?",
    discard_dialog_body:
      "Se restaurarán los últimos valores guardados solo para esta sección.",
    keep_editing: "Seguir editando",
    discard_changes: "Descartar cambios",
    overwrite_dialog_title: "¿Sobrescribir los valores guardados más recientes?",
    overwrite_dialog_body:
      "Otra persona (u otra pestaña) guardó esta sección después de que empezaste a editar. Sobrescribir reemplazará esos cambios con tu borrador.",
    cancel: "Cancelar",
    keep_tab_dialog_title: "¿Conservar los cambios de esta pestaña?",
    keep_tab_dialog_body:
      "Otra pestaña escribió un borrador diferente para esta sección. Conservar los cambios sobrescribirá ese borrador.",
  },

  // Slice 2S · Continuidad EN/ES — Panel guiado del Paso 3
  guided: {
    use_recommended: "Usar el recomendado",
    edit_wording: "Editar texto",
    help_me_change_it: "Ayúdame a cambiarlo",
    set_later: "Configurar después",
    back: "Atrás",
    continue: "Continuar",
    save: "Guardar",
    cancel: "Cancelar",
    confirm: "Confirmar",
    reset: "Restablecer",
    close: "Cerrar",
    unsaved_changes: "Cambios no guardados",
    discard_draft: "Descartar borrador",
    handle_outside_closeout: "Gestionar el cierre fuera de JobClose AI",
    handle_outside_warranty: "Gestionar las garantías fuera de JobClose AI",
    use_recommended_closeout: "Usar la configuración de cierre recomendada por JobClose AI",
    use_recommended_warranty: "Usar la configuración de garantía recomendada por JobClose AI",
    recommended_wording: "Texto recomendado por JobClose AI",
    recommended_contract_wording: "Texto de contrato recomendado",
    recommended_invoice_wording: "Texto de factura recomendado",
    recommended_customer_update_wording: "Texto de aviso al cliente recomendado",
    recommended_closeout_wording: "Texto de cierre recomendado",
    recommended_warranty_wording: "Texto de garantía recomendado",
    company_incomplete: "El perfil de la empresa está incompleto",
    company_incomplete_body:
      "Agrega el nombre, teléfono y correo de tu empresa en Datos del negocio antes de usar el texto recomendado por JobClose AI.",
    add_company_info: "Agregar datos de la empresa",
    tone: "Tono",
    length: "Extensión",
    length_shorter: "Más corto",
    length_standard: "Estándar",
    length_more_detailed: "Más detallado",
    tone_friendly: "Amistoso",
    tone_professional: "Profesional",
    tone_straightforward: "Directo",
    optional_clauses: "Cláusulas opcionales",
    optional_emphasis: "Énfasis opcional",
    compare_changes: "Comparar cambios",
    preview_updated: "Actualizar la vista previa",
    conflict_newer_saved: "Existe una versión guardada más reciente en el servidor.",
    conflict_choose_reconcile: "Elige cómo resolverlo:",
    conflict_newer_other_tab: "Existen cambios más recientes sin guardar en otra pestaña.",
    conflict_load_latest: "Cargar los últimos valores guardados",
    conflict_keep_editing: "Seguir editando",
    conflict_overwrite: "Sobrescribir con mis cambios",
    add_trade_override: "Agregar excepción por oficio",
    remove: "Quitar",
    trade_overrides_toggle: "Usar excepciones por oficio",
    custom_period: "Período personalizado",
    saving: "Guardando…",
    status: {
      recommended: "Recomendado",
      customized: "Personalizado",
      legacy: "Anterior",
      later: "Configurar después",
      outside: "Gestionado fuera de JobClose AI",
    },
    emphasis: {
      contract: {
        scheduling: "Protección de cronograma más fuerte",
        concealed: "Protección más fuerte para condiciones ocultas",
        customer_responsibility: "Redacción más fuerte de responsabilidad del cliente",
        materials: "Redacción más fuerte de selección de materiales",
        cleanup: "Redacción más fuerte de limpieza",
      },
      invoice: {
        amount_due: "Redacción más clara del monto vencido",
        overdue: "Redacción más fuerte de saldos vencidos",
        instructions: "Instrucciones de pago más detalladas",
        thank_you: "Agradecimiento al cliente más cálido",
      },
      customer_update: {
        clearer_next_steps: "Próximos pasos más claros",
        warmer_delay: "Aviso de retraso más cálido",
        text_message_style: "Estilo más conciso para mensaje de texto",
        warmer_review_referral: "Reseña y referidos más cálidos",
      },
      closeout: {
        clearer_final_next_steps: "Próximos pasos finales más claros",
        stronger_punch_list_clarity: "Claridad más fuerte del punch list",
        warmer_completion: "Mensaje de finalización más cálido",
        clearer_ack: "Reconocimiento del cliente más claro",
      },
      warranty: {
        clearer_workmanship_coverage: "Cobertura de mano de obra más clara",
        stronger_exclusion_clarity: "Claridad más fuerte de exclusiones",
        clearer_manufacturer_distinction: "Distinción más clara de garantía del fabricante",
        clearer_warranty_request_steps: "Pasos más claros para solicitar garantía",
      },
    },
  },

  nav: {
    dashboard: "Panel",
    customers: "Clientes",
    projects: "Proyectos",
    job_calendar: "Calendario de trabajos",
    estimates: "Estimaciones",
    proposals: "Propuestas",
    contracts: "Contratos",
    invoices: "Facturas",
    customer_updates: "Avisos al cliente",
    change_orders: "Órdenes de cambio",
    work_orders: "Órdenes de trabajo",
    selections: "Selecciones",
    closeout: "Cierre",
    company_profile: "Perfil de la empresa",
    document_defaults: "Preferencias de documentos",
    closeout_defaults: "Preferencias de cierre",
    pricing_settings: "Ajustes de precios",
    account: "Cuenta / Suscripción",
    meet_levi: "Conoce a Levi",
    company_and_settings: "Empresa y ajustes",
    daily_primary_work: "Día a día",
    attention: "Atención",
    help_company: "Ayuda · Empresa",
    custom_job_with_levi: "Trabajo a medida con Levi",
    estimates_awaiting: "Estimados por revisar",
    proposals_awaiting: "Propuestas por respuesta",
    contracts_awaiting: "Contratos por firmar",
    invoices_unpaid: "Facturas sin pagar",
    invoices: "Facturas",
    closeout_warranty: "Cierre / Garantía",
    job_workflow: "Flujo del trabajo",
    customer_jobsite: "Cliente y obra",
    setup: "Configuración",
    help: "Ayuda",
    set_up_your_business: "Configura tu negocio",
    continue_setup: "Continuar configuración",
    review_setup: "Revisar configuración",
    sign_out: "Cerrar sesión",
    home: "Inicio",
    customers_short: "Clientes",
    projects_short: "Proyectos",
    more_short: "Más",
    levi_short: "Levi",
    levi_open: "Abrir a Levi",
    mobile_bottom_nav: "Navegación principal",
  },
  common: {
    back: "Atrás",
    continue: "Continuar",
    save: "Guardar",
    cancel: "Cancelar",
    edit: "Editar",
  },

  // Slice 2D4 · Cobertura de idioma para las rutas principales.
  actions: {
    new: "Nuevo",
    create: "Crear",
    save: "Guardar",
    cancel: "Cancelar",
    delete: "Eliminar",
    edit: "Editar",
    view: "Ver",
    close: "Cerrar",
    back: "Atrás",
    send: "Enviar",
    print: "Imprimir",
    download: "Descargar",
    email: "Correo",
    approve: "Aprobar",
    generate: "Generar",
    mark_paid: "Marcar como pagada",
    add: "Agregar",
    open: "Abrir",
  },
  status: {
    loading: "Cargando…",
    saving: "Guardando…",
    saved: "Guardado",
    not_found: "No encontrado",
  },
  pages: {
    dashboard: {
      title: (name) => `Bienvenido de nuevo, ${name}.`,
      welcome_fallback_name: "por aquí",
      subtitle_default: "Tu espacio de trabajo de JobClose AI.",
    },
    customers: {
      title: "Clientes",
      subtitle: "Cada contacto que JobClose AI rastrea para tu negocio — propietarios, administradores de propiedades y cuentas comerciales.",
      new_title: "Nuevo cliente",
      new_subtitle: "Personas o empresas.",
      detail_loading_title: "Cliente",
      detail_loading_subtitle: "Cargando…",
      detail_not_found_subtitle: "No encontrado",
      new_btn: "Nuevo cliente",
      empty_title: "Aún no hay clientes",
      empty_body: "Agrega propietarios, administradores de propiedades o cuentas comerciales para empezar a crear trabajos.",
    },
    projects: {
      title: "Proyectos",
      subtitle: "Sigue cada trabajo desde el estimado hasta el cierre.",
      new_btn: "Nuevo proyecto",
      empty_title: "Aún no hay proyectos",
      empty_body: "Inicia un trabajo para verlo aquí.",
      detail_loading_title: "Proyecto",
      detail_loading_subtitle: "Cargando…",
    },
    estimates: {
      title: "Estimados",
      subtitle: "Borrador, revisión, envío.",
      new_btn: "Nuevo estimado",
      empty_title: "Aún no hay estimados",
      empty_body: "Redacta tu primer estimado desde un proyecto.",
      detail_loading_title: "Estimado",
      detail_loading_subtitle: "Cargando…",
    },
    proposals: {
      title: "Propuestas",
      subtitle: "Propuestas para el cliente con tus términos.",
      detail_loading_title: "Propuesta",
      detail_loading_subtitle: "Cargando…",
      empty_title: "Aún no hay propuestas",
      empty_body: "Genera una propuesta desde un estimado aprobado.",
    },
    contracts: {
      title: "Contratos",
      subtitle: "Borradores listos para contrato. Siempre pide la revisión de tu abogado.",
      detail_loading_title: "Contrato",
      detail_loading_subtitle: "Cargando…",
      empty_title: "Aún no hay contratos",
      empty_body: "Genera un contrato desde una propuesta aceptada.",
    },
    invoices: {
      title: "Facturas",
      subtitle: "Depósito · Progreso · Final · Orden de cambio.",
      new_btn: "Nueva factura",
      new_title: "Nueva factura",
      detail_loading_title: "Factura",
      detail_loading_subtitle: "Cargando…",
      empty_title: "Aún no hay facturas",
      empty_body: "Crea una desde un contrato firmado o directamente aquí.",
    },
    customer_updates: {
      title: "Avisos al cliente",
      subtitle: "Mensajes bilingües — inglés y español.",
      empty_title: "Aún no hay avisos",
      empty_body: "Envía tu primer aviso al cliente desde un proyecto.",
    },
    work_orders: {
      title: "Órdenes de trabajo / Paquetes de cuadrilla",
      subtitle: "Paquetes para la cuadrilla. Alcance, cantidades y tareas — sin precios, costos ni margen.",
      detail_loading_title: "Paquete de cuadrilla",
      detail_loading_subtitle: "Cargando…",
      empty_title: "Aún no hay órdenes de trabajo",
      empty_body: "Genera un paquete de cuadrilla desde un proyecto.",
    },
    closeout: {
      title: "Paquetes de cierre / garantía",
      subtitle: "Entrega a tus clientes un paquete final claro con notas de terminación, expectativas de garantía, guía de mantenimiento y próximos pasos.",
      detail_loading_title: "Paquete de cierre",
      detail_loading_subtitle: "Cargando…",
      empty_title: "Aún no hay paquetes de cierre",
      empty_body: "Genera un paquete de cierre cuando termine un trabajo.",
    },
    company_profile: {
      title: "Perfil de la empresa",
      subtitle: "Identidad y marca del contratista que se usan en tus documentos para el cliente.",
      loading_subtitle: "Configuración · Identidad y marca del contratista",
    },
    pricing: {
      title: "Ajustes de precios",
      subtitle: "Mantenimiento de tus precios guardados. La configuración inicial vive en el Asistente de configuración del contratista.",
      loading_title: "Ajustes de precios",
      loading_subtitle: "Cargando…",
    },
    account: {
      title: "Cuenta y suscripción",
      subtitle: "Del estimado al contrato a la factura — sin perder el control de tus precios.",
    },
    meet_levi: {
      title: "Conoce a Levi",
      subtitle: "Tu compañero de trabajo en JobClose AI.",
      identity_line: "Tu compañero de trabajo en JobClose AI.",
      open_assistant: "Conocer a Levi",
      does_label: "Lo que Levi hace",
      does_not_label: "Lo que Levi NO hace",
      where_label: "Dónde encontrar a Levi",
    },
  },
  invoice_types: {
    deposit: "Depósito",
    progress: "Avance",
    final: "Final",
    change_order: "Orden de cambio",
  },
  invoice_form: {
    project: "Proyecto",
    choose_project: "Elige un proyecto…",
    type: "Tipo",
    amount: "Monto",
    due_date: "Fecha de vencimiento",
    customer_language: "Idioma del cliente",
    notes: "Notas",
    cancel: "Cancelar",
    create: "Crear",
    toast_choose_project: "Selecciona un proyecto",
    toast_created: "Factura creada",
    toast_create_failed: "No se pudo crear",
    columns: {
      customer: "Cliente",
      type: "Tipo",
      amount: "Monto",
      status: "Estado",
      created: "Creada",
    },
  },
  updates_form: {
    generate_heading: "Generar aviso",
    project: "Proyecto",
    choose_project: "Elige un proyecto…",
    type: "Tipo",
    language: "Idioma",
    custom_note: "Nota personalizada (opcional)",
    preview: "Vista previa",
    cancel: "Cancelar",
    generate: "Generar",
    empty_dash: "—",
    new_btn: "Nuevo aviso",
    email_update: "Enviar aviso por correo",
  },
  update_type_labels: {
    estimate_received: "Estimado recibido",
    proposal_sent: "Propuesta enviada",
    contract_ready: "Contrato listo",
    deposit_invoice_sent: "Factura de depósito enviada",
    project_scheduled: "Proyecto programado",
    project_delay: "Retraso del proyecto",
    change_order_needed: "Se necesita orden de cambio",
    final_invoice_sent: "Factura final enviada",
    thank_you_review: "Agradecimiento / solicitud de reseña",
  },
  contracts_list_page: {
    view_proposals_btn: "Ver propuestas",
    columns: {
      customer: "Cliente",
      total: "Total",
      status: "Estado",
      created: "Creado",
    },
    status_labels: {
      draft: "Borrador",
      sent: "Enviado",
      signed: "Firmado",
      voided: "Anulado",
      expired: "Expirado",
    },
  },
  contract_detail: {
    back: "Atrás",
    preview_customer_view: "Vista previa del cliente",
    print: "Imprimir",
    download_pdf: "Descargar PDF",
    copy_customer_link: "Copiar enlace para el cliente",
    email_link_to_customer: "Enviar enlace al cliente",
    create_deposit_invoice: "Crear factura de depósito",
    sign_contract_btn: "Firmar contrato",
    sign_input_placeholder: "Escribe el nombre legal completo para firmar",
    subtitle_prefix: (customerName) => `Contrato · ${customerName}`,
    toast_sign_missing_name: "Ingresa el nombre legal completo del cliente para firmar",
    toast_signed: "Contrato marcado como firmado.",
    toast_sign_failed: "No se pudo firmar",
    toast_deposit_created: "Factura de depósito creada",
    toast_deposit_failed: "No se pudo crear la factura",
    toast_link_copied: "Enlace público copiado — compártelo con tu cliente",
    prompt_copy_link: "Copia este enlace:",
    prompt_email_to: "Enviar correo del contrato a:",
    toast_email_mock: "Correo en cola (envío simulado)",
    toast_email_sent: "Correo enviado",
    toast_email_failed: "No se pudo enviar el correo",
    toast_downloaded: (filename) => `Descargado ${filename}`,
    toast_pdf_failed: "No se pudo generar el PDF",
  },
  contract_doc: {
    your_company_fallback: "Tu empresa",
    kind_label: "Contrato",
    agreement_with: (name) => `Contrato con ${name}`,
    date_label: "Fecha",
    status_label: "Estado",
    section_scope: "Alcance aprobado",
    section_terms: "Términos del contrato",
    section_change_orders: "Órdenes de cambio",
    section_customer_responsibilities: "Responsabilidades del cliente",
    section_exclusions: "Exclusiones",
    contract_price: "Precio del contrato",
    deposit_due_prefix: "Depósito a pagar",
    balance_prefix: "Saldo",
    includes_allowances: "Incluye asignaciones",
    tm_reserve_line: (amount) => `Reserva posible de T&M: ${amount} — no incluida en el total en firme`,
    tm_section_title: "Trabajo adicional posible (Tiempo y materiales)",
    tm_col_item: "Artículo",
    tm_col_qty: "Cant.",
    tm_col_est_total: "Total est.",
    tm_legend: "Estos artículos son solo estimaciones y podrían facturarse si el trabajo es necesario.",
    contractor: "Contratista",
    customer_signature: "Firma del cliente",
    signed_on: (date) => `Firmado el ${date}`,
    legal_disclaimer_heading: "Aviso legal · Legal disclaimer",
    status_labels: {
      draft: "Borrador",
      sent: "Enviado",
      signed: "Firmado",
      voided: "Anulado",
      expired: "Expirado",
    },
  },
  invoice_detail: {
    back: "Atrás",
    print: "Imprimir",
    download_pdf: "Descargar PDF",
    email_link_to_customer: "Enviar enlace al cliente",
    mark_paid: "Marcar como pagada",
    subtitle: (type, status) => `${type} · ${status}`,
    toast_marked_paid: "Factura marcada como pagada",
    toast_mark_paid_failed: "Error",
    prompt_email_to: "Enviar correo de la factura a:",
    toast_email_mock: "Correo en cola (envío simulado)",
    toast_email_sent: "Correo enviado",
    toast_email_failed: "No se pudo enviar el correo",
    toast_downloaded: (filename) => `Descargado ${filename}`,
    toast_pdf_failed: "No se pudo generar el PDF",
  },
  invoice_doc: {
    your_company_fallback: "Tu empresa",
    kind_label: "Factura",
    bill_to: "Facturar a",
    type_label: "Tipo",
    issued_label: "Emitida",
    due_label: "Vence",
    total_due: "Total a pagar",
    pay_online_placeholder: "Pagar en línea (marcador de posición)",
    notes_heading: "Notas",
    invoice_type_labels: {
      deposit: "Depósito",
      progress: "Progreso",
      final: "Final",
      change_order: "Orden de cambio",
    },
    payment_status_labels: {
      pending: "Pendiente",
      sent: "Enviada",
      unpaid: "Sin pagar",
      partial: "Parcial",
      paid: "Pagada",
      overdue: "Vencida",
      void: "Anulada",
      refunded: "Reembolsada",
    },
    empty_dash: "—",
  },
  customer_updates_page: {
    prompt_email_to: "Enviar correo de aviso al cliente a:",
    toast_choose_project: "Elige un proyecto",
    toast_created: "Aviso al cliente generado",
    toast_create_failed: "No se pudo crear",
    toast_email_mock: "Correo en cola (envío simulado)",
    toast_email_sent: "Correo enviado",
    toast_email_failed: "No se pudo enviar el correo",
  },
  customer_preview: {
    title_proposal: "Vista previa del cliente · Propuesta",
    title_contract: "Vista previa del cliente · Contrato",
    copy_btn: "Copiar vista previa como texto",
    copy_title: "Copiar vista previa como texto",
    readonly_notice: "Vista previa de solo lectura · no se envía correo",
    secure_link_note: "Esto es lo que tu cliente ve en el enlace seguro.",
    toast_copied: "Vista previa copiada.",
    prompt_copy: "Copia esta vista previa:",
    sr_dialog_desc: (kind) => `Vista de cliente de solo lectura del ${kind}. Ningún dato se modifica.`,
  },
  public_docs: {
    secure_link: "Enlace seguro para el cliente",
    loading: "Cargando…",
    link_gone_title: "Este enlace ya no está disponible.",
    link_expired_body: "El enlace expiró. Pide al contratista un enlace nuevo.",
    link_missing_body: "No pudimos encontrar a qué apunta este enlace. Verifica la URL con tu contratista.",
    powered_by_line: "Con tecnología de JobClose AI · Hecho por un contratista, para contratistas.",
    privacy_link: "Privacidad",
    terms_link: "Términos",
    print_save_pdf: "Imprimir / Guardar PDF",
    link_expires_prefix: (date) => `El enlace expira el ${date}.`,
    proposal_header_line: "Propuesta de",
    proposal_kind_label: "Propuesta",
    proposal_status_label: "Estado",
    proposal_scope_of_work: "Alcance del trabajo",
    proposal_included: "Incluido",
    proposal_excluded: "Excluido",
    proposal_pricing: "Precios",
    proposal_item: "Artículo",
    proposal_qty: "Cant.",
    proposal_total: "Total",
    proposal_subtotal: "Subtotal",
    proposal_tax: "Impuestos",
    proposal_deposit: "Depósito",
    proposal_payment_schedule: "Calendario de pagos",
    proposal_timeline: "Cronograma",
    proposal_next_steps: "Próximos pasos",
    proposal_next_steps_fallback: "Responde a tu contratista para aprobar esta propuesta.",
    proposal_status_labels: {
      draft: "Borrador",
      sent: "Enviada",
      approved: "Aprobada",
      expired: "Expirada",
      revoked: "Anulada",
    },
    contract_header_line: "Contrato de",
    contract_kind_label: "Contrato",
    contract_agreement_with: (name) => `Contrato con ${name}`,
    contract_from_prefix: "De",
    contract_issued_prefix: "Emitido",
    contract_awaiting_signature: "Esperando firma",
    contract_signed: "Firmado",
    contract_section_scope: "Alcance aprobado",
    contract_section_terms: "Términos del contrato",
    contract_section_change_orders: "Órdenes de cambio",
    contract_section_customer_responsibilities: "Responsabilidades del cliente",
    contract_section_exclusions: "Exclusiones",
    contract_price: "Precio del contrato",
    contract_deposit_due_prefix: "Depósito a pagar",
    contract_balance_prefix: "Saldo",
    contract_signature_heading: "Firma del cliente",
    contract_sign_input_label: "Escribe tu nombre legal completo para firmar",
    contract_sign_input_placeholder: "p. ej. Ana M. García",
    contract_sign_btn: "Firmar contrato",
    contract_sign_submitting: "Enviando…",
    contract_signed_on: (date) => `Firmado el ${date}`,
    contract_err_missing_name: "Escribe tu nombre legal completo.",
    contract_err_sign_failed: "No se pudo enviar la firma.",
    contract_legal_disclaimer_heading: "Aviso legal · Legal disclaimer",
    // Phase 10 · PR-1 — Public change-order review + sign copy (ES)
    co_header_line: "Orden de cambio de",
    co_kind_label: "Orden de cambio",
    co_number_prefix: "Orden de Cambio",
    co_awaiting_signature: "Esperando su firma",
    co_partially_signed: "Esperando la firma final",
    co_fully_executed: "Totalmente firmada",
    co_section_scope_summary: "Qué está cambiando",
    co_financial_delta: "Cambio al precio del contrato",
    co_revised_contract_prefix: "Nuevo total del contrato",
    co_schedule_delta: "Cambio de calendario",
    co_days_added_prefix: "Días añadidos",
    co_new_completion_prefix: "Nueva fecha objetivo de finalización",
    co_signature_heading: "Su firma electrónica",
    co_consent_language: "Al escribir su nombre legal completo a continuación, usted acepta que (1) su nombre escrito constituye su firma electrónica, (2) da su consentimiento para realizar esta transacción electrónicamente, (3) esta firma electrónica tiene el mismo efecto legal que una firma manuscrita, y (4) se le entregará una copia del documento completado para sus archivos.",
    co_sign_input_label: "Escriba su nombre legal completo para firmar esta orden de cambio",
    co_sign_input_placeholder: "p. ej. Ana M. García",
    co_sign_btn: "Firmar orden de cambio",
    co_err_missing_name: "Escribe tu nombre legal completo.",
    co_err_sign_failed: "No se pudo enviar la firma.",
    // Phase 1 shared customer-document standard · Public invoice
    // (`/i/:token`). Solo visualización, sin pago en línea.
    invoice_kind_label: "Factura",
    invoice_bill_to: "Facturar a",
    invoice_type_label: "Tipo",
    invoice_due_label: "Vence",
    invoice_line_items: "Detalle",
    invoice_amount_due: "Monto",
    invoice_balance_due: "Saldo",
    invoice_payment_instructions: "Instrucciones de pago",
    invoice_notes_heading: "Notas",
    invoice_type_labels: {
      deposit: "Depósito",
      progress: "Avance",
      final: "Final",
      change_order: "Orden de cambio",
    },
    invoice_status_labels: {
      pending: "Pendiente",
      sent: "Enviada",
      unpaid: "Sin pagar",
      partial: "Parcial",
      paid: "Pagada",
      overdue: "Vencida",
      void: "Anulada",
      refunded: "Reembolsada",
    },
  },
  customers_form: {
    type_label: "Tipo",
    individual: "Persona / Propietario",
    business: "Empresa / Organización",
    business_name: "Nombre del negocio",
    first_name: "Nombre",
    last_name: "Apellido",
    phone: "Teléfono",
    email: "Correo",
    save_customer: "Guardar cliente",
    saving: "Guardando…",
    cancel: "Cancelar",
    search_placeholder: "Buscar por nombre, empresa, teléfono o correo…",
    loading: "Cargando…",
    empty_note: "Aún no hay clientes. Agrega tu primer cliente para empezar.",
    no_contact_yet: "Aún sin información de contacto",
    showing_recent: (shown, total) => `Mostrando tus ${shown} más recientes · ${total} en total`,
    showing_search: (shown) => `${shown} coincidencia${shown === 1 ? "" : "s"}`,
    hidden_hint: (n) => `${n} más · busca para encontrar a cualquier cliente`,
    project_count_short: (n) => `${n} proyecto${n === 1 ? "" : "s"}`,
  },
  projects_list: {
    empty_title: "Aún no hay proyectos",
    empty_body_short: "Inicia un nuevo estimado para crear tu primer proyecto.",
    start_estimate: "Iniciar estimado",
    drafts_heading: "Estimados en borrador",
    drafts_help: "Los comenzaste pero no los terminaste. Reanuda para agregar el resto de los detalles, o elimínalos si ya no los necesitas.",
    resume: "Reanudar",
    delete: "Eliminar borrador",
    keep_draft: "Conservar borrador",
    deleting: "Eliminando…",
    untitled_customer: "Sin título",
    columns: {
      customer: "Cliente",
      job: "Trabajo",
      started: "Iniciado",
      actions: "Acciones",
      address: "Dirección",
      created: "Creado",
    },
    active_heading: "Proyectos activos",
    active_empty_title: "Aún no hay proyectos activos",
    active_empty_body_drafts_present: "Reanuda un borrador de arriba o inicia un estimado nuevo.",
    active_empty_body_no_drafts: 'Haz clic en "Nuevo estimado" para comenzar uno.',
    open: "Abrir",
    search_placeholder: "Buscar por cliente, trabajo o dirección…",
    helper_body: "Abre un proyecto para ver sus estimados, propuestas, contratos, facturas, avisos al cliente y paquetes de cuadrilla en un solo lugar.",
    toast_draft_deleted: "Borrador eliminado",
    toast_draft_delete_failed: "No se pudo eliminar el borrador",
    delete_dialog_title: (name) => `¿Eliminar el borrador de "${name}"?`,
    delete_dialog_body: "Esto no se puede deshacer. Cualquier nota, foto o archivo adjunto a este borrador se eliminará permanentemente.",
  },
  work_orders: {
    loading_body: "Cargando paquetes de cuadrilla...",
    goto_projects: "Ir a proyectos",
    columns: {
      customer_project: "Cliente / Proyecto",
      linked_estimate: "Estimado vinculado",
      generated: "Generado",
      status: "Estado",
    },
    view_packet: "Ver paquete",
    crew_safe_headline: "Paquete seguro para la cuadrilla.",
    crew_safe_body: "Solo alcance y cantidades. Se excluyen intencionalmente precios, costos, márgenes, proveedores, SKU, la Doble Verificación y los datos consultivos de IA.",
    sections: {
      job_info: "Información del trabajo",
      scope: "Alcance",
      materials: "Materiales necesarios",
      tasks: "Tareas",
      access_notes: "Notas de acceso",
      safety_notes: "Notas de seguridad",
      schedule_notes: "Notas de programación",
      crew_assigned: "Cuadrilla asignada",
    },
    save_changes: "Guardar cambios",
    saving: "Guardando...",
    back_link: "Todos los paquetes de cuadrilla",
  },
  closeout: {
    loading_body: "Cargando paquetes de cierre...",
    back_link: "Todos los paquetes",
  },
  // ----- Slice 2S · claves ES para continuidad de idioma -----
  login: {
    hero_overline: "Hecho por un contratista, para contratistas",
    hero_headline: "Trae tus notas de voz. Mantén tus precios. Envía cotizaciones reales.",
    hero_body: "Tus precios. Apoyo de IA. Aprobación final tuya.",
    copyright: "JobClose AI",
    panel_overline: "Iniciar sesión",
    panel_heading: "Bienvenido de nuevo.",
    email_label: "Correo",
    email_placeholder: "tu@empresa.com",
    password_label: "Contraseña",
    password_placeholder: "Tu contraseña",
    submit: "Iniciar sesión",
    submitting: "Iniciando sesión...",
    error_default: "No se pudo iniciar sesión. Inténtalo de nuevo.",
  },
  signup: {
    hero_overline: "De la nota de voz a la propuesta",
    hero_headline: "Convierte conversaciones de alcance en propuestas aprobadas.",
    hero_body:
      "Tu perfil de precios impulsa cada sugerencia de la IA. Tú apruebas cada línea.",
    copyright: "JobClose AI",
    panel_overline: "Crea tu cuenta",
    panel_heading: "Empieza en dos minutos.",
    already_have: "¿Ya tienes una cuenta?",
    go_login: "Iniciar sesión",
    full_name_placeholder: "Tu nombre completo",
    company_placeholder: "Nombre de la empresa",
    email_placeholder: "Correo de trabajo",
    password_placeholder: "Elige una contraseña (mínimo 6 caracteres)",
    submit: "Crear cuenta",
    submitting: "Creando cuenta...",
    error_default: "No se pudo crear la cuenta. Inténtalo de nuevo.",
  },
  job_detail: {
    not_available: "Este trabajo no está disponible.",
    customer: "Cliente",
    property: "Propiedad",
    type: "Tipo",
    status: "Estado",
    linked_estimate: "Estimado vinculado",
    created: "Creado",
    updated: "Actualizado",
    loading: "Cargando trabajo...",
    back_link: "Todos los trabajos",
  },
  material_price_book: {
    title: "Catálogo de precios de materiales",
    subtitle:
      "Tus costos de materiales guardados — usados por Levi para sugerir precios realistas.",
    contractor_internal_prefix: "Uso interno del contratista.",
    contractor_internal_body:
      "Esta es tu referencia — los clientes nunca ven estos números directamente.",
    filter_trade: "Oficio",
    filter_system: "Tipo de sistema",
    filter_supplier: "Proveedor",
    filter_all_trades: "Todos los oficios",
    filter_all_systems: "Todos los tipos",
    filter_all_suppliers: "Todos los proveedores",
    search_placeholder: "Buscar por nombre, SKU o proveedor...",
    empty_title: "Aún no hay materiales en tu catálogo",
    empty_body:
      "Agrega los materiales que más usas para que Levi cotice con tus números reales, no promedios de internet.",
    add_item: "Agregar material",
    column_material: "Material",
    column_unit: "Unidad",
    column_supplier: "Proveedor",
    column_cost: "Tu costo",
    column_updated: "Actualizado",
    column_actions: "Acciones",
    edit: "Editar",
    remove: "Eliminar",
    save: "Guardar",
    cancel: "Cancelar",
    saving: "Guardando...",
    confirm_remove_title: "¿Eliminar este material?",
    confirm_remove_body:
      "Esto no afectará los estimados existentes que ya lo hayan usado.",
    confirm_remove_action: "Eliminar material",
    toast_saved: "Material guardado.",
    toast_removed: "Material eliminado.",
    toast_save_failed: "No se pudo guardar. Inténtalo de nuevo.",
    filter_brand: "Marca",
    filter_all_brands: "Todas las marcas",
    filter_status: "Estado",
    filter_active_only: "Solo activos",
    filter_archived_only: "Solo archivados",
    filter_all_status: "Todos",
    filter_search: "Buscar",
    search_hint: "producto / marca / SKU",
    loading: "Cargando...",
    empty_body_short: "Tu Catálogo de precios de materiales está vacío",
    empty_hint:
      "Agrega materiales para agilizar los estimados y mantener los costos consistentes.",
    col_product: "Producto",
    col_trade_system: "Oficio / Sistema",
    col_supplier_brand: "Proveedor / Marca",
    col_cost: "Costo",
    col_waste_pct: "Desperdicio %",
    col_unit: "Unidad",
    col_status: "Estado",
    col_updated: "Actualizado",
    row_archived: "Archivado",
    row_active: "Activo",
    row_edit: "Editar",
    row_archive: "Archivar",
    row_restore: "Restaurar",
    modal_add_title: "Agregar material",
    modal_edit_title: "Editar material",
    field_trade_required: "Oficio *",
    field_trade_select: "Selecciona un oficio...",
    field_system_type: "Tipo de sistema",
    field_category: "Categoría",
    field_supplier_vendor: "Proveedor / Vendedor",
    field_brand_manufacturer: "Marca / Fabricante",
    field_product_required: "Producto / Especificación *",
    field_sku_optional: "SKU (opcional)",
    field_sku_placeholder: "SKU del proveedor",
    field_unit_of_measure: "Unidad de medida",
    field_coverage_per_unit: "Cobertura por unidad",
    field_contractor_cost_required: "Costo del contratista *",
    field_default_waste_pct: "Desperdicio % por defecto",
    field_markup_optional: "Margen % (opcional)",
    field_active: "Activo",
    field_notes: "Notas",
    field_notes_placeholder: "Notas internas (nunca se muestran al cliente)",
    modal_save_changes: "Guardar cambios",
    validation_trade_product_required: "Oficio y producto son obligatorios",
    toast_updated: "Material actualizado",
    toast_added: "Material agregado",
    toast_archived: "Archivado",
    toast_archive_failed: "No se pudo archivar",
    toast_reactivated: "Reactivado",
    toast_reactivate_failed: "No se pudo reactivar",
    banner_contractor_only_body:
      "Proveedor, marca, SKU, costo del contratista, % de desperdicio, margen y cobertura permanecen solo de tu lado — ninguno de estos campos aparece en la propuesta, contrato, PDF o enlace público del cliente.",
    placeholder_system_type: "p. ej. Teja asfáltica",
    placeholder_category: "p. ej. Tejas, Fijadores",
    placeholder_supplier: "p. ej. ABC Supply",
    placeholder_brand: "p. ej. GAF",
    placeholder_product: "p. ej. Timberline HDZ",
    placeholder_unit: "p. ej. sq, lf, gal, unidad",
    placeholder_coverage: "p. ej. 33.3 sf por bulto",
    placeholder_markup: "hereda del perfil por defecto",
    active_in_use: "En uso",
    confirm_archive_prompt: (product) =>
      `¿Archivar "${product}"? No se eliminará — puedes reactivarlo más tarde.`,
  },
  closeout_defaults_page: {
    title: "Preferencias de cierre",
    subtitle:
      "Garantía, lista de pendientes y solicitudes de reseña aplicadas por defecto a los nuevos paquetes de cierre. Puedes editar cada paquete individualmente.",
    save: "Guardar preferencias",
    saving: "Guardando...",
    toast_saved: "Preferencias de cierre guardadas.",
    toast_failed: "No se pudo guardar. Inténtalo de nuevo.",
  },
  document_defaults_page: {
    title: "Preferencias de documentos",
    subtitle:
      "Términos y descargos usados por defecto al generar propuestas, contratos, facturas y órdenes de cambio. Puedes editar cada documento individualmente.",
    save: "Guardar preferencias",
    saving: "Guardando...",
    toast_saved: "Preferencias de documentos guardadas.",
    toast_failed: "No se pudo guardar. Inténtalo de nuevo.",
  },
  onboarding: {
    save_exit: "Guardar y salir",
    back: "Atrás",
    next: "Siguiente",
    continue: "Continuar",
    finish: "Finalizar",
    skip: "Omitir",
    skip_for_now: "Omitir por ahora",
    set_up_later_title: "¿Configurar esta sección más tarde?",
    set_up_later_body:
      "Puedes volver a terminarla desde Configuración en cualquier momento. Levi seguirá marcando lo que falta.",
    set_up_later_confirm: "Configurar después",
    set_up_later_cancel: "Continuar",
    saving: "Guardando...",
    saved: "Guardado.",
    save_failed: "No se pudo guardar. Inténtalo de nuevo.",
    required_field: "Este campo es obligatorio.",
    loading: "Cargando tu configuración...",
    step_label: (n, total, name) => `Paso ${n} de ${total} · ${name}`,
    step_welcome: "Bienvenida",
    step_business_trades: "Negocio y oficios",
    step_company_profile: "Perfil de empresa",
    step_trade_setup: "Configuración de oficios",
    step_pricing_setup: "Configuración de precios",
    step_documents: "Documentos y preferencias del cliente",
    welcome_headline: "Bienvenido a JobClose AI.",
    welcome_body:
      "En los próximos minutos configuraremos tu negocio, tus oficios y tus precios para que Levi te ayude a cotizar trabajos reales con tus números reales.",
    business_trades_headline: "Cuéntanos sobre tu negocio.",
    business_trades_body:
      "Esto aparece en las propuestas, contratos y facturas del cliente. Puedes ajustar el resto (dirección, licencia, marca) en cualquier momento desde Perfil de empresa.",
    trade_pick_headline: "¿Qué oficios maneja tu cuadrilla?",
    trade_pick_body:
      "Elige cada oficio que maneja tu cuadrilla — JobClose AI usa esto para aplicar las reglas de precios correctas y las sugerencias de Levi.",
    pricing_setup_headline: "Estilo de precios por defecto",
    pricing_setup_body:
      "Estos son los números que JobClose AI usa cuando una línea no los sobrescribe. Puedes ajustarlos en cualquier momento desde Configuración de precios.",
    documents_headline: "Documentos y preferencias del cliente",
    documents_body:
      "Cómo se ven y hablan tus propuestas, contratos y facturas frente a los clientes.",
    documents_complete: "Documentos y preferencias del cliente completos",
    quickstart_step1_overline: "Datos de la empresa",
    quickstart_step1_headline: "Cuéntanos sobre tu negocio.",
    quickstart_step2_overline: "Precios básicos",
    quickstart_step2_headline: "¿Cómo prefieres cotizar?",
    quickstart_company_logo_optional: "Logo de la empresa (opcional)",
  },
  account: {
    current_plan: "Plan actual",
    status: "Estado",
    cycle: "Ciclo",
    monthly: "Mensual",
    annual: "Anual",
    save_two_months: "ahorra 2 meses",
    available_plans: "Planes disponibles",
    additional_users: "Usuarios internos adicionales: $20/mes cada uno.",
    setup_packages: "Paquetes de configuración e incorporación",
    setup_packages_body: "Los paquetes de configuración se revisan antes de la incorporación para que tu perfil de precios, tu equipo y tu flujo de trabajo queden bien configurados.",
    no_surprise_fees: "La configuración siempre se revisa antes de la incorporación. Sin cargos sorpresa.",
    contact_sales: "Contactar a ventas",
    subscribe: "Suscribirse",
    current_plan_action: "Plan actual",
    redirecting: "Redireccionando...",
    customer_doc_lang_heading: "Idioma de documentos del cliente",
    customer_doc_lang_body: "Idioma predeterminado para generar documentos para el cliente (propuestas, contratos, facturas, avisos). Aún puedes cambiar el idioma en cada documento individual. Es independiente del idioma de la aplicación en la barra superior.",
    // Slice 2R · superficie de suscripción de Stripe
    manage_billing: "Administrar facturación",
    renews_on: "Próxima renovación",
    cancels_on: "Se cancela el",
    set_to_cancel: "Se cancelará al final del periodo",
    launch_offer_active: "Oferta de lanzamiento",
    launch_offer_badge: "Oferta de lanzamiento",
    launch_offer_headline: "Precio de lanzamiento de JobClose AI",
    launch_offer_body:
      "$129/mes · 2 usuarios completos incluidos · +$20/mes por cada usuario adicional.",
    launch_offer_price_note: "$129/mes · 2 usuarios incluidos",
    setup_fee_separate: "La incorporación es una compra única y aparte.",
    // Slice 2S · claves ES adicionales para la página de Cuenta.
    most_popular_badge: "MÁS POPULAR",
    setup_pkgs_heading: "Paquetes de incorporación y configuración",
    setup_pkgs_body:
      "Cada paquete se revisa con tu equipo antes de la incorporación para configurar correctamente tu perfil de precios, tus oficios y tu flujo de trabajo.",
    no_surprise_fees_chip: "Sin cargos sorpresa",
    setup_pkg_basic_name: "Configuración básica",
    setup_pkg_basic_body:
      "Ideal para contratistas solitarios — perfil de empresa, un oficio y precios por defecto.",
    setup_pkg_pro_name: "Configuración Pro",
    setup_pkg_pro_body:
      "Nivel Team Contractor — perfil de precios completo, configuración multi-oficio y preferencias de documentos.",
    setup_pkg_premium_name: "Configuración Premium",
    setup_pkg_premium_body:
      "Nivel Growth — perfil de precios completo, configuración multi-cuadrilla y catálogo de precios inicial.",
    setup_pkg_custom_name: "Configuración a medida",
    setup_pkg_custom_body:
      "Equipos más grandes — cotizado por adelantado con tu líder de operaciones.",
    starting_at_setup: "desde $499 de configuración",
    quoted_upfront: "cotizado por adelantado",
    website_starter_heading: "Complemento opcional · Sitio web inicial",
    website_starter_body:
      "Sitio web sencillo con la marca del contratista con tus servicios, áreas de servicio y formulario de contacto de JobClose AI. Las ediciones avanzadas y la redacción a medida se cotizan por separado.",
    status_labels: {
      trial: "Prueba",
      active_test_mode: "Activo (modo prueba)",
      active_placeholder: "Activo",
      active: "Activo",
      trialing: "En prueba",
      past_due: "Pago vencido",
      canceled: "Cancelado",
      incomplete: "Incompleto",
      incomplete_expired: "Expirado",
      unpaid: "Sin pagar",
      paused: "Pausado",
    },
  },
  dashboard: {
    brand_promise_overline: "Promesa de marca",
    brand_promise_headline: "De la nota de voz a la propuesta. Impulsado por IA. Controlado por ti.",
    brand_bullets: [
      "· Tu perfil de precios guía cada sugerencia de IA.",
      "· Salvaguardas de estimado en verde, amarillo y rojo.",
      "· Avisos bilingües al cliente (EN / ES).",
      "· Borradores listos para contrato — nunca asesoría legal.",
    ],
    recent_projects: "Proyectos recientes",
    active_projects_h2: "Proyectos activos",
    view_all: "Ver todo",
    kpi_active_projects: "Proyectos activos",
    kpi_draft_estimates: "Estimados en borrador",
    kpi_sent_proposals: "Propuestas enviadas",
    kpi_open_invoices: "Facturas abiertas",
    kpi_contracts: "Contratos",
    kpi_customer_updates: "Avisos al cliente",
    paid_revenue: "Ingresos pagados",
    paid_revenue_hint: "En todas las facturas pagadas",
    empty_body: "Comienza tu primer proyecto desde la visita al sitio — notas de voz, fotos, mediciones — y deja que la IA organice tu alcance.",
    start_new_estimate: "Iniciar un nuevo estimado",
    new_estimate: "Nuevo estimado",
    pricing_profile_ready: "Perfil de precios listo",
    verified_on: "Verificado el",
    review_pricing_profile: "Revisar perfil de precios",
    lifecycle_swipe_hint: "Desliza",
    custom_job_shortcut_overline: "Acceso rápido",
    custom_job_shortcut_title: "Trabajo a medida con Levi",
    custom_job_shortcut_body: "Activa Levi Vision para trabajos únicos o fuera de tus precios",
    custom_job_eyebrow: "Trabajo a medida con Levi",
    custom_job_title: "¿Tienes un trabajo fuera de tu alcance habitual?",
    custom_job_body: "Levi te ayuda a organizar el alcance y te recomienda un rango de precio. Tú lo revisas, lo ajustas y decides el precio final.",
    custom_job_cta: "Iniciar un trabajo a medida",
    custom_job_alt: "Un contratista mostrándole a Levi un árbol caído — el tipo de trabajo inusual, fuera de su alcance habitual, que Levi ayuda a organizar.",
    attention_overline: "Hoy · Lo que necesita tu atención",
    attention_headline: "Lo que necesita tu atención",
    attention_composed_hint: "Compuesto desde tus datos existentes",
    attention_pill_draft: "Borrador",
    attention_pill_awaiting_response: "Esperando respuesta",
    attention_pill_awaiting_signature: "Esperando firma",
    attention_pill_open_invoice: "Factura abierta",
    attention_estimate_title: (name) => `${name} · estimado en borrador`,
    attention_estimate_title_generic: "Estimado en borrador listo para revisión",
    attention_estimate_hint: "Listo para tu revisión",
    attention_proposal_title: (name) => `${name} · propuesta`,
    attention_proposal_title_generic: "Propuesta esperando respuesta del cliente",
    attention_proposal_hint: "Enviada — esperando respuesta del cliente",
    attention_contract_title: (name) => `${name} · contrato`,
    attention_contract_title_generic: "Contrato esperando firma",
    attention_contract_hint: "Esperando firma",
    attention_invoice_title: (name) => `${name} · factura`,
    attention_invoice_title_generic: "Factura abierta",
    attention_invoice_hint: "Sin pagar",
    workflow_overline: "Flujo completo",
    workflow_headline: "Estimado → Propuesta → Contrato → Factura → Cierre",
    workflow_body: "JobClose AI es todo el flujo del trabajo — no solo los estimados.",
    project_lifecycle_overline: "Ciclo de vida del proyecto",
    lifecycle_count_items: (n) => `${n} en este proyecto`,
    lifecycle_choose_one: "Elige uno",
    lifecycle_cancel: "Cancelar",
    status_live: "En vivo",
    status_coming_next: "Próximamente",
    step_estimate: "Estimado",
    step_proposal: "Propuesta",
    step_contract: "Contrato",
    step_customer_updates: "Avisos al cliente",
    step_change_orders: "Órdenes de cambio",
    step_work_orders: "Órdenes de trabajo",
    step_invoice: "Factura",
    step_closeout: "Cierre / Garantía",
    levi_card_title: "Conoce a Levi, tu compañero en JobClose AI",
    levi_card_teaser: "Tu compañero de trabajo en JobClose AI. Te ayuda con la toma del trabajo, estimados y seguimiento — siempre a través de flujos que has aprobado.",
    levi_card_learn_more: "Saber más",
  },
  company_form: {
    loading_body: "Cargando perfil de la empresa…",
    section_identity: "Identidad de la empresa",
    section_address: "Dirección del negocio",
    section_license: "Licencia y área de servicio",
    section_trades: "Categorías / servicios",
    section_branding: "Marca",
    company_name: "Nombre de la empresa",
    phone: "Teléfono",
    email: "Correo",
    website: "Sitio web",
    timezone: "Zona horaria",
    tz_auto: "— Detectar automáticamente al guardar —",
    street: "Calle",
    city: "Ciudad",
    state: "Estado",
    zip: "Código postal",
    license_number: "Número de licencia",
    license_expiration: "Vencimiento de la licencia",
    license_type: "Tipo de licencia",
    service_area: "Área de servicio",
    trade_categories: "Categorías / servicios",
    add_custom_trade: "Agregar servicio",
    add: "Agregar",
    logo: "Logotipo del contratista",
    upload_logo: "Subir logotipo",
    replace_logo: "Reemplazar logotipo",
    remove_logo: "Quitar logotipo",
    primary_color: "Color primario",
    accent_color: "Color de acento",
    save: "Guardar perfil de la empresa",
    saving: "Guardando…",
    cancel: "Cancelar",
    toast_saved: "Perfil de la empresa guardado",
    toast_load_failed: "No se pudo cargar el perfil de la empresa",
    toast_logo_uploaded: "Logotipo subido",
    toast_logo_removed: "Logotipo quitado",
    toast_logo_failed: "Falló la subida del logotipo",
    toast_remove_failed: "Falló al quitar",
    toast_trade_long: "Etiqueta de servicio demasiado larga",
    toast_primary_hex: "El color primario debe ser un hex como #0f2540",
    toast_accent_hex: "El color de acento debe ser un hex como #ffb020",
    placeholder_company: "Nombre de tu empresa",
    placeholder_phone: "(555) 555-5555",
    placeholder_email: "oficina@empresa.com",
    placeholder_website: "https://www.empresa.com",
    placeholder_license: "p. ej. Contratista general",
    placeholder_service_area: "San Antonio y 50 mi a la redonda",
    placeholder_new_trade: "Agrega un servicio personalizado (p. ej., canales, ventanas)",
  },
  pricing_form: {
    save_all: "Guardar ajustes de precios",
    saving: "Guardando…",
    section_summary: "Resumen de precios",
    section_labor: "Tarifas de mano de obra",
    section_materials: "Catálogo de materiales",
    section_rules: "Reglas de precio aprobadas",
    add_rule: "Agregar regla de precio",
    rule_name: "Nombre de la regla",
    trade: "Servicio",
    system: "Sistema",
    category: "Categoría",
    pricing_method: "Método de precio",
    unit: "Unidad",
    installed_selling_price: "Precio de venta instalado completo",
    labor: "Mano de obra",
    materials: "Materiales",
    markup: "Margen",
    subcontractor: "Cotización de subcontratista",
    flat_price: "Precio fijo",
    tm: "Tiempo y materiales",
    edit: "Editar",
    delete: "Eliminar",
    save: "Guardar",
    cancel: "Cancelar",
    empty_title: "Aún no hay reglas de precio aprobadas",
    empty_body: "Agrega reglas de precio para estimar más rápido.",
    toast_saved: "Ajustes de precios guardados",
    toast_load_failed: "No se pudieron cargar los ajustes de precios",
    zero_price_warn: "Regla con precio cero — confirma antes de guardar.",
    unit_mismatch_warn: "Unidades no coinciden — verifica la conversión.",
  },
  work_orders_form: {
    loading_body: "Cargando paquetes de cuadrilla...",
    empty_title: "Aún no hay paquetes de cuadrilla",
    empty_body: "Genera un paquete de cuadrilla desde un proyecto.",
    goto_projects: "Ir a proyectos",
    columns: {
      customer_project: "Cliente / Proyecto",
      linked_estimate: "Estimado vinculado",
      generated: "Generado",
      status: "Estado",
    },
    status: {
      draft: "Borrador",
      ready: "Listo",
      sent: "Enviado",
      completed: "Completado",
    },
    view_packet: "Ver paquete",
    crew_safe_headline: "Paquete seguro para la cuadrilla.",
    crew_safe_body: "Solo alcance y cantidades. Se excluyen intencionalmente precios, costos, márgenes, proveedores, SKU, la Doble Verificación y los datos consultivos de IA.",
    sections: {
      job_info: "Información del trabajo",
      scope: "Alcance",
      materials: "Materiales necesarios",
      tasks: "Tareas",
      access_notes: "Notas de acceso",
      safety_notes: "Notas de seguridad",
      schedule_notes: "Notas de programación",
      crew_assigned: "Cuadrilla asignada",
    },
    save_changes: "Guardar cambios",
    saving: "Guardando...",
    cancel: "Cancelar",
    edit: "Editar",
    back: "Todos los paquetes de cuadrilla",
    toast_saved: "Paquete de cuadrilla guardado",
    toast_load_failed: "No se pudo cargar el paquete de cuadrilla",
  },
  closeout_form: {
    loading_body: "Cargando paquetes de cierre...",
    empty_title: "Aún no hay paquetes de cierre",
    empty_body: "Genera un paquete de cierre cuando termine un trabajo.",
    columns: {
      customer_project: "Cliente / Proyecto",
      generated: "Generado",
      status: "Estado",
    },
    status: {
      draft: "Borrador",
      ready: "Listo",
      sent: "Enviado",
      completed: "Completado",
    },
    sections: {
      notes: "Notas",
      warranty_overview: "Resumen de garantía",
      maintenance: "Mantenimiento",
      next_steps: "Próximos pasos",
    },
    view_packet: "Ver paquete",
    save: "Guardar",
    cancel: "Cancelar",
    edit: "Editar",
    back: "Todos los paquetes",
    toast_saved: "Paquete de cierre guardado",
    toast_load_failed: "No se pudo cargar el paquete de cierre",
    manufacturer_warranty: "Notas de garantía del fabricante",
    review_request: "Solicitud de reseña",
    referral_request: "Solicitud de referencia",
    service_contact: "Contacto de garantía / servicio",
    field_terms: "Términos",
    field_care: "Cuidado y mantenimiento",
    field_message: "Mensaje",
    field_contact: "Instrucciones de contacto",
    final_punch: "Notas finales de la lista pendiente",
  },
  customer_notifications: {
    section_overline: "Notificaciones al cliente",
    title: "Notificar automáticamente a los clientes sobre actualizaciones importantes del proyecto",
    helper: "Cuando está activado, Levi — su guía de proyecto — enviará un correo electrónico al cliente en su nombre cuando el proyecto llegue a una nueva etapa importante. La entrega es manejada por JobClose AI, y las respuestas se dirigen a los datos de contacto guardados de su empresa.",
    choice_yes: "Sí",
    choice_no: "No",
    toast_auto_sent: "Levi envió una notificación al cliente sobre la nueva etapa del proyecto en su nombre.",
    toast_auto_skipped_missing_email: "No se envió ninguna notificación porque este proyecto no tiene una dirección de correo electrónico válida del cliente.",
    toast_auto_failed: "Se guardó el estado pero la notificación de Levi al cliente no se pudo entregar. Queda registrada para que la reintentes.",
  },
  company_extra: {
    dba: "DBA / Nombre comercial",
    line1: "Línea 1",
    line2: "Línea 2",
    license_state: "Estado de la licencia",
    no_logo: "Sin logotipo cargado",
    branding_description: "Tu logotipo y colores de marca aparecen en propuestas, contratos, facturas y paquetes de cierre para el cliente. El logotipo de JobClose AI permanece en el chrome de la aplicación.",
    how_this_is_used: "Cómo se usa",
    how_bullet_1: "· El perfil de la empresa solo guarda tu información. Nada más en la aplicación cambia por ahora.",
    how_bullet_2: "· El logotipo y los colores aparecerán en los PDF del cliente en una fase posterior.",
    how_bullet_3: "· Los costos, el margen, los detalles del perfil de precios y las notas internas permanecen privadas.",
    logo_hint: "PNG · JPG · WEBP · SVG · hasta",
    mb_suffix: "MB",
  },
  pricing_extra: {
    maintenance_headline: "Ajustes de precios · Modo mantenimiento.",
    maintenance_body: "Edita aquí las tarifas y reglas existentes. Para agregar un sistema de techado con plantilla, usa el",
    setup_wizard: "Asistente de configuración del contratista",
    company_name: "Nombre de la empresa",
    trade_primary: "Servicio / Servicio principal",
    trade_primary_placeholder: "p. ej. Contratista general, HVAC, techado",
    approved_overline: "Precios de servicios aprobados",
    approved_heading: "Tus reglas de precios guardadas",
    approved_body: "Las reglas de precio instalado reutilizables que aprobaste en la Configuración de servicios. Las ediciones aquí actualizan la misma regla — no se crean duplicados y no se reescriben los estimados históricos.",
    open_trade_setup: "Abrir Configuración de servicios",
    approved_empty_title: "Aún no hay precios de servicios aprobados",
    approved_empty_body_prefix: "Aprueba un sistema en",
    approved_empty_body_link: "Configuración de servicios",
    approved_empty_body_suffix: "y aparecerá aquí para editarlo.",
    row_pricing: "Precio",
    row_rate: "Tarifa",
    row_unit: "Unidad",
    row_status: "Estado",
    row_installed: "Precio instalado completo",
    row_installed_headline: "Precio instalado completo · precio final para el cliente",
    row_installed_body: "Incluye materiales · Incluye mano de obra · Incluye gastos generales · Incluye ganancia · Sin margen adicional.",
    row_installed_note: "Esta tarifa aprobada se usará en futuros estimados que coincidan. Los estimados existentes permanecen como capturas sin cambios. Guardada en tus Ajustes de precios aprobados — JobClose AI no agregará otro margen.",
    active: "Activo",
    inactive: "Inactivo",
    reusable: "Reutilizable",
    one_off: "Único",
    edit: "Editar",
    save_changes: "Guardar cambios",
    saving: "Guardando…",
    cancel: "Cancelar",
    style_overline: "Estilo de precio predeterminado",
    style_heading: "¿Cómo cotizas los trabajos normalmente?",
    style_body: "Elige la forma en que normalmente cotizas el trabajo. Aún puedes cotizar cada línea del estimado por precio unitario, precio fijo o mano de obra + materiales — esto solo define tu valor predeterminado.",
    style_unit: "Precio por unidad / producción",
    style_flat: "Precio fijo / suma alzada",
    style_lm: "Mano de obra + materiales",
    style_hybrid: "Híbrido / según el trabajo",
    job_defaults: "Valores predeterminados del trabajo",
    terms: "Términos",
    approved_rule_updated: "Regla aprobada actualizada",
    save_failed: "Falló al guardar",
    loading: "Cargando…",
  },
  wo_extra: {
    untitled_customer: "Cliente sin nombre",
    project_default: "Proyecto",
    no_address: "Sin dirección",
    no_scope: "No hay elementos de alcance en este paquete.",
    no_materials: "No hay lista de materiales adjunta.",
    add_task: "Agregar tarea",
    task_placeholder: "Describe una tarea...",
    access_placeholder: "Códigos de puerta, estacionamiento, mascotas, preferencias del propietario...",
    safety_placeholder: "EPP, riesgos, protección contra caídas...",
    schedule_placeholder: "Fecha de inicio, duración prevista, restricciones del propietario...",
    crew_placeholder: "Líder, ayudantes, subcontratistas...",
    item: "Elemento",
    qty: "Cant.",
    unit: "Unidad",
    customer: "Cliente",
    project: "Proyecto",
    address: "Dirección",
    empty_body_full: "Genera un paquete de cuadrilla desde cualquier proyecto que tenga un estimado. El paquete le da a tu cuadrilla el alcance, cantidades, tareas y notas — sin exponer tus precios.",
  },
  closeout_extra: {
    untitled_customer: "Cliente sin nombre",
    project_default: "Proyecto",
    job_summary: "Resumen del trabajo",
    work_completed: "Trabajo realizado",
    final_invoice_status: "Estado de la factura final",
    address: "Dirección",
    invoice: "Factura",
    completed: "Completado",
    created: "Creado",
    open_packet: "Abrir paquete",
    completion_placeholder: "Resumen de lo que se completó, condición del sitio al entregar...",
    punch_placeholder: "Elementos pendientes o notas del recorrido final...",
    warranty_placeholder: "Duración, alcance, exclusiones...",
    manufacturer_placeholder: "Garantías de producto/material, información de registro...",
    maintenance_placeholder: "Instrucciones de cuidado, responsabilidades del propietario...",
    review_placeholder: "Agradece al cliente y pide una reseña...",
    referral_placeholder: "Pregunta si conocen a alguien que necesite un trabajo similar...",
    contact_placeholder: "Cómo debe contactarte el cliente para garantía o servicio...",
  },
  // Slice 2D4 · Batch 1 — Estimados + Propuestas (UI del contratista).
  estimates_form: {
    list_empty_title: "Aún no hay estimados",
    list_empty_body: "Haz clic en \u201cNuevo estimado\u201d para empezar.",
    col_customer: "Cliente",
    col_lines: "Renglones",
    col_total: "Total",
    col_status: "Estado",
    col_created: "Creado",

    // Slice 2J · Simplificación y reutilización del estimado
    row_open_aria: (name) => `Abrir estimado de ${name}`,
    row_menu_aria: "Acciones del estimado",
    row_duplicate: "Duplicar",
    row_duplicating: "Duplicando\u2026",
    row_delete_draft: "Eliminar borrador",
    toast_duplicated: "Estimado duplicado. Abriendo el nuevo borrador\u2026",
    toast_duplicate_failed: "No pudimos duplicar ese estimado. Intenta de nuevo.",
    toast_deleted: "Borrador eliminado.",
    toast_delete_failed: "No pudimos eliminar ese borrador.",
    delete_dialog_title: (name) => `¿Eliminar este borrador de estimado para ${name}?`,
    delete_dialog_body: "Esto elimina el borrador. No toca al cliente, al trabajo ni a tus precios guardados. No se puede deshacer.",
    delete_dialog_cancel: "Conservar borrador",
    delete_dialog_confirm: "Eliminar borrador",
    delete_dialog_deleting: "Eliminando\u2026",
    reuse_cue_label: "Usa los detalles de este trabajo para no volver a escribirlos.",
    reuse_cue_action: "Rellenar desde la visita",
    reuse_cue_dismiss: "Ahora no",
    reuse_cue_dismiss_aria: "Descartar la sugerencia de reutilizar del trabajo",

    // Slice 2J amendment · Editar estimado + acuse centrado de Levi.
    edit_estimate_btn: "Editar estimado",
    edit_ack_line_1: "La informaci\u00f3n del trabajo se ha actualizado.",
    edit_ack_line_2: "Revisar\u00e9 los cambios y te ayudar\u00e9 con el siguiente paso recomendado.",

    // Slice 2J correction · Simplificación de la creación del estimado.
    wizard_step3_helper: "Habla el trabajo o escríbelo. La grabación y las notas alimentan el mismo estimado \u2014 usa lo que sea más fácil.",
    wizard_job_notes_label: "Notas y mediciones del trabajo",
    wizard_job_notes_placeholder: "Agrega mediciones, pies cuadrados, dimensiones, detalles del alcance, materiales, acceso, solicitudes del cliente, condiciones especiales o cualquier cosa importante.",
    wizard_job_notes_helper: "Un solo campo para mediciones y notas escritas \u2014 no tienes que decidir dónde va cada detalle.",
    wizard_voice_walkthrough_label: "Recorrido con voz",
    wizard_voice_walkthrough_help: "Habla mientras caminas el trabajo. Tus palabras se capturan para que Levi actualice y mejore tu estimado.",
    wizard_voice_or_typed_helper: "La grabación de voz y las notas del trabajo se guardan juntas con este estimado \u2014 usa lo que sea más fácil, o ambos.",

    detail_title_prefix: "Estimado",
    subtitle_back_title: "Volver al trabajo — ver notas de voz, descripción y detalles de la visita.",
    back_to_job: "Volver al trabajo",
    back_to_job_title: "Regresa al trabajo / visita al sitio — notas de voz, descripción y fotos.",
    back_short: "Atrás",
    customer_output_label: "Salida al cliente",
    lang_english: "Inglés",
    lang_spanish: "Español",
    save: "Guardar",
    saving: "Guardando...",
    save_changes: "Guardar cambios",
    save_estimate: "Guardar estimado",
    generate_proposal: "Generar propuesta",
    regenerate_proposal: "Regenerar propuesta",
    unsaved_changes: "Cambios sin guardar",
    all_saved: "Todos los cambios están guardados",
    last_updated: (ts) => `Actualizado ${ts}`,

    drift_headline: "Este estimado se actualizó después de enviar una propuesta.",
    drift_body: "Regenera la propuesta para que el cliente vea los números más recientes. Tu enlace de propuesta actual seguirá mostrando los números anteriores hasta que envíes una nueva.",

    locked_headline: "Este estimado está bloqueado",
    locked_body_default: "Este estimado está vinculado a una propuesta que ya fue enviada. Crea una nueva versión del estimado para hacer cambios sin alterar el documento original del cliente.",
    create_new_version: "Crear nueva versión del estimado",
    creating_new_version: "Creando\u2026",

    account_pricing_headline: "La configuración de precios de la cuenta está incompleta",
    account_pricing_body: "Aún puedes fijar precios línea por línea en este estimado. Para que JobClose AI ponga precios automáticamente a futuros alcances, termina la configuración de precios de tu oficio.",
    complete_pricing_setup: "Completar configuración de precios",

    multi_headline: "Encontramos más de un sistema de techado aprobado. ¿Cuál aplica a este trabajo?",
    multi_installed_copy: "La tarifa aprobada ya incluye materiales, mano de obra, gastos generales y ganancia. JobClose AI no agregará otro margen. La tarifa proviene de tu configuración de precios aprobada.",
    multi_installed_prefix: "Precio instalado completo \u00b7 precio final para el cliente.",
    multi_use_system: "Usar este sistema",
    multi_dismiss: "Descartar \u2014 lo elegiré manualmente en el Constructor Simple de Techado.",
    multi_price_per: (price, unit) => `$${price} por ${unit}`,

    unpriced_headline: "Este estimado aún tiene renglones sin precio",
    unpriced_add_prices: "Agrega los precios abajo y luego Genera la propuesta.",
    unpriced_lines: (n) => `${n} renglón${n === 1 ? "" : "es"} necesita${n === 1 ? "" : "n"} precio de material o mano de obra. `,
    unpriced_need_material: (n) => `${n} requiere${n === 1 ? "" : "n"} costo de material. `,

    needs_qty_headline: (n) => `Ingresa la cantidad para ${n} renglón${n === 1 ? "" : "es"} con precio aprobado.`,
    needs_qty_body: "El precio ya está aprobado \u2014 ingresa la cantidad del trabajo para calcular el total del renglón.",

    ai_organize_title: "Organizar alcance con IA",
    ai_organize_body_idle: "Convierte notas de voz y la visita al sitio en un alcance estructurado y renglones sugeridos.",
    ai_organize_body_running: "Procesando...",
    ai_check_title: "Ejecutar revisión IA del estimado",
    ai_check_body_idle: "Revisión Verde / Amarilla / Roja para detectar mano de obra, materiales, gastos y riesgos que falten.",
    ai_check_body_running: "Revisando...",

    // Slice 2I · Experiencia unificada de Levi en el estimado
    levi_coworker_overline: "Levi \u00b7 Revisi\u00f3n del estimado",
    levi_coworker_title: "Levi est\u00e1 listo para revisar este estimado",
    // Slice 2J correction · Unified Levi estimate-check copy.
    levi_estimate_check_cta: "Que Levi revise este estimado",
    levi_estimate_check_running: "Levi est\u00e1 revisando\u2026",
    levi_estimate_check_help: "Levi buscar\u00e1 vac\u00edos de alcance, precios, materiales, mano de obra, limpieza, deducibles y otros detalles que puedan necesitar tu atenci\u00f3n.",
    levi_recommends: "Levi recomienda",
    levi_you_decide: "Tú decides cuándo actuar.",
    levi_state_ready_to_organize: "Listo para organizar la visita.",
    levi_state_scope_organized_nothing_new: "Alcance organizado. Tu precio aprobado ya lo cubre \u2014 no hacen falta renglones nuevos.",
    levi_state_approved_pricing_matched: (name) => `Precio aprobado encontrado: ${name}.`,
    levi_state_needs_pricing: (n) => `${n} renglón${n === 1 ? "" : "es"} aún necesita${n === 1 ? "" : "n"} precio.`,
    levi_state_ready_for_double_check: "Levi está listo para revisar este estimado.",
    levi_state_double_check_findings: (r, y) => (r > 0 || y > 0)
      ? `La doble revisión encontró ${r} en rojo \u00b7 ${y} en amarillo. Revisa antes de enviar.`
      : "Doble revisión sin observaciones \u2014 nada que revisar.",
    levi_state_organizer_failed: "El último paso de IA no se completó. Tu trabajo está a salvo \u2014 inténtalo de nuevo.",
    levi_action_organize: "Organizar la visita",
    levi_action_review_missing: "Revisar precios faltantes",
    levi_action_run_double_check: "Ejecutar doble revisión",
    levi_action_retry: "Intentar de nuevo",
    levi_secondary_more: "Más acciones de IA",
    levi_secondary_less: "Ocultar acciones de IA",
    levi_promise: "Levi nunca envía, aprueba ni edita el trabajo sin ti.",

    double_check_overline: "Doble revisión",
    double_check_title: "Ejecutar doble revisión",
    double_check_body_idle: "Revisión determinista previa al envío \u2014 detecta materiales, mano de obra, limpieza o demolición faltantes, T&M sin notas, provisiones sin divulgación, capturas incompletas del catálogo de materiales y más. Solo informativo; aún puedes generar la propuesta.",
    double_check_jump_hint: "Ir al panel de hallazgos.",
    double_check_body_running: "Revisando...",
    double_check_warnings: (n) => `${n} ${n === 1 ? "advertencia" : "advertencias"} necesitan revisión antes de enviar.`,
    double_check_red_suffix: (n) => `${n} en rojo`,
    double_check_yellow_suffix: (n) => `${n} en amarillo`,
    view_warnings: "Ver advertencias",
    include_ai_advisory: "Incluir asesoría de IA",
    include_ai_advisory_body: "Desactivado por defecto. Cuando está activado, agrega una asesoría específica del oficio encima de la revisión determinista. La asesoría de IA es solo para el contratista y nunca aparece en propuestas ni PDFs del cliente.",

    photos_heading: "Fotos y documentos del trabajo",
    photos_body: "Sube fotos del sitio, hojas de medición y PDFs. Levi los usará al revisar y ayudarte con este estimado.",

    builder_overline: "Constructor del estimado",
    builder_heading: "Renglones",
    add_line: "Agregar renglón",
    empty_lines: "No hay renglones. Haz clic en \u201cOrganizar alcance con IA\u201d o \u201cAgregar renglón\u201d.",

    totals_overline: "Totales",
    totals_no_priced: "No hay renglones con precio",
    totals_material_missing: "El total puede estar incompleto \u2014 falta material",
    totals_material_missing_title: "Uno o más renglones aún necesitan precio de material \u2014 el total probablemente esté por debajo del costo real.",
    totals_subtotal: "Subtotal",
    totals_tax_rate: "Tasa de impuesto",
    totals_tax: "Impuesto",
    totals_total: "Total",
    totals_deposit_pct: "Depósito %",
    totals_deposit: "Depósito",
    totals_balance: "Saldo por pagar",

    guard_hard_headline: "Este estimado aún no está listo para enviar.",
    guard_soft_headline: "Este estimado podría no estar listo para enviar",
    guard_hard_body: "JobClose AI no puede generar una propuesta hasta que cada renglón tenga precio. Agrega los precios que faltan abajo, o termina la configuración de precios del oficio en el asistente de configuración.",
    guard_soft_body: "Revisa los problemas abajo. Enviar ahora mostrará al cliente la propuesta exactamente con estos precios:",
    guard_soft_footnote: "La IA no debe reemplazar tus precios \u2014 la IA debe proteger tus precios.",
    guard_back_edit: "Volver a editar precios",
    guard_send_anyway: "Enviar de todos modos",
    guard_sending: "Enviando\u2026",

    ack_title: "Revisa los hallazgos de la doble revisión antes de enviar",
    ack_intro_prefix: "La doble revisión encontró",
    ack_intro_finding: (n) => `${n} hallazgo${n === 1 ? "" : "s"} en rojo`,
    ack_intro_suffix: "en este estimado. Revisa cada elemento abajo. Puedes continuar después de reconocerlos.",
    ack_suggestion_prefix: "Sugerencia:",
    ack_checkbox: "He revisado estos hallazgos en rojo y quiero continuar de todos modos.",
    ack_hint: "Este reconocimiento está atado a la versión actual del estimado. Si editas el estimado antes de generar una propuesta, te lo preguntaremos otra vez.",
    ack_back: "Volver al estimado",
    ack_confirm: "Reconocer y generar propuesta",
    ack_confirming: "Reconociendo\u2026",

    basis_title: "Convertir base de precios",
    basis_prompt_switch: (from, to) => `Estás cambiando el precio del material de ${from} a ${to}.`,
    basis_prompt_ratio: (basis, qty) => `¿Cuántos ${basis} por ${qty}?`,
    basis_prompt_examples: "Ejemplos: 3 paquetes por cuadro (tejas), 22 pies² por caja (LVP), 350 pies² por galón (pintura).",
    basis_cancel: "Cancelar",
    basis_apply: "Aplicar",

    pb_modal_title: "Insertar desde el catálogo de materiales",
    pb_modal_body: "Selector solo para el contratista. Proveedor, SKU y costo quedan solo de tu lado.",
    pb_close: "Cerrar",
    pb_all_trades: "Todos los oficios",
    pb_all_suppliers: "Todos los proveedores",
    pb_all_brands: "Todas las marcas",
    pb_search_placeholder: "producto / marca / SKU",
    pb_loading: "Cargando\u2026",
    pb_empty_prefix: "No hay materiales que coincidan en tu catálogo. Agrega materiales en ",
    pb_empty_link: "Perfil de precios \u2192 Catálogo de materiales",
    pb_waste: (pct) => `${pct}% de desperdicio`,
    pb_use: "Usar",
    pb_linked_prefix: "Vinculado:",
    pb_linked_fallback: "Elemento del catálogo",
    pb_change: "Cambiar",
    pb_clear: "Quitar",
    pb_insert: "Insertar del catálogo",
    pb_disclosure: "El nombre de marca / producto aparece en la propuesta del cliente solo si lo escribes en el nombre o descripción del renglón.",
    pb_snapshot_title: "Captura puntual desde tu catálogo de materiales. Solo para el contratista \u2014 nunca se muestra al cliente.",

    line_approved: "Precio aprobado",
    line_matched_settings: "Coincidencia con tu configuración de precios",
    line_review_required: "Revisión del contratista requerida",
    line_needs_pricing: "Necesita precio",
    line_needs_material: "Necesita precio de material",
    line_needs_quantity: "Ingresa la cantidad",
    line_item_name_placeholder: "Nombre del renglón",
    line_item_name_std_placeholder: "Nombre del renglón (ej. Instalar underlayment sintético)",
    line_description_installed_placeholder: "Alcance (visible al cliente)",
    line_description_std_placeholder: "Descripción (visible al cliente)",
    line_quantity: "Cantidad",
    line_quantity_prefilled_placeholder: "Precargado \u2014 edita solo para este trabajo",
    line_quantity_enter_placeholder: "Ingresa la cantidad para este trabajo",
    line_qty_select: "Selecciona o ingresa la cantidad del trabajo.",
    line_qty_default: "Cantidad del trabajo para este renglón.",
    line_unit: "Unidad",
    line_unit_locked_title: "La unidad está fijada según la unidad de venta aprobada en la configuración del oficio.",
    line_unit_locked_body: "Fijado \u2014 se establece en la configuración del oficio.",
    line_approved_price: "Precio unitario aprobado",
    line_approved_price_title: "La tarifa es tu precio de venta aprobado en la configuración del oficio. Cámbialo en la configuración del oficio.",
    line_per_unit: (unit) => `por ${unit}`,
    line_total: "Total del renglón",
    line_installed_prefix: "Precio instalado completo \u00b7 precio final para el cliente.",
    line_installed_body: "La tarifa aprobada ya incluye materiales, mano de obra, gastos generales y ganancia. JobClose AI no agregará otro margen. La tarifa proviene de tu configuración de precios aprobada.",
    line_installed_note_prefix: "Ajustar la cantidad o el nombre del renglón cambia",
    line_installed_note_this_job: "solo este trabajo",
    line_installed_note_suffix: ". Para cambiar el valor por defecto guardado para futuros trabajos, abre ",
    line_installed_note_link: "Configuración del oficio",
    line_from_job_meas: "mediciones del trabajo",
    line_from_prefix: "De ",
    line_adjusted_from_prefix: "Ajustado de ",
    line_from_work_area: "medición aprobada",
    line_use_approved_measurement: "Usar una medición aprobada",
    line_edited_suffix: " \u00b7 editado por el contratista",
    line_converted_to: (val, unit) => ` \u00b7 convertido a ${val} ${unit}`,
    line_needs_qty_title: "Ingresa la cantidad para este trabajo para calcular el total del renglón.",
    line_needs_pricing_title: "Agrega precio de material o mano de obra antes de enviar.",
    line_needs_material_title: "Este renglón requiere materiales pero el total del material sigue en $0.",
    line_move_up: "Subir renglón",
    line_move_down: "Bajar renglón",
    line_duplicate: "Duplicar renglón",
    line_remove: "Eliminar renglón",
    line_material: "Material",
    line_material_basis_title: "Base de precios para este material (puede diferir de la unidad de cantidad)",
    line_material_qty_title: (basis) => `Cantidad en ${basis}`,
    line_material_math_needed: (qty, unit) => `Precio del material no establecido (${qty} ${unit})`,
    line_material_math_priced: (mq, basis, cost, total) => `${mq} ${basis} \u00d7 ${cost} por ${basis} = ${total} material`,
    line_labor: "Mano de obra",
    line_labor_only_row: "Renglón solo de mano de obra",
    line_labor_only_title: "Este renglón no lleva materiales \u2014 cobra solo mano de obra / tarifa.",
    line_labor_math_legacy: (total) => `Plano ${total} mano de obra (heredado)`,
    line_labor_math_hour: (qty, rate, total) => `${qty} ${qty === 1 ? "hora" : "horas"} \u00d7 ${rate} por hora = ${total} mano de obra`,
    line_labor_math_job: (total) => `Plano ${total} mano de obra (por trabajo)`,
    line_labor_math_basis: (qty, basis, rate, total) => `${qty} ${basis} \u00d7 ${rate} por ${basis} = ${total} mano de obra`,
    line_qty_placeholder: "cant.",
    line_rate_placeholder: "tarifa",
    line_qty_helper: "Medición del trabajo y base de precio por defecto para este renglón.",
    line_markup: "Margen",
    line_per_hour: "por hora",
    line_per_job: "por trabajo",
    line_per_basis: (b) => `por ${b}`,
    line_basis_per_unit: (u) => `por ${u}`,

    pm_label: "Método de precio",
    pm_standard: "Estándar (mano de obra + materiales)",
    pm_standard_labor_only: "Estándar (solo mano de obra)",
    pm_unit_price: "Precio unitario (cant. \u00d7 precio unitario)",
    pm_flat: "Plano / suma alzada",
    pm_qty: "Cant.",
    pm_qty_labor_pkg: "Área de techo / unidades de mano de obra",
    pm_unit_price_label: "Precio unitario ($)",
    pm_labor_pkg_rate: "Tarifa del paquete de mano de obra ($)",
    pm_unit: "Unidad",
    pm_unit_placeholder: "\u2014 unidad \u2014",
    pm_line_total: "Total del renglón",
    pm_flat_price: "Precio plano ($)",
    pm_no_markup_default: "No se aplica margen adicional a los renglones con precio unitario \u2014 tu precio unitario se toma como el precio final para el cliente.",
    pm_no_markup_labor_pkg: "No se aplica margen adicional. Esta tarifa del paquete de mano de obra se toma como el precio final para el cliente en este renglón.",
    pm_no_markup_flat: "No se aplica margen adicional a los renglones planos / de suma alzada.",
    pm_prefill: (source, qty, unit, converted) => `Traído de ${source}: ${qty} ${unit || "cuadro"}${converted ? " (convertido desde pies²)" : ""}. Edita si es necesario.`,

    mib_headline: "El precio del material está incompleto.",
    mib_body_suffix: ". Revisa costos de materiales, mano de obra, margen y totales antes de enviar.",
    mib_unpriced: (n) => `${n} renglón${n === 1 ? "" : "es"} sin precio`,
    mib_need_material: (n) => `${n} renglón${n === 1 ? "" : "es"} necesita${n === 1 ? "" : "n"} precio de material`,

    scope_banner_added_headline: (n) => `La IA agregó ${n} renglón${n === 1 ? "" : "es"} sugerido${n === 1 ? "" : "s"} a tu estimado.`,
    scope_banner_added_body_prefix: "Estos son ",
    scope_banner_added_body_drafts: "borradores sugeridos",
    scope_banner_added_body_suffix: ". Revisa cantidades, mano de obra, materiales, margen y totales antes de enviar. Los renglones que aún no tengan precio están marcados como ",
    scope_banner_needs_pricing: "Necesita precio",
    scope_banner_added_body_dot: ".",
    scope_banner_empty_headline: "Alcance organizado \u2014 no se generaron renglones.",
    scope_banner_empty_body_prefix: "Después haz clic en ",
    scope_banner_empty_body_add: "Agregar renglón",
    scope_banner_empty_body_mid: " en el Constructor del estimado para agregar renglones con precio manualmente, o vuelve a ejecutar ",
    scope_banner_empty_body_run: "Organizar alcance con IA",
    scope_banner_empty_body_suffix: " después de agregar más detalle a las notas de la visita al sitio.",
    scope_banner_dismiss: "Descartar",

    aiscope_overline: "Levi \u00b7 Notas del alcance",
    aiscope_heading: "Alcance organizado",
    aiscope_block_scope: "Alcance del trabajo",
    aiscope_block_labor: "Supuestos de mano de obra",
    aiscope_block_material: "Supuestos de materiales",
    aiscope_block_missing: "Preguntas faltantes",
    aiscope_block_risk: "Áreas de riesgo",
    aiscope_customer_desc: "Descripción para el cliente",

    dc_sev_green: "Verde",
    dc_sev_yellow: "Amarillo",
    dc_sev_red: "Rojo",
    dc_stale_suffix: "\u00b7 desactualizado",
    dc_overline: "Doble revisión",
    dc_heading: "Hallazgos",
    dc_body: "Revisión determinista previa al envío. Solo para el contratista \u2014 nada de esto aparece en la propuesta, contrato o PDF del cliente.",
    dc_last_run: (ts) => ` \u00b7 Última ejecución ${ts}`,
    dc_rerun_run: "Ejecutar de nuevo",
    dc_rerun_stale: "Volver a ejecutar doble revisión",
    dc_rechecking: "Revisando\u2026",
    dc_stale_banner: "El estimado cambió desde la última doble revisión. Vuelve a ejecutarla para refrescar los hallazgos antes de generar una propuesta.",
    dc_ai_error_title: "Asesoría de IA no disponible en esta ejecución.",
    dc_ai_error_body: "La revisión determinista está completa. Puedes volver a ejecutar la doble revisión con asesoría de IA para intentarlo de nuevo.",
    dc_empty_green: "No se detectaron problemas. El estimado parece listo para enviar.",
    dc_ai_chip_title: "Esta asesoría viene de la capa de IA, no del motor determinista.",
    dc_suggestion_prefix: "Sugerencia:",
    dc_ai_chip: "IA",

    dc_finding: {
    missing_material: {
      title: (name) => `Falta precio de material en \u201c${name}\u201d`,
      detail: "Este renglón está marcado como que requiere material pero material_cost es $0. El total del estimado probablemente esté por debajo del costo real.",
      suggestion: "Abre el renglón e ingresa el precio del material por unidad base, o insértalo desde tu catálogo de materiales.",
    },
    missing_labor: {
      title: (name) => `Falta mano de obra en \u201c${name}\u201d`,
      detail: "El material tiene costo pero la mano de obra es $0 para este renglón. Enviarlo así dejará el trabajo con precio bajo.",
      suggestion: "Agrega horas de mano de obra \u00d7 tarifa para este renglón, o anota explícitamente en la descripción que es solo de material.",
    },
    missing_waste_factor: {
      title: (name) => `Puede faltar el factor de desperdicio en \u201c${name}\u201d`,
      detail: "La cantidad de material se ve menor de lo que sugeriría la reserva de desperdicio del artículo vinculado del catálogo de materiales.",
      suggestion: "Recalcula material_qty con el porcentaje de desperdicio de tu catálogo, o establécelo explícitamente si sabes que este trabajo necesita menos.",
    },
    missing_cleanup: {
      title: "Falta el renglón de limpieza",
      detail: "Este oficio casi siempre incluye una limpieza final, pero ningún renglón del estimado lo menciona.",
      suggestion: "Agrega un renglón de limpieza / barrido final para que el cliente lo vea y tú cobres por el tiempo.",
    },
    missing_demo_disposal: {
      title: "Falta el renglón de demolición / desecho",
      detail: "Ningún renglón menciona tear-off, demolición, desecho, dumpster o retiro. Solo las tarifas de vertedero pueden consumir la ganancia en este tipo de trabajo.",
      suggestion: "Agrega un renglón de demolición / desecho con la tarifa del vertedero + mano de obra para retirar y llevarse el material viejo.",
    },
    missing_access_notes: {
      title: "El acceso parece no estándar pero ningún renglón lo cubre",
      detail: "Las notas del proyecto mencionan escaleras, multi-nivel o acceso ajustado, pero nada en el estimado contempla el tiempo o equipo extra.",
      suggestion: "Agrega un renglón para andamio, mano de obra extra o preparación de acceso \u2014 o anota en la descripción de un renglón que el acceso suma tiempo.",
    },
    missing_equipment_rental: {
      title: "Puede faltar un renglón de equipo / alquiler",
      detail: "Este oficio típicamente requiere un dumpster, elevador o andamio. Ningún renglón menciona equipo o alquiler.",
      suggestion: "Agrega un renglón para el dumpster / elevador / andamio que planeas usar, o inclúyelo en el costo de un renglón existente.",
    },
    tm_without_notes: {
      title: (name) => `El renglón T&M \u201c${name}\u201d no tiene notas`,
      detail: "Este renglón está en Tiempo y Materiales pero no tiene notas ni descripción que expliquen qué se le facturará al cliente.",
      suggestion: "Agrega una explicación T&M en las notas del renglón para que el cliente no se sorprenda después y para que no se lea como parte del precio fijo.",
    },
    allowance_without_disclosure: {
      title: (name) => `El renglón de provisión \u201c${name}\u201d no tiene divulgación`,
      detail: "Los renglones de provisión requieren una descripción que diga al cliente qué selección está incluida y qué pasa si su elección cuesta más.",
      suggestion: "Agrega una frase como \u2018incluye hasta $X para la selección de losetas; el excedente se factura al costo\u2019 en la descripción o notas.",
    },
    sub_quote_zero: {
      title: (name) => `El renglón de cotización de subcontratista \u201c${name}\u201d no tiene monto`,
      detail: "Este renglón se cotiza desde un subcontratista pero el monto es $0. El cliente verá $0 para este alcance.",
      suggestion: "Ingresa el monto cotizado por el subcontratista o elimina el renglón si el sub aún no está en el alcance.",
    },
    unusually_low_labor: {
      title: "La mano de obra se ve muy baja vs. el material",
      detail: "La mano de obra total de este estimado está por debajo del 10% del costo total del material. Eso es inusual para la mayoría de oficios \u2014 puede faltar un renglón de mano de obra.",
      suggestion: "Verifica las horas de mano de obra en cada renglón, o asegúrate de que exista un renglón separado para el trabajo que no está ligado a material.",
    },
    incomplete_scope: {
      title: "El alcance se ve escaso para este tipo de trabajo",
      detail: (jobType, minMinusOne, lineCount) => `Este tipo de trabajo (${jobType || "remodelación compleja"}) usualmente tiene más de ${minMinusOne ?? 3} renglones. El estimado actual tiene ${lineCount ?? 0}.`,
      suggestion: "Verifica que materiales, mano de obra, demolición, limpieza y permisos / tarifas estén representados cada uno como renglón.",
    },
    price_book_data_incomplete: {
      title: (name) => `A la captura del catálogo en \u201c${name}\u201d le faltan datos`,
      detail: (missing) => `Este renglón está vinculado a tu catálogo de materiales pero a la captura congelada le falta: ${(missing || []).join(", ")}.`,
      suggestion: "Vuelve a abrir el selector del catálogo y reinserta este artículo para que la captura esté completa, o quita el vínculo y pon precio manualmente.",
    },
    category: {
      missing_material: "Falta material",
      missing_labor: "Falta mano de obra",
      missing_waste_factor: "Factor de desperdicio",
      missing_cleanup_demo_disposal: "Limpieza / desecho",
      missing_access_notes: "Acceso",
      missing_equipment_rental: "Equipo / alquiler",
      tm_without_notes: "Notas T&M",
      allowance_without_disclosure: "Provisión",
      sub_quote_zero: "Cotización de sub",
      unusually_low_labor: "Cordura de mano de obra",
      incomplete_scope: "Alcance",
      price_book_data_incomplete: "Catálogo",
      Pricing: "Precios",
      missing_profile_rules: "Faltan reglas del perfil",
    },
    },

    aicheck_overline: "Revisión IA del estimado",
    aicheck_heading: "Hallazgos",
    aicheck_suggestion_prefix: "Sugerencia:",
    aicheck_pricing_safety_title: "Seguridad de precios",
    aicheck_pricing_safety_suggestion: "Agrega precio de material y mano de obra a cada renglón, o elimina renglones no usados, antes de generar la propuesta.",
    aicheck_missing_rules_title: "Al perfil de precios le faltan reglas de material",
    aicheck_missing_rules_detail: (trade) => `Tu perfil de precios no incluye reglas estándar de material${trade ? ` para ${trade}` : ""}. La IA no puede sembrar costos de material de forma confiable sin ellas.`,
    aicheck_missing_rules_suggestion: "Completa la configuración de precios \u2192 agrega reglas de material / costos por unidad (ej. tejas por cuadro, underlayment, drip edge, ridge cap, sujetadores, botas de tubo, tarifas de vertedero). Luego vuelve a ejecutar Organizar alcance con IA.",

    issue_no_items: "Este estimado no tiene renglones.",
    issue_none_ready: "Ningún renglón está listo para enviar \u2014 agrega precio o cantidad.",
    issue_unpriced: (n) => `${n} renglón${n === 1 ? "" : "es"} aún necesita${n === 1 ? "" : "n"} precio (material y/o mano de obra).`,
    issue_needs_qty: (n) => `${n} renglón${n === 1 ? "" : "es"} con precio aprobado necesita${n === 1 ? "" : "n"} cantidad.`,
    issue_needs_material: (n) => `${n} renglón${n === 1 ? "" : "es"} aún necesita${n === 1 ? "" : "n"} precio de material \u2014 el total puede quedar muy por debajo del costo real.`,
    issue_missing_name: (n) => `${n} renglón${n === 1 ? "" : "es"} ${n === 1 ? "no tiene" : "no tienen"} nombre.`,
    issue_total_zero: "El total del estimado es $0.00 después de aplicar los precios \u2014 revisa la tasa de impuesto y los totales de los renglones.",
    issue_placeholders: (fields) => `Se detectó un marcador sin resolver en tus valores por defecto guardados (${fields}). Corrígelo en Fundamentos del documento antes de enviar \u2014 de lo contrario el cliente vería texto crudo como "[Empresa]".`,
    issue_proposal_not_created: "Propuesta no creada \u2014 este estimado aún tiene elementos sin precio.",
    issue_hardblock_fallback: "Este estimado aún tiene renglones sin precio.",

    toast_project_deleted: "El proyecto de este estimado fue eliminado.",
    toast_not_found: "Estimado no encontrado \u2014 puede que se haya eliminado.",
    toast_load_failed: "No se pudo cargar el estimado.",
    toast_matched: (system) => `Se emparejó ${system} desde tu configuración de precios. Revisa el renglón abajo.`,
    toast_staged: (system) => `${system} agregado.`,
    toast_stage_failed: "No se pudo agregar el sistema seleccionado.",
    toast_line_removed: (name) => `Renglón "${(name || "Sin título").slice(0, 40)}" eliminado`,
    toast_undo: "Deshacer",
    toast_saved: "Estimado guardado",
    toast_locked: "Este estimado está bloqueado \u2014 crea una nueva versión.",
    toast_save_failed: "No se pudo guardar",
    toast_new_version: (v) => `Nueva versión v${v} del estimado creada`,
    toast_new_version_failed: "No se pudo crear una nueva versión",
    toast_no_walkthrough: "No hay notas de la visita para organizar. Haz clic en Volver al trabajo \u2192 agrega notas de voz o escritas \u2192 regresa aquí y vuelve a ejecutar.",
    toast_organize_empty: "Organizar alcance se ejecutó pero no encontró nada a lo que poner precio. Intenta agregar más detalle de la visita en la página del proyecto y vuelve a ejecutar.",
    toast_organize_added: (n) => `La IA agregó ${n} renglones sugeridos \u2014 revisa los precios.`,
    toast_ai_scope_failed: "Falló el alcance con IA",
    toast_ai_check_done: "Revisión IA del estimado completada",
    toast_ai_check_failed: "Falló la revisión IA",
    toast_dc_red: (n) => `Doble revisión: ${n} hallazgo${n === 1 ? "" : "s"} en rojo`,
    toast_dc_yellow: (n) => `Doble revisión: ${n} hallazgo${n === 1 ? "" : "s"} en amarillo`,
    toast_dc_green: "Doble revisión: se ve bien",
    toast_dc_failed: "Falló la doble revisión",
    toast_save_before_send: "No se pudo guardar el estimado \u2014 intenta de nuevo.",
    toast_proposal_generated: "Propuesta generada",
    toast_proposal_failed: "No se pudo crear la propuesta",
    toast_ack_stale: "El estimado cambió mientras el diálogo estaba abierto \u2014 vuelve a ejecutar la doble revisión.",
    toast_ack_failed: "No se pudo guardar el reconocimiento",
    toast_pb_load_failed: "No se pudo cargar el catálogo de materiales",
    toast_pb_linked: (product) => `Se vinculó "${product}" desde tu catálogo`,

    status_draft: "borrador",
    status_sent: "enviado",
    status_approved: "aprobado",
    status_expired: "expirado",
    status_revoked: "anulado",
  },
  scope_orchestrator: {
    overline: "Levi \u00b7 Chequeo de alcance",
    headline: "Encontré trabajo que aún necesita atención.",
    all_priced_title: "Todo el alcance reconocido está en este estimado.",
    subhead: "Levi recomienda. Tú decides. No se agrega nada sin tu clic.",
    recognized_in: (field) => `Reconocido en ${field}`,
    recognized_from: "Reconocido en",
    source_field: {
      job_description: "Descripción del trabajo",
      typed_notes:     "Notas escritas",
      voice_transcript:"Recorrido por voz",
      measurements:    "Información del proyecto",
      labor_notes:     "Notas de mano de obra",
      material_notes:  "Notas de materiales",
    },
    count_priced: (n) => `${n} con precio`,
    count_needs_review: (n) => `${n} para revisar`,
    count_trade_not_setup: (n) => `${n} sin configurar`,
    count_unsupported_capability: (n) => `${n} con precio manual`,
    count_unresolved: (n) => `${n} pendientes`,
    state: {
      priced:                 "Con precio",
      needs_review:           "Revisar",
      trade_not_setup:        "Configuración de precios incompleta",
      unsupported_capability: "Precio manual requerido",
      unresolved:             "Aún no está en el estimado",
    },
    guidance: {
      trade_not_setup:        (trade) => `Encontré trabajo de ${trade}, pero la configuración de precios de ${trade} aún no está completa.`,
      unsupported_capability: (label) => `Encontré trabajo de ${label}. JobClose AI todavía no automatiza el precio de este trabajo, pero puedes ponerle precio manualmente solo para este trabajo.`,
    },
    set_up_trade: (trade) => `Configurar ${trade}`,
    price_this_job_only: "Precio solo para este trabajo",
    skip_for_now: "Omitir por ahora",
    needs_review_hint: "Abre el renglón para confirmar cantidad o precio.",
    review_line: "Revisar este renglón",
    load_failed: "No se pudo hacer el chequeo de alcance.",
    action_failed: "No se pudo actualizar este alcance. Intenta de nuevo.",
  },
  levi: {
    name_label: "Levi \u00b7 JobClose AI",
    alt_text: "Levi, la mascota de JobClose AI",
    headline_warning: "Levi olfateó cosas para revisar",
    headline_info_with_items: "Levi tiene un par de sugerencias suaves",
    headline_info_standing_by: "Levi está atento",
    headline_success: "Levi dice que este se ve limpio",
    what_is_title: "¿Qué es Levi?",
    show_notes: "Mostrar las notas de Levi",
    hide_notes: "Ocultar las notas de Levi",
    empty_hint: "Nada que olfatear en este momento.",
    learn_more: "Conocer a Levi",
    advisory_no_line_items_with_notes: "Tus notas de la visita al sitio están listas. Haz clic en Organizar alcance con IA arriba para convertirlas en renglones con precio.",
    advisory_no_line_items_without_notes: "Agrega notas de la visita en la página del proyecto (haz clic en Volver al trabajo) y luego haz clic en Organizar alcance con IA para armar el estimado.",
    advisory_organize_returned_nothing: "Organizar alcance con IA se ejecutó pero no encontró nada a lo que poner precio. Intenta agregar más detalle a las notas de la visita en la página del proyecto y vuelve a ejecutarlo.",
    advisory_double_check_never_run: "¿Quieres que le eche un olfateo antes de enviarlo? Ejecuta una doble revisión rápida.",
    advisory_double_check_stale: "Has cambiado cosas desde la última doble revisión. ¿Volverla a ejecutar?",
    advisory_double_check_findings: "Olfateé algunas cosas para revisar antes de enviar esto.",
    advisory_missing_material_pricing: "Parece que aún falta precio de material en algunos renglones.",
    advisory_notes_edited_after_scope: "Editaste las notas de la visita después de la última ejecución de Organizar alcance. Si quieres que el estimado refleje esas notas, vuelve a ejecutar Organizar alcance con IA.",
    advisory_all_clean: "Este se ve bastante limpio. No olfateé banderas rojas.",
    action_back_to_job: "Volver al trabajo",
    action_run_organize: "Organizar alcance con IA",
    action_run_double_check: "Ejecutar doble revisión",
    action_view_warnings: "Ver advertencias",
    action_jump_missing: "Ir al primero que falta",
  },
  photo_uploader: {
    uploading: "Subiendo...",
    drag_prompt: "Arrastra y suelta fotos / PDFs aquí, o",
    choose_files: "Elegir archivos",
    format_hint: (mb) => `JPG \u00b7 PNG \u00b7 HEIC \u00b7 WebP \u00b7 PDF \u00b7 hasta ${mb} MB cada uno`,
    toast_no_project: "El proyecto aún no está guardado.",
    toast_uploaded: (name) => `Se subió ${name}`,
    toast_over_limit: (name, mb) => `${name} supera los ${mb} MB \u2014 omitido.`,
    toast_failed: (name) => `Falló: ${name}`,
    toast_removed: (name) => `Se eliminó ${name || "archivo"}`,
    toast_remove_failed: "No se pudo eliminar el archivo",
    tooltip_remove: "Eliminar",
  },
  roofing_builder: {
    overline: "Techado \u00b7 opcional",
    title: "Constructor simple de techado",
    helper: "Elige el sistema de techado y cómo quieres poner precio a este trabajo específico. ¿No es techado? Déjalo colapsado \u2014 el editor estándar de estimado sigue funcionando normalmente.",
    approved_label: "Tus sistemas de techado aprobados (desde la configuración de precios)",
    approved_loading: "Cargando sistemas aprobados\u2026",
    approved_empty_prefix: "Aún no tienes sistemas de techado aprobados.",
    approved_empty_link: "Abrir configuración del oficio",
    approved_empty_suffix: " para aprobar uno y aparecerá aquí.",
    approved_price_prefix: "Precio aprobado \u00b7 ",
    approved_price_per: (unit) => `por ${unit}`,
    approved_flat_prefix: "Precio plano aprobado \u00b7 ",
    approved_reusable_prefix: "Regla reutilizable aprobada \u00b7 ",
    use_approved: "Usar precio aprobado",
    installed_headline: "Precio instalado completo \u00b7 precio final para el cliente",
    installed_body: "Materiales incluidos \u00b7 Mano de obra incluida \u00b7 Gastos generales incluidos \u00b7 Ganancia incluida \u00b7 Sin margen adicional.",
    installed_note: "La tarifa proviene de tu configuración de precios aprobada. JobClose AI no agregará otro margen.",
    approved_this_job_prefix: "Ajustar la cantidad, el precio unitario o las notas del renglón agregado cambia ",
    approved_this_job_bold: "solo este trabajo",
    approved_this_job_suffix: ". Para cambiar el valor por defecto guardado para futuros trabajos, abre ",
    approved_this_job_link: "Configuración del oficio",
    q_system: "¿Qué tipo de instalación de techado estás estimando?",
    choose_system: "\u2014 Elegir un sistema de techado \u2014",
    q_pricing: "¿Cómo quieres poner precio a este techo?",
    pricing_radiogroup_label: "Estilo de precios del estimado",
    labor_pkg_hint_with_system_prefix: "Agrega un solo renglón amigable con el techador \u2014 ",
    labor_pkg_hint_with_system_suffix: ". Ingresa cantidad \u00d7 tarifa. El total lo establece el contratista; no se agrega margen adicional.",
    labor_pkg_hint_no_system_prefix: "Elige un sistema de techado arriba para habilitar el botón amigable de paquete de mano de obra. Esto inserta ",
    labor_pkg_hint_no_system_bold: "un",
    labor_pkg_hint_no_system_suffix: " renglón \u2014 no un desglose de tear-off / instalación / flashing / desecho.",
    labor_pkg_already_added: "Paquete de mano de obra ya agregado \u2014 ir al renglón",
    labor_pkg_add: "Agregar paquete de mano de obra de techado",
    detailed_hint_bold: "Modo detallado.",
    detailed_hint_body: " Usa el editor estándar de renglones abajo para agregar tear-off, instalación, flashing, desecho y materiales como filas separadas. Todo lo existente \u2014 Slice F precio unitario, insertar del catálogo de materiales, organizador con IA, doble revisión \u2014 sigue funcionando.",
    labor_package_description_help: "Este paquete de mano de obra pretende incluir tear-off estándar, instalación del sistema de techado aprobado, mano de obra normal de la cuadrilla y limpieza, a menos que se excluya o se detalle por separado abajo.",
    system_asphalt_shingle: "Reemplazo de techo de tejas asfálticas",
    system_tpo: "Sistema TPO / techo plano",
    system_standing_seam: "Techo metálico de junta alzada",
    system_tile: "Techo de tejas",
    system_mod_bit: "Bitumen modificado / techo plano",
    system_coating: "Sistema de recubrimiento de techo",
    system_repair: "Solo reparación de techo",
    system_other: "Otro / alcance de techado personalizado",
    style_labor_package_materials_label: "Paquete de mano de obra + materiales",
    style_labor_package_materials_desc: "Un paquete de mano de obra para el techo + los materiales que agrega el contratista.",
    style_all_in_per_square_label: "Precio todo incluido por cuadro de techado",
    style_all_in_per_square_desc: "Una tarifa por cuadro de techado cubre todo.",
    style_all_in_per_sqft_label: "Precio todo incluido por pie²",
    style_all_in_per_sqft_desc: "Una tarifa por pie² cubre todo.",
    style_flat_lump_sum_label: "Plano / suma alzada",
    style_flat_lump_sum_desc: "Un precio para el alcance de techado aprobado.",
    style_detailed_label: "Desglose detallado renglón por renglón",
    style_detailed_desc: "Divide tear-off, instalación, flashing, desecho y materiales en filas.",
    toast_already_added: (name) => `${name} ya está en este estimado \u2014 saltamos a ese renglón.`,
    toast_added_with_prefill: (name, qty, unitDisplay, source, converted) => `${name} agregado. Se trajeron ${qty} ${unitDisplay} de ${source}${converted ? " (convertido)" : ""}. Ingresa la tarifa del paquete de mano de obra abajo.`,
    toast_added_no_prefill: (name) => `${name} agregado. Ingresa el área del techo y la tarifa del paquete de mano de obra abajo.`,
    toast_approved_added_prefill: (displayName, sourceText, sourceLabel) => `Se agregó ${displayName} con precio aprobado. Precargado ${sourceText} de ${sourceLabel}.`,
    toast_approved_added: (displayName) => `Se agregó ${displayName} con precio aprobado. Ingresa la cantidad abajo.`,
    toast_approved_duplicate: (displayName) => `${displayName} ya está en este estimado.`,
  },
  proposals_form: {
    list_empty_title: "Aún no hay propuestas",
    list_empty_body: "Crea un estimado y luego haz clic en \"Generar propuesta\".",
    list_empty_action: "Ver estimados",
    col_customer: "Cliente",
    col_total: "Total",
    col_status: "Estado",
    col_created: "Creado",

    detail_title_prefix: "Propuesta",
    back: "Atrás",
    preview_customer: "Previsualizar vista del cliente",
    print: "Imprimir",
    download_pdf: "Descargar PDF",
    copy_link: "Copiar enlace del cliente",
    email_link: "Enviar enlace al cliente",
    mark_approved: "Marcar como aprobada",
    contract_lang_label: "Contrato",
    lang_english: "Inglés",
    lang_spanish: "Español",
    draft_contract: "Redactar contrato",

    fallback_company: "Tu empresa",
    header_overline: "Propuesta",
    header_date: "Fecha:",
    header_status: "Estado:",

    section_scope: "Alcance del trabajo",
    section_included: "Incluido",
    section_excluded: "Excluido",
    section_pricing: "Precios",
    col_item: "Artículo",
    col_qty: "Cant.",
    col_unit: "Unidad",
    allowance_badge: "Provisión",
    tfoot_subtotal: "Subtotal",
    tfoot_tax: "Impuesto",
    tfoot_total: "Total",
    tfoot_deposit: "Depósito",
    tfoot_balance: "Saldo por pagar",
    tm_reserve_prefix: "Posible reserva T&M",
    tm_reserve_suffix: "\u00b7 no incluido en el total en firme",
    allowance_note_prefix: "Provisión:",
    allowance_note_body: " Los renglones de provisión son montos preliminares. El costo final puede variar según selecciones o condiciones y se manejará mediante una orden de cambio por escrito.",
    tm_section_heading: "Trabajo adicional posible (Tiempo y Materiales)",
    tm_col_estimate: "Total est.",
    tm_footnote: "Estos renglones son solo estimados y pueden facturarse si el trabajo llega a ser necesario.",
    section_payment: "Calendario de pagos",
    section_timeline: "Cronograma",
    section_next_steps: "Próximos pasos",

    prompt_copy_link: "Copia este enlace:",
    prompt_email_to: "Enviar el correo de la propuesta a:",
    toast_public_link_copied: "Enlace público copiado \u2014 compártelo con tu cliente",
    toast_email_queued: "Correo en cola (envío simulado)",
    toast_email_sent: "Correo enviado",
    toast_email_failed: "No se pudo enviar el correo",
    toast_downloaded: (filename) => `Descargado ${filename}`,
    toast_pdf_failed: "No se pudo generar el PDF",
    toast_approved: "Propuesta aprobada. Lista para redactar el contrato.",
    toast_approve_failed: "No se pudo aprobar",
    toast_contract_created: "Borrador de contrato creado",
    toast_contract_failed: "No se pudo crear el contrato",

    status_draft: "borrador",
    status_sent: "enviado",
    status_approved: "aprobado",
    status_expired: "expirado",
    status_revoked: "anulado",
  },
  // Slice VI-02 · Decisión y Promoción de Mediciones del Contratista (ES).
  vi02: {
    section: {
      title: "Mediciones sugeridas por Levi",
      loading: "Cargando sugerencias de Levi…",
      pending_count: "{count} pendientes de tu revisión",
      empty_recommend_video: "Aún no hay sugerencias de Levi para este proyecto. Un recorrido en video guiado le ayuda a Levi a proponer mediciones que puedes revisar aquí.",
      show_resolved: "Mostrar resueltas ({count})",
      hide_resolved: "Ocultar resueltas",
      hierarchy_note: "El video y la voz guiados son la fuente principal de Levi. Las fotos apoyan al video. Tu decisión es definitiva.",
    },
    state: {
      levi_preliminary: "Preliminar — esperando tu revisión",
      accepted_levi: "Aceptada",
      contractor_corrected: "Corregida y aceptada",
      contractor_rejected: "Rechazada",
      recapture_requested: "Recaptura solicitada",
      // Etiqueta amigable para la candidata que el contratista revisó
      // y decidió posponer. El estado técnico sigue siendo
      // left_preliminary; sólo cambia la etiqueta visible.
      left_preliminary: "Decidir después",
    },
    evidence: {
      video: "Video",
      video_and_voice: "Video + Voz",
      voice: "Voz",
      photo: "Foto",
      calculation: "Cálculo",
      unknown_provenance: "Procedencia desconocida",
    },
    confidence: {
      high: "confianza alta",
      medium: "confianza media",
      low: "confianza baja",
    },
    values: {
      levi_proposed: "Levi propuso",
      contractor_approved: "Tú aprobaste",
    },
    candidate: { unnamed: "Medición sin nombre" },
    blocked: {
      awaiting_contractor_decision: "Aún no confiable — decide para promover.",
      contractor_rejected: "La rechazaste — no elegible.",
      recapture_requested: "Esperando evidencia adicional.",
      contractor_deferred: "Elegiste decidir después — no elegible hasta decidir.",
      superseded: "Reemplazada por una decisión más reciente.",
      not_promoted: "Aún no promovida a Áreas de trabajo.",
    },
    actions: {
      accept: "Aceptar",
      correct: "Corregir",
      reject: "Rechazar",
      recapture: "Pedir recaptura",
      leave_preliminary: "Decidir después",
      toggle_details: "Mostrar detalles",
      cancel: "Cancelar",
    },
    correct: {
      title: "Ingresa tu valor medido",
      original_preserved_note: "La propuesta original de Levi se conservará para referencia.",
      approved_value: "Tu valor",
      approved_unit: "Unidad",
      reason_placeholder: "Nota opcional (por qué la corrección)",
      confirm: "Guardar corrección",
    },
    confirm: {
      accept_title: "¿Aceptar la propuesta de Levi?",
      accept_message: "Se promoverá el valor de Levi a Áreas de trabajo como una medición confiable. Se puede corregir después.",
      reject_title: "¿Rechazar esta medición?",
      reject_message: "La candidata se conservará en el historial pero no se podrá usar más adelante.",
      recapture_title: "¿Pedir recaptura?",
      recapture_message: "Levi te dirá qué evidencia adicional se necesita. Se prefiere un video guiado; una foto de apoyo también es bienvenida.",
      leave_title: "¿Decidir después?",
      leave_message: "Puedes volver a decidir cuando quieras. La candidata queda excluida de las mediciones confiables.",
      // Slice VI-02 alineación pre-QA · Confirmación final de corrección.
      correct_title: "¿Guardar esta corrección?",
      correct_original_label: "Levi propuso",
      correct_new_label: "Tu valor corregido",
      correct_context_label: "Medición",
      correct_becomes_trusted: "Tu valor corregido se convertirá en la medición confiable del proyecto.",
      correct_no_estimate: "No se cambiará ningún estimado, línea, precio, alcance, contrato ni factura.",
      correct_confirm_label: "Guardar corrección",
    },
    recapture: {
      guidance_title: "Guía de recaptura de Levi",
      prefer_video_note: "Se prefiere un video guiado breve; una foto de cerca también ayuda.",
    },
    details: {
      dimension_kind: "Tipo de dimensión",
      session_ref: "Del recorrido en video",
      evidence_list: "Evidencia",
    },
    toast: {
      accept_success: "Aceptada y agregada a Áreas de trabajo.",
      correct_success: "Corrección guardada y agregada a Áreas de trabajo.",
      reject_success: "Marcada como rechazada.",
      recapture_success: "Recaptura solicitada.",
      "leave-preliminary_success": "Dejada preliminar.",
    },
    errors: {
      invalid_value: "Ingresa un valor válido y no negativo.",
      invalid_unit: "Elige una unidad válida.",
      decision_locked: "La decisión de esta medición ya es definitiva.",
      accept_failed: "No se pudo aceptar — intenta de nuevo.",
      correct_failed: "No se pudo guardar la corrección.",
      reject_failed: "No se pudo rechazar.",
      recapture_failed: "No se pudo pedir la recaptura.",
      "leave-preliminary_failed": "No se pudo diferir.",
      generic: "Algo salió mal. Intenta de nuevo.",
    },
  },
  // Slice VI-03 · Sugerencias de estimado revisadas por el contratista (ES).
  vi03: {
    section: {
      title: "Alcance sugerido por Levi",
      loading: "Cargando sugerencias…",
      empty: "Aún no hay sugerencias. Aparecerán después de que apruebes las mediciones de Levi en Áreas de trabajo.",
      item_count: "{count} sugerencias",
      accepted_count: "{count} aceptadas, listas para comprometer",
      committed_note: "Estas sugerencias ya se comprometieron al estimado.",
      invariant_note: "Nada entra al estimado hasta que comprometas los ítems seleccionados.",
      refresh: "Actualizar desde Áreas de trabajo aprobadas",
    },
    state: {
      levi_preliminary: "Esperando tu revisión",
      contractor_accepted: "Aceptada",
      contractor_edited: "Editada y aceptada",
      contractor_rejected: "Rechazada",
      committed_to_estimate: "Comprometida al estimado",
    },
    item: {
      from_work_area: "Del Área de trabajo",
      levi_proposed: "Levi propuso",
      your_edit: "Tu edición",
    },
    edit: { quantity: "Cant.", unit: "Unidad", unit_price: "Precio unitario",
            save: "Guardar edición" },
    actions: { accept: "Aceptar", edit: "Editar", reject: "Rechazar",
               reset: "Restablecer", commit: "Comprometer al estimado",
               cancel: "Cancelar" },
    commit: {
      title: "¿Comprometer las sugerencias seleccionadas a este estimado?",
      summary: "{count} sugerencia(s) aceptada(s) se convertirán en nuevas líneas en {estimate}.",
      becomes_lines: "Las sugerencias seleccionadas se convierten en nuevas líneas del estimado.",
      no_downstream: "No se creará ninguna propuesta, contrato, factura, orden de trabajo, cita ni mensaje al cliente.",
      confirm: "Comprometer",
    },
    toast: { committed: "Sugerencias comprometidas al estimado." },
    errors: {
      prepare_failed: "No se pudieron cargar las sugerencias.",
      commit_failed: "No se pudo comprometer — intenta de nuevo.",
      no_committable_items: "Acepta al menos una sugerencia antes de comprometer.",
      estimate_not_found: "Ese estimado no se encontró.",
      project_mismatch: "Ese estimado pertenece a otro proyecto.",
      suggestion_not_committable: "Esta sugerencia ya no es editable.",
      item_state_locked: "El estado de ese ítem ya no puede cambiar.",
      item_already_committed: "Ese ítem ya está en el estimado.",
      no_editable_fields: "Ingresa un valor o unidad para editar.",
      generic: "Algo salió mal. Intenta de nuevo.",
    },
  },
  // Slice 2H · Fundación de Inteligencia de Mediciones — Áreas de Trabajo (ES).
  work_areas: {
    card_title: "Mediciones del trabajo",
    card_subtitle: "Mide cuartos, áreas exteriores, secciones de techo, objetos o cualquier otra cosa que necesites para este trabajo.",
    measure_action: "Empezar a medir",
    empty_state: "Aún no se han agregado mediciones.",
    empty_state_optional: "¿Ya tienes tus mediciones? Puedes seguir usando tus propias cantidades en el estimado.",
    future_methods_note: "Pronto habrá más formas de medir con Levi.",
    chooser_title: "¿Qué te gustaría medir?",
    chooser_cancel: "Cancelar",
    chooser: {
      interior_room: "Cuarto interior",
      exterior_area: "Área exterior",
      roof_section: "Sección de techo",
      elevation: "Pared / Elevación",
      object: "Objeto",
      linear_measurement: "Medición lineal",
      something_else: "Otra cosa",
    },
    types: {
      interior_room: "Cuarto interior",
      exterior_area: "Área exterior",
      roof_section: "Sección de techo",
      elevation: "Pared / Elevación",
      commercial_space: "Espacio comercial",
      mechanical_area: "Área mecánica",
      linear_run: "Medición lineal",
      object: "Objeto",
      custom: "Otra cosa",
    },
    openings: { door: "Puerta", window: "Ventana", opening: "Abertura", other: "Otra" },
    sources: {
      manual: "Ingresado manualmente",
      apple_roomplan: "Escaneado con iPhone",
      camera_assisted: "Agregado desde fotos",
      voice_assisted: "Agregado por voz",
      bluetooth_device: "Agregado desde dispositivo",
      plan_import: "Agregado desde plano",
      drone: "Dron",
      other: "Otro",
    },
    units: { ft: "ft", in: "in", sqft: "pies\u00b2", linear_ft: "pies lineales", count: "conteo", each: "cada uno", ft_in: "ft/in" },
    status: { approved: "Aprobado", review_required: "Lista para revisar" },
    form: {
      name: "Nombre de la medición", name_placeholder: "ej. Sala, Techo frontal, Árbol del patio",
      name_placeholders: {
        interior_room: "ej. Sala, Cocina, Recámara",
        exterior_area: "ej. Patio delantero, Patio, Entrada para autos",
        roof_section: "ej. Techo frontal, Techo del garaje, Techo del porche",
        elevation: "ej. Elevación frontal, Pared norte, Pared del garaje",
        object: "ej. Árbol del patio, Calentador de agua, Base para equipo",
        linear_measurement: "ej. Cerca del patio, Zoclo de la sala, Canalón frontal",
        something_else: "ej. Área o elemento personalizado",
      },
      change_type: "Cambiar",
      area_type: "Tipo de medición",
      length_ft: "Largo (ft)", width_ft: "Ancho (ft)", height_ft: "Altura (ft)",
      total_length_ft: "Largo total (ft)", count: "Conteo",
      openings: "Aberturas \u2014 puertas, ventanas y áreas similares", add_opening: "Agregar abertura",
      opening_label: "Etiqueta", opening_width: "Ancho (ft)", opening_height: "Alto (ft)", opening_qty: "Cant",
      custom_dimensions: "Mediciones", add_custom: "Agregar medición",
      custom_label_placeholder: "ej. Altura, Ancho, Diámetro",
      notes: "Notas para Levi",
      approved_at_label: "Aprobado",
      cancel: "Cancelar", save_draft: "Guardar mediciones", save_review: "Guardar mediciones",
      no_mutation_notice: "Guardar o aprobar mediciones no cambia tu estimado ni ninguna otra parte del trabajo. Tú decides cuándo y cómo usarlas.",
    },
    actions: { edit: "Editar mediciones", approve_and_save: "Aprobar mediciones", delete_confirm: "¿Eliminar esta medición?" },
    calc: {
      preview_title: "Valores calculados",
      floor_area: "Área de piso", ceiling_area: "Área de techo", perimeter: "Perímetro",
      gross_wall_area: "Área bruta de muros", total_opening_area: "Área total de aberturas",
      net_wall_area: "Área neta de muros", linear_feet: "Pies lineales", run_wall_area: "Área del tramo",
    },
    saved_ready_hint: "Estas mediciones están listas si decides usarlas más adelante al estimar.",
    validation: { openings_exceed_walls: "El área de las aberturas excede el área bruta de muros. Corrige las aberturas antes de aprobar." },
    toast: { saved_draft: "Mediciones guardadas.", saved_review: "Mediciones actualizadas \u2014 lista para revisar.", approved: "Mediciones aprobadas.", deleted: "Medición eliminada." },
    review_summary: {
      approved_count: (n) => `${n} aprobadas`,
      review_required_count: (n) => `${n} para revisar`,
      ready_hint: "Las mediciones aprobadas están listas para usarse en el estimado.",
    },
    staging: {
      picker_open: "Usar una medición aprobada",
      picker_title: "Usar una medición aprobada",
      picker_subtitle: "Solo se muestran las mediciones aprobadas de este proyecto. Levi recomienda. Tú decides.",
      no_approved: "Aún no tienes mediciones aprobadas en este proyecto.",
      no_approved_hint: "Guarda y aprueba una medición primero para que aparezca aquí.",
      choose_measurement: "Elige una medición",
      choose_calc: "Elige un valor calculado",
      review_title: "Revisa antes de usar",
      review_source: "De la medición",
      current_quantity: "Cantidad actual en esta línea",
      current_quantity_none: "Esta línea aún no tiene cantidad",
      proposed_quantity: "Cantidad propuesta",
      proposed_hint: "Puedes editarla antes de usarla. Solo afecta esta línea, este trabajo.",
      edit_before_using: "Editar antes de usar",
      cancel_edit: "Cancelar edición",
      use_proposed: "Usar cantidad propuesta",
      keep_existing: "Mantener la cantidad actual",
      cancel_skip: "Cancelar / Omitir",
      replace_warning: "Esta línea ya tiene una cantidad que tú ingresaste o editaste. Al reemplazarla se sobrescribe solo en esta línea.",
      staging_now: "Aplicando la medición\u2026",
      staged_ok: "Medición aplicada a esta línea.",
      already_staged: "Esta medición ya está en esta línea.",
      kept_existing: "Cantidad actual conservada.",
    },
    errors: {
      save_failed: "No se pudieron guardar estas mediciones. Intenta de nuevo en un momento.",
      approve_failed: "No se pudieron aprobar estas mediciones. Intenta de nuevo en un momento.",
      delete_failed: "No se pudo eliminar esta medición.",
      work_area_name_required: "Se necesita un nombre para la medición.",
      invalid_area_type: "Elige qué estás midiendo.",
      invalid_measurement_mode: "Elige un tipo de medición válido.",
      invalid_source: "Fuente de medición inválida.",
      invalid_length: "El largo debe ser cero o un número positivo.",
      invalid_width: "El ancho debe ser cero o un número positivo.",
      invalid_height: "La altura debe ser cero o un número positivo.",
      invalid_total_length: "El largo total debe ser cero o un número positivo.",
      invalid_opening_type: "Tipo de abertura inválido.",
      invalid_opening_width: "El ancho de la abertura debe ser cero o un número positivo.",
      invalid_opening_height: "La altura de la abertura debe ser cero o un número positivo.",
      invalid_opening_quantity: "La cantidad de aberturas debe ser cero o un número positivo.",
      custom_dimension_label_required: "Cada medición necesita una etiqueta.",
      invalid_unit: "Unidad inválida.",
      opening_area_exceeds_wall_area: "El área de las aberturas excede el área bruta de muros.",
      work_area_not_found: "No se encontró esa medición.",
      work_area_not_approved: "Esa medición aún no está aprobada.",
      project_mismatch: "Esa medición pertenece a otro proyecto.",
      invalid_calc_field: "Ese valor calculado no está disponible en esta medición.",
      invalid_calc_value: "Ese valor calculado falta en esta medición.",
      estimate_line_not_found: "No se encontró esa línea del estimado.",
      estimate_not_found: "No se encontró ese estimado.",
      replacement_confirmation_required: "Esta línea ya tiene una cantidad que ingresaste. Confirma antes de reemplazarla.",
      staging_failed: "No se pudo usar esta medición. Intenta de nuevo en un momento.",
    },
  },

  project_detail: {
    fallback_title: "Proyecto",
    loading: "Cargando...",
    no_address: "Sin dirección",
    all_projects: "Todos los proyectos",
    resuming_draft: "Reanudando tu borrador",
    still_draft: "Este proyecto aún es un borrador",
    finish_working: "Procesando...",
    finish_action: "Terminar y crear estimado",
    crew_packet_title: "Paquete para la cuadrilla",
    crew_packet_body_disabled: "Crea un estimado antes de generar un paquete para la cuadrilla.",
    crew_packet_body_idle: "Genera un paquete seguro para la cuadrilla \u2014 sin precios expuestos.",
    crew_packet_generate: "Generar paquete de cuadrilla",
    crew_packet_working: "Procesando...",
    closeout_title: "Cierre / Garantía",
    closeout_body_existing: "Abrir el paquete de cierre existente de este proyecto.",
    closeout_body_idle: "Genera un paquete de cierre listo para el cliente con garantía, mantenimiento y próximos pasos.",
    closeout_open: "Abrir paquete de cierre",
    closeout_generate: "Generar paquete de cierre",
    closeout_working: "Procesando...",
    job_details: "Detalles del trabajo",
    edit_notes: "Editar notas de la visita",
    edit_notes_title: "Corrige o agrega a las notas originales de la visita al sitio capturadas durante la toma. NO reescribe los renglones del estimado.",
    label_customer_phone: "Teléfono del cliente",
    label_customer_email: "Correo del cliente",
    label_timeline: "Cronograma",
    label_created: "Creado",
    label_description: "Descripción",
    customer: "Cliente",
    photos_docs: "Fotos y documentos",
    pcv_working_docs_overline: "Documentos de trabajo",
    pcv_working_docs_headline: "Estimados · Propuestas · Contratos",
    pcv_job_intel_overline: "Inteligencia del trabajo",
    pcv_job_intel_headline: "Vision · Medidas · Notas de Levi",
    pcv_job_outcome_overline: "Resultado del trabajo",
    pcv_job_outcome_headline: "Facturas · Rentabilidad · Cierre",
    pcv_pill_ask_levi: "Pregúntale a Levi",
    notes: "Notas",
    typed_notes_prefix: "Notas escritas:",
    measurements_prefix: "Mediciones:",
    job_notes_prefix: "Notas y mediciones del trabajo:",
    material_prefix: "Material:",
    labor_prefix: "Mano de obra:",
    special_prefix: "Especial:",
    concerns_prefix: "Preocupaciones:",
    related_estimates: "Estimados",
    related_proposals: "Propuestas",
    related_contracts: "Contratos",
    related_invoices: "Facturas",
    related_updates: "Avisos al cliente",
    toast_notes_saved: "Notas de la visita guardadas.",
    toast_notes_failed: "No se pudieron guardar las notas de la visita.",
    toast_draft_finished: "Borrador promovido a activo. Continúa con tu estimado.",
    toast_draft_failed: "No se pudo terminar el borrador",
    toast_crew_estimate_first: "Crea un estimado antes de generar un paquete para la cuadrilla.",
    toast_crew_generated: "Paquete de cuadrilla generado",
    toast_crew_failed: "No se pudo generar el paquete de cuadrilla",
    toast_closeout_existing: "Abriendo el paquete de cierre existente",
    toast_closeout_generated: "Paquete de cierre generado",
    toast_closeout_failed: "No se pudo generar el paquete de cierre",
    draft_body: "Agrega las fotos o notas faltantes abajo, luego promuévelo a un proyecto activo y continúa con tu estimado.",
    related_none_yet: "Aún no hay ninguno.",
    related_new: "+ Nuevo",
    status_labels: {
      draft: "Borrador",
      sent: "Enviado",
      approved: "Aprobado",
      expired: "Expirado",
      revoked: "Anulado",
      signed: "Firmado",
      ready: "Listo",
      active: "Activo",
    },
  },
  voice_notes: {
    title: "Notas de voz",
    help: "Graba o sube un audio de la visita al sitio. Whisper lo transcribe, tú lo revisas y editas, y luego lo guardas como nota del proyecto o lo agregas a un estimado. Las notas de voz son internas — nunca aparecen en propuestas, contratos, facturas ni paquetes de cuadrilla del cliente.",
    record: "Grabar",
    stop: "Detener",
    upload_audio: "Subir audio",
    format_hint: (mb) => `MP3 · M4A · WAV · WEBM · OGG · hasta ${mb} MB`,
    loading: "Cargando notas de voz…",
    empty: "Aún no hay notas de voz. Graba o sube la primera arriba.",
    exceeds_limit: (mb) => `El audio supera el límite de ${mb} MB.`,
    audio_uploaded: "Audio subido. Transcribiendo…",
    transcript_ready: "Transcripción lista.",
    upload_failed: "No se pudo subir la nota de voz",
    recording_unsupported: "La grabación no es compatible con este navegador.",
    recording_prompt: "Grabando… haz clic en Detener cuando termines.",
    mic_denied: "Acceso al micrófono denegado.",
    confirm_delete: "¿Eliminar esta nota de voz? Esto no se puede deshacer.",
    deleted: "Nota de voz eliminada.",
    delete_failed: "No se pudo eliminar",
    transcript_saved: "Transcripción guardada.",
    save_failed: "No se pudo guardar",
    transcript_refreshed: "Transcripción actualizada.",
    transcription_failed: "No se pudo transcribir",
    appended_success: "Agregado a las notas del estimado.",
    append_failed: "No se pudo agregar",
    status_uploaded: "Subido",
    status_transcribing: "Transcribiendo…",
    status_transcribed: "Transcrito",
    status_failed: "Falló",
    fallback_filename: "Nota de voz",
    badge_recorded: "Grabado",
    linked_suffix: " · agregado a un estimado",
    error_prefix: "Error:",
    aria_toggle: "Alternar nota de voz",
    aria_delete: "Eliminar nota de voz",
    retry_transcription: "Reintentar transcripción",
    transcribe_now: "Transcribir ahora",
    transcript_label: "Transcripción (editable)",
    transcript_placeholder: "Transcripción…",
    save_edit: "Guardar edición",
    retranscribe_title: "Volver a ejecutar Whisper sobre el audio original (no sobrescribe tus ediciones)",
    retranscribe: "Volver a transcribir",
    estimate_option: (short, n) => `Estimado ${short} · ${n} renglones`,
    dirty_title: "Guarda tus ediciones primero",
    append_to_estimate: "Agregar a las notas del estimado",
    internal_footer: "Las notas de voz son internas. Nunca aparecen en propuestas, contratos, facturas, paquetes de cierre ni paquetes de cuadrilla del cliente.",
  },
  levi_estimate: {
    chooser_heading: "¿Qué estás cotizando hoy?",
    chooser_help: "Elige el flujo que se ajuste a este trabajo. Siempre puedes cambiar después.",
    chooser_approved_title: "Estimado de proyecto completo",
    chooser_approved_body: "Ideal para techado, HVAC, restauración, remodelación, pisos, pintura, cercado, concreto y otros proyectos estándar de contratista usando tus precios guardados y tu flujo detallado de estimados.",
    chooser_approved_action: "Iniciar estimado completo",
    chooser_levi_title: "Trabajo puntual con Levi",
    chooser_levi_badge: "Nuevo",
    chooser_levi_body: "Ideal para trabajos poco comunes, personalizados o pequeños que no encajan en tus precios normales. Ejemplos: retiro de árboles · retiro de escombros · demolición de cobertizos · trabajos pequeños de manitas · trabajos personalizados varios.",
    chooser_levi_action: "Iniciar trabajo puntual",
    chooser_levi_notice: "El Trabajo puntual con Levi está pensado para trabajos poco comunes o personalizados. Para sistemas de techado completos, reemplazos de HVAC, proyectos de restauración, remodelaciones y otros trabajos estándar de oficio, usa el Estimado de proyecto completo para obtener el resultado más preciso.",
    before_title: "¿Es Levi la herramienta adecuada para este trabajo?",
    before_body: "El Trabajo puntual con Levi está diseñado para trabajos poco comunes o personalizados. Si estás cotizando un sistema completo de techado, un reemplazo de HVAC, un proyecto de restauración, una remodelación u otro proyecto estándar de oficio, normalmente obtendrás un estimado más preciso usando el Estimado de proyecto completo con tus precios guardados.",
    before_continue: "Iniciar trabajo puntual",
    before_switch: "Ir al Estimado de proyecto completo",
    smart_title: "Creo que esto parece un proyecto completo de oficio.",
    smart_body: "Normalmente obtendrás un estimado más preciso usando tu flujo de Estimado de proyecto completo con tus precios guardados.",
    smart_switch: "Cambiar al Estimado de proyecto completo (recomendado)",
    smart_continue: "Continuar con Levi de todos modos",
    page_title: "Trabajo a medida con Levi",
    page_subtitle: "Para trabajos a medida que no encajan en tu alcance habitual.",
    back_to_new_estimate: "Volver a Nuevo estimado",
    // V-P2 · Trabajo a medida con Levi hero (aprobado 2026-08-22)
    hero_eyebrow: "TRABAJO A MEDIDA CON LEVI",
    hero_headline: "¿Un trabajo fuera de tu alcance habitual?",
    hero_body: "A veces un cliente te pide un trabajo que no forma parte de tu día a día. Muéstrale a Levi el trabajo, cuéntale lo que estás viendo, y él te ayuda a organizar el alcance y te da una recomendación para que la revises.",
    hero_primary_cta: "Muéstrale el trabajo a Levi",
    hero_talk_cta: "Habla del trabajo",
    hero_photos_cta: "Añadir fotos / planos",
    hero_image_alt: "Levi con gorra JobClose AI y polo azul sosteniendo una videocámara en una obra en construcción.",
    // V-P2 · Explicación de tres pasos (reemplaza el bloque anterior de cuatro tarjetas)
    how_overline: "Cómo funciona",
    how_step1_title: "Muéstrale el trabajo a Levi",
    how_step1_body: "Usa Vision, fotos o planos para mostrarle a Levi lo que estás viendo.",
    how_step2_title: "Habla del trabajo",
    how_step2_body: "Cuéntale a Levi qué hay que hacer, las medidas que conoces, las limitaciones o cualquier otra cosa que importe.",
    how_step3_title: "Revisa la recomendación de Levi",
    how_step3_body: "Levi organiza la información y te da una recomendación. Revísala, ajústala o pídele a Levi que la revise.",
    how_footer: "Levi recomienda. Tú decides.",
    // V-P2 · Panel de seguridad para revisión del contratista
    privacy_message: "Solo tú ves la recomendación de Levi. Nada se envía a tu cliente hasta que termines el flujo normal del estimado.",
    step1_heading: "Muéstrale a Levi el trabajo.",
    step1_help: "Para trabajos a medida que no encajan en tu alcance habitual, dale a Levi todo lo que tengas. Usa Vision, agrega fotos o planos, graba una nota de voz o describe el trabajo abajo.",
    vision_cta_title: "Activa Vision y camina el trabajo con Levi",
    vision_cta_body: "Recorre el trabajo en tiempo real. Cuéntale a Levi lo que ves y dale tus medidas. Vision usa la cámara de tu dispositivo — pediremos permiso si es necesario.",
    vision_cta_button: "Activar Vision",
    vision_hide: "Ocultar Vision",
    vision_preparing: "Preparando tu sesión de Trabajo a medida…",
    vision_continuity_hint: "Todo lo que capturas aquí se queda con este trabajo. Si aceptas la recomendación de Levi, el recorrido pasa automáticamente al proyecto resultante.",
    recommended_range_label: "Rango recomendado por Levi",
    suggested_working_price_label: "Precio de trabajo sugerido",
    recommendation_hint: "Esto es una recomendación de Levi, no tus precios guardados. Revísalo y edítalo antes de usarlo.",
    drivers_label: "Lo que Levi consideró",
    needs_more_info_overline: "Levi necesita una cosa más",
    needs_more_info_generic: "¿Puedes contarme un poco más sobre este trabajo?",
    needs_more_info_hint: "Responde arriba (o en Revisar) y Levi correrá la recomendación de nuevo.",
    cannot_price_overline: "No es suficiente para dar un precio responsable",
    cannot_price_headline: "Levi puede ayudarte a definir el alcance — no tiene información confiable suficiente para recomendar un precio todavía.",
    cannot_price_body: "Agrega un par de fotos más, una nota de voz, o escribe una descripción con medidas, y prueba de nuevo.",
    section_photos: "Fotos y planos",
    section_photos_hint: "JPEG · PNG · WEBP · PDF · hasta 12 MB cada uno.",
    add_photos_btn: "Añadir fotos / planos",
    delete_photo: "Quitar foto",
    section_voice: "Notas de voz",
    section_voice_hint: "Audio grabado o subido. Whisper transcribe automáticamente.",
    record_btn: "Grabar descripción",
    stop_btn: "Detener",
    upload_audio_btn: "Subir audio",
    section_description: "Descripción",
    description_placeholder: "Describe el trabajo con palabras sencillas — qué hay que hacer, tiempos, cualquier limitación…",
    section_followup: "Levi preguntó",
    followup_answer_placeholder: "Escribe tu respuesta a la pregunta de Levi…",
    save_answer: "Guardar respuesta",
    analyze_btn: "Pídele la recomendación a Levi",
    analyzing: "Levi está pensando…",
    analyze_disabled_hint: "Muéstrale a Levi el trabajo para comenzar.",
    recommendation_heading: "Esto es lo que te recomendaría según lo que me mostraste.",
    recommendation_note: "Este es un estimado inicial recomendado por IA. Revísalo y ajústalo antes de enviarlo al cliente.",
    job_type_label: "Tipo de trabajo",
    scope_summary_label: "Alcance propuesto",
    explanation_label: "Por qué Levi eligió este precio",
    action_use: "Usar estimado",
    action_edit_price: "Editar precio",
    action_revise: "Pedirle a Levi que revise",
    action_cancel: "Cancelar",
    edit_price_title: "Editar el precio del estimado",
    edit_price_body: "Puedes ingresar cualquier monto. La sugerencia de Levi es un punto de partida — no un precio final.",
    edit_price_input_label: "Nuevo monto (USD)",
    edit_price_confirm: "Guardar precio",
    edit_price_dismiss: "Conservar el precio de Levi",
    revise_title: "Pedirle a Levi que revise",
    revise_body: "Dile a Levi qué está mal o qué cambiar y volverá a analizar con tu nota.",
    revise_placeholder: 'p. ej. "El árbol es mucho más grande, 60 pies no 30." o "Incluye el retiro de escombros."',
    revise_confirm: "Enviar a Levi",
    revise_dismiss: "Mejor no",
    cancel_title: "¿Descartar esta sesión con Levi?",
    cancel_body: "Se eliminarán las fotos, notas de voz y la recomendación. Esto no se puede deshacer.",
    cancel_confirm: "Descartar sesión",
    cancel_dismiss: "Seguir trabajando",
    use_dialog_title: "Crear estimado en borrador desde la recomendación de Levi",
    use_dialog_body: "Ingresa la información del cliente para guardar esto como un estimado en borrador normal. Puedes editar todo después.",
    use_dialog_customer_name: "Nombre del cliente",
    use_dialog_customer_email: "Correo del cliente",
    use_dialog_customer_phone: "Teléfono del cliente",
    use_dialog_job_address: "Dirección del trabajo",
    use_dialog_amount: "Monto del estimado",
    use_dialog_confirm: "Crear estimado en borrador",
    use_dialog_dismiss: "Aún no",
    use_missing_customer: "El nombre del cliente es obligatorio.",
    toast_photo_uploaded: "Foto agregada.",
    toast_photo_failed: "No se pudo subir la foto.",
    toast_photo_deleted: "Foto quitada.",
    toast_voice_uploaded: "Nota de voz agregada — transcribiendo…",
    toast_voice_failed: "No se pudo subir la nota de voz.",
    toast_answer_saved: "Respuesta guardada.",
    toast_analyze_failed: "Levi no pudo generar una recomendación.",
    toast_used: "Estimado en borrador creado.",
    toast_use_failed: "No se pudo crear el estimado en borrador.",
    toast_cancelled: "Sesión de Levi descartada.",
    voice_status_transcribing: "Transcribiendo…",
    voice_status_transcribed: "Transcrito",
    voice_status_failed: "Falló",
    voice_status_uploaded: "Subido",
    empty_state_photos: "Aún no hay fotos.",
    empty_state_voice: "Aún no hay notas de voz.",
    internal_note: "Solo tú ves esta recomendación. No se envía nada al cliente hasta que termines el flujo normal del estimado.",
    // Vision Panel · Slice 2E3 — Spanish parallel of the EN vision panel
    // copy. Natural contractor-to-contractor voice; not a literal
    // translation. Backend keys (what_i_see / possible_concerns /
    // questions_before_pricing / often_forgotten) are NOT renamed.
    vision_panel_title: "Lo que piensa Levi de este trabajo",
    vision_panel_intro: "Así estoy leyendo este trabajo — rápido, honesto, de contratista a contratista.",
    vision_panel_what_i_see_heading: "Lo Que Veo",
    vision_panel_what_i_see_help: "Esto es lo que me llamó la atención.",
    vision_panel_what_i_see_empty: "Todavía no tengo suficiente información visible en la foto para confirmar nada.",
    vision_panel_possible_concerns_heading: "Lo Que Yo Confirmaría",
    vision_panel_possible_concerns_help: "Hay algunas cosas que podrían necesitar una revisión más de cerca.",
    vision_panel_possible_concerns_empty: "No se identificaron preocupaciones adicionales con la información disponible.",
    vision_panel_questions_heading: "Preguntas Antes de Poner Precio",
    vision_panel_questions_help: "Esto es lo que yo confirmaría antes de poner un número.",
    vision_panel_questions_empty: "No se generaron preguntas adicionales.",
    vision_panel_forgotten_heading: "No Olvides",
    vision_panel_forgotten_help: "Detalles que son fáciles de pasar por alto al preparar el alcance.",
    vision_panel_forgotten_empty: "No se generaron recordatorios adicionales.",
    vision_panel_loading_title: "Levi está revisando el trabajo…",
    vision_panel_loading_body: "Dale un momento para mirar las fotos y las notas.",
    vision_panel_error_title: "Levi no pudo revisar este trabajo en este momento.",
    vision_panel_error_body: "Tus fotos y los detalles del trabajo siguen aquí. Inténtalo de nuevo cuando estés listo.",
    // Reusable Levi Working Environment — Spanish parallel of the EN
    // presentation copy. Natural contractor-to-contractor voice; not
    // a literal translation.
    working_env_identity_title: "Levi, tu compañero en JobClose AI",
    working_env_visual_alt: "Levi, tu compañero en JobClose AI",
    working_env_status_ready: "Levi está listo cuando tú lo estés.",
    working_env_status_listening: "Levi está mirando lo que tienes. Pregúntale cuando estés listo.",
    working_env_status_reviewing: "Levi está revisando el trabajo…",
    working_env_status_needs_information: "Levi necesita una cosa más antes de poner el precio.",
    working_env_status_waiting_for_contractor: "Revisa cómo Levi está leyendo este trabajo antes de decidir.",
    working_env_status_recommendation_ready: "La recomendación de Levi está lista para que la revises.",
    working_env_status_recoverable_error: "Levi no pudo revisar este trabajo en este momento — tus datos siguen aquí.",
  },
  cohort_trade_setup: {
    intro: "Elige un sistema, selecciona cómo cobras normalmente, ingresa tu precio reutilizable y apruébalo. El precio aprobado se usará para futuras estimaciones que coincidan y nunca cambia las anteriores.",
    save: "Guardar",
    save_and_approve: "Guardar y aprobar",
    approved: "Aprobado",
    approved_per_job: "Aprobado · Precio por trabajo",
    not_started: "Sin comenzar",
    in_progress: "Configuración en progreso",
    custom_title: "Sistema personalizado / único",
    custom_placeholder: "Describe el sistema personalizado",
    custom_reusable: "Reutilizable",
    custom_one_off: "Único (solo este trabajo)",
    custom_add: "Agregar sistema personalizado",
    saved_toast: "Guardado",
    approved_toast: "Aprobado como precio reutilizable",
    err_incomplete: "Completa los campos requeridos antes de aprobar.",
    err_not_saved: "Guarda tus cambios antes de aprobar.",
    err_generic: "No se pudo guardar. Inténtalo de nuevo.",
  },
  email_common: {
    recipient_override_error:
      "No se puede cambiar el destinatario — se usa el correo guardado del cliente.",
  },
  schedule: {
    card_overline: "Programa del proyecto",
    add_btn: "Programar trabajo",
    add_assignee: "Agregar nuevo responsable",
    assignee_none: "— Sin asignar —",
    assignee_save: "Guardar",
    assignee_name_placeholder: "p. ej. Cuadrilla de Techos A",
    unassigned: "Sin asignar",
    loading: "Cargando programa…",
    empty_state: "Aún no hay trabajo programado. Agrega el primer elemento para comenzar.",
    next_up: "Próximo",
    upcoming: "Próximos",
    edit: "Editar",
    duplicate: "Duplicar",
    delete: "Eliminar",
    cancel: "Cancelar",
    save_create: "Crear",
    save_edit: "Guardar cambios",
    saving: "Guardando…",
    dialog_create_title: "Programar trabajo",
    dialog_edit_title: "Editar trabajo programado",
    field_title: "Título",
    title_placeholder: "p. ej. Retiro de techo día 1",
    field_assignee: "Responsable",
    field_all_day: "Todo el día",
    field_start: "Inicia",
    field_end: "Termina",
    field_recurrence: "Se repite",
    field_status: "Estado",
    field_reminder_minutes: "Recordatorio (minutos antes)",
    reminder_placeholder: "Dejar en blanco para sin recordatorio",
    field_internal_notes: "Notas internas",
    field_customer_notes: "Notas para el cliente (opcional)",
    customer_notes_placeholder: "Se guardan en el registro — no se envía nada al cliente.",
    delete_title: "¿Eliminar este elemento programado?",
    delete_body: (title) => `Se eliminará "${title || "el elemento programado"}" del programa del proyecto. Esta acción no se puede deshacer.`,
    toast_created: "Elemento programado agregado.",
    toast_updated: "Elemento programado actualizado.",
    toast_deleted: "Elemento programado eliminado.",
    toast_duplicated: "Duplicado.",
    toast_assignee_created: "Responsable agregado.",
    toast_save_failed: "No se pudo guardar — inténtalo de nuevo.",
    toast_delete_failed: "No se pudo eliminar — inténtalo de nuevo.",
    toast_duplicate_failed: "No se pudo duplicar — inténtalo de nuevo.",
    toast_assignee_failed: "No se pudo agregar el responsable — inténtalo de nuevo.",
    conflict_heading: "Atención — esto se cruza con otro trabajo",
    conflict_never_blocks: "Aún puedes guardar. Levi recomienda; tú decides.",
    conflict_unnamed: "Elemento sin título",
    conflict_kind: {
      same_assignee: "el mismo responsable está reservado",
      same_project: "otro elemento en este proyecto",
    },
    status: {
      scheduled: "Programado",
      in_progress: "En curso",
      completed: "Completado",
      canceled: "Cancelado",
    },
    recurrence_rules: {
      none: "No se repite",
      daily: "Diariamente",
      weekly: "Semanalmente",
      monthly: "Mensualmente",
    },
    assignee_kind: {
      self: "Yo",
      individual: "Individuo",
      crew: "Cuadrilla",
      subcontractor: "Subcontratista",
    },
    errors: {
      title_required: "Agrega un título.",
      times_required: "Define una hora de inicio y de fin.",
    },
    // Slice 2L · Calendario de trabajos
    company: {
      page_title: "Calendario de trabajos",
      page_subtitle: "Todo el trabajo programado de tu empresa. Aquí es solo lectura — abre un trabajo para editar su programa.",
      view_month: "Mes",
      view_agenda: "Agenda",
      today: "Hoy",
      prev: "Anterior",
      next: "Siguiente",
      loading: "Cargando tu calendario…",
      error: "No se pudo cargar el calendario — intenta de nuevo.",
      retry: "Reintentar",
      empty_month: "Aún no hay nada programado este mes.",
      empty_agenda: "Nada programado en los próximos 30 días.",
      empty_hint: "Abre un proyecto y usa su tarjeta de Programa para agregar trabajo programado.",
      show_canceled: "Mostrar cancelados",
      hide_canceled: "Ocultar cancelados",
      more_events: (n) => `+${n} más`,
      overlap_badge: "Conflicto",
      overlap_tooltip: "La misma cuadrilla está reservada en otro trabajo a esta hora.",
      open_project: "Abrir proyecto",
      customer_untitled: "Proyecto sin título",
      unassigned: "Sin asignar",
      weekday_short: {
        sun: "Dom", mon: "Lun", tue: "Mar", wed: "Mié",
        thu: "Jue", fri: "Vie", sat: "Sáb",
      },
      section_today: "Hoy",
      section_this_week: "Esta semana",
      section_upcoming: "Más adelante",
      count_today: (n) => `${n} hoy`,
      count_this_week: (n) => `${n} esta semana`,
      filter_assignee_label: "Filtrar por responsable",
      filter_assignee_all: "Todos los asignados",
      filter_status_label: "Filtrar por estado",
      filter_status_all: "Todos los estados",
      empty_filtered: "No hay trabajo programado que coincida con estos filtros.",
      levi: {
        section_overline: "Levi",
        section_title: "Lo que Levi ve en tu calendario",
        section_subtitle: "Todo lo de abajo viene del programa que ya tienes registrado. Levi nunca inventa fechas.",
        empty_state: "Todavía no hay nada en el calendario. Abre un proyecto para agregar trabajo programado.",
        today_count: (n) => `Tienes ${n} elemento${n === 1 ? "" : "s"} programado${n === 1 ? "" : "s"} en el calendario de hoy.`,
        today_empty: "No hay nada en el calendario de hoy.",
        this_week_count: (n) => `${n} elemento${n === 1 ? "" : "s"} programado${n === 1 ? "" : "s"} en los próximos 7 días.`,
        conflict_pairs: (n) => `${n} conflicto${n === 1 ? "" : "s"} de cuadrilla — el mismo responsable está reservado dos veces.`,
        overdue_in_progress: (n) => `${n} trabajo${n === 1 ? "" : "s"} en curso ${n === 1 ? "está" : "están"} pasado${n === 1 ? "" : "s"} de la fecha programada de fin.`,
        next_visit_row: (customer, when) => `La próxima visita para ${customer} es ${when}.`,
      },
    },
  },
  crew_packet: {
    title_fallback: "Paquete para la cuadrilla",
    subtitle_fallback: "Vista en vivo del proyecto",
    live_banner: "Este paquete siempre refleja el proyecto actual. Los cambios al proyecto aparecen aquí de inmediato — no se guarda una copia estática.",
    print: "Imprimir",
    refresh: "Actualizar",
    back: "Volver al proyecto",
    loading: "Cargando paquete…",
    error: "No se pudo cargar el paquete — intenta de nuevo.",
    no_address: "Sin dirección registrada",
    job_name_prefix: "Trabajo:",
    untitled_event: "Trabajo programado",
    untitled_line: "Línea de alcance sin título",
    untitled_area: "Área de trabajo sin título",
    not_scheduled: "Sin programar",
    unassigned: "Sin asignar",
    section_job: "Trabajo",
    section_schedule: "Programa",
    section_scope: "Alcance organizado",
    section_work_areas: "Áreas de trabajo",
    section_measurements: "Mediciones",
    section_photos: "Fotos",
    section_safety: "Notas de seguridad",
    section_materials: "Notas de materiales",
    section_labor: "Notas de mano de obra",
    section_internal: "Notas internas de la cuadrilla",
    scope_empty: "Aún no hay alcance organizado. Agrega un estimado para poblar la lista.",
    work_areas_empty: "Aún no hay áreas de trabajo aprobadas.",
    measurements_empty: "Aún no se capturaron mediciones.",
    photos_empty: "Aún no hay fotos adjuntas a este proyecto.",
    safety_empty: "No se capturaron notas de seguridad.",
    materials_empty: "No se capturaron notas de materiales.",
    labor_empty: "No se capturaron notas de mano de obra.",
    internal_empty: "No se capturaron notas internas de la cuadrilla.",
    urgency: {
      emergency: "Emergencia", urgent: "Urgente",
      normal: "Normal", planned: "Planificado",
    },
    status: {
      scheduled: "Programado", in_progress: "En curso",
      completed: "Completado", canceled: "Cancelado",
    },
    assignee_kind: {
      self: "Yo", individual: "Individuo",
      crew: "Cuadrilla", subcontractor: "Subcontratista",
    },
    area_type: {
      interior_room: "Habitación interior", exterior_area: "Área exterior",
      roof_section: "Sección de techo", elevation: "Elevación",
      commercial_space: "Espacio comercial", mechanical_area: "Área mecánica",
      linear_run: "Tramo lineal", object: "Objeto", custom: "Personalizado",
    },
  },
  material_takeoff: {
    card_overline: "Cálculo de materiales",
    open_new: "Iniciar cálculo",
    open_draft: "Abrir borrador",
    open_approved: "Ver aprobado",
    loading: "Cargando…",
    page_title: "Cálculo de materiales",
    page_subtitle: "Levi recomienda. Tú decides.",
    back: "Volver al proyecto",
    picker_title: "¿Cómo quieres construir este cálculo?",
    picker_intro: "Elige el punto de partida. Levi usa la información del proyecto disponible y nunca te bloquea si el catálogo de precios está incompleto.",
    editor_overline: "Borrador de cálculo",
    items_heading: "Materiales",
    items_empty: "Aún no hay materiales en este cálculo. Agrega uno para comenzar.",
    guidance_from_project: {
      heading: "Levi aún no pudo generar un plan de materiales desde este proyecto.",
      body: "Para construir desde tu proyecto, Levi necesita alcance de estimado organizado o líneas de estimado con precio. Cuando existan, Construir desde el proyecto generará un plan completo. Mientras tanto puedes elegir una de las opciones de abajo.",
      action_continue_estimate: "Continuar con el estimado",
      action_build_from_measurements: "Construir desde medidas",
      action_start_manual: "Iniciar cálculo manual",
    },
    add_item: "Agregar línea de material",
    remove: "Quitar",
    save_draft: "Guardar borrador",
    run_double_check: "Ejecutar Doble Verificación",
    approve_takeoff: "Aprobar cálculo",
    revise_takeoff: "Revisar cálculo",
    cancel: "Cancelar",
    field_name: "Etiqueta de línea",
    field_product: "Producto / descripción",
    field_qty: "Cantidad",
    field_waste_pct: "Desperdicio %",
    field_contingency_pct: "Contingencia %",
    field_unit_price: "Precio unitario ($)",
    calc_required_waste: "Desperdicio requerido",
    calc_contingency: "Contingencia",
    calc_surplus: "Excedente recuperable",
    calc_total: "Total a comprar",
    calc_line_total: "Total de línea:",
    price_missing_help: "Precio faltante — Levi conservó el plan de cantidades pero el total no se calculó.",
    alternatives_toggle: (n) => `${n} alternativa${n === 1 ? "" : "s"} a considerar`,
    alternative: "Alternativa",
    dc_heading: "Hallazgos de Doble Verificación",
    dc_never_mutates: "Solo consultivo — el cálculo nunca se modifica.",
    measurements_prompt_title: "Ingresa la medida sobre la que Levi debe planificar",
    measurements_area_label: "Área (sq ft)",
    measurements_trade_label: "Oficio",
    measurements_system_label: "Sistema / familia de producto",
    measurements_submit: "Continuar",
    state: {
      none: "Sin cálculo aún",
      draft: "Borrador de cálculo",
      approved: "Cálculo aprobado",
      draft_revision: "Borrador de revisión",
    },
    state_desc: {
      none: "Levi puede iniciar un nuevo plan de materiales desde tu proyecto, tus medidas o una plantilla vacía.",
      draft: "Revisa el plan, ejecuta la Doble Verificación y aprueba cuando esté listo.",
      approved: "Este cálculo está bloqueado. Inicia una revisión si necesita cambios.",
      draft_revision: "Una revisión está en curso. Apruébala para bloquear una nueva versión.",
    },
    status: {
      draft: "Borrador",
      approved: "Aprobado",
    },
    entry: {
      from_project: {
        title: "Construir desde el proyecto",
        body: "Usa el alcance aprobado, áreas de trabajo, medidas y tu catálogo de precios.",
      },
      from_measurements: {
        title: "Construir desde medidas",
        body: "Comienza desde una medida — Levi sugerirá materiales.",
      },
      manual: {
        title: "Manual · Ayúdame a construir",
        body: "Ingresa lo que sabes. Levi puede ayudar a completar y optimizar el plan.",
      },
    },
    trade: {
      roofing: "Techado", drywall: "Tabla roca",
      siding: "Revestimiento", flooring: "Pisos",
    },
    source: {
      price_book: "De tu catálogo de precios",
      project_scope: "Del alcance del proyecto",
      levi_suggestion: "Sugerencia de Levi",
      contractor_added: "Agregado por el contratista",
      price_missing: "Precio faltante",
    },
    toast_created: "Borrador de cálculo creado.",
    toast_saved: "Borrador guardado.",
    toast_dc_ran: (n) => `Doble Verificación finalizada. ${n} hallazgo${n === 1 ? "" : "s"}.`,
    toast_approved: "Cálculo aprobado.",
    toast_revised: "Borrador de revisión creado.",
    toast_create_failed: "No se pudo crear el cálculo — inténtalo de nuevo.",
    toast_save_failed: "No se pudo guardar el borrador — inténtalo de nuevo.",
    toast_dc_failed: "No se pudo ejecutar la Doble Verificación — inténtalo de nuevo.",
    toast_approve_failed: "No se pudo aprobar — inténtalo de nuevo.",
    toast_revise_failed: "No se pudo iniciar la revisión — inténtalo de nuevo.",
  },
  profitability: {
    // Slice 2N · Inteligencia de rentabilidad del trabajo (PASE 2).
    card_overline: "Rentabilidad",
    page_overline: "Rentabilidad",
    page_title: "Rentabilidad",
    back: "Volver al proyecto",
    loading: "Cargando…",
    saving: "Guardando…",
    starting: "Iniciando…",
    cancel: "Cancelar",
    value_unavailable: "Aún no disponible",
    value_no_estimate: "Necesita un estimado vinculado al contrato",
    hint: {
      needs_estimate:
        "Agrega o vincula un estimado para establecer la base de ingresos.",
      enter_expected_margin_below:
        "Ingresa un margen esperado abajo.",
      profit_reason: {
        no_confirmed_actual_costs:
          "Confirma al menos un costo real antes de calcular la ganancia real.",
        no_supported_revenue:
          "La base de ingresos aún no está soportada.",
      },
    },
    open_start: "Iniciar seguimiento",
    open_workspace: "Abrir espacio",
    strip_revenue: "Contrato revisado",
    strip_expected_profit: "Ganancia esperada",
    strip_actual_profit: "Ganancia real",
    first_use_overline: "Control opcional del trabajo",
    first_use_title: "¿Rastrear la rentabilidad de este trabajo?",
    first_use_body:
      "El seguimiento de rentabilidad es opcional. Levi guarda tu margen esperado, registra los costos que ingresas y muestra cómo terminó realmente el trabajo. Nunca cambia tu estimado, contrato o facturas. Tú decides cuándo iniciarlo y confirmarlo.",
    start_tracking: "Iniciar seguimiento de rentabilidad",
    missing_data_heading: "Algunas cosas para tener en cuenta",
    missing_flag: {
      no_contract_linked_estimate:
        "No se encontró un estimado vinculado al contrato — la base de ingresos es limitada.",
      no_expected_margin: "Aún no se ha ingresado el margen esperado.",
      unconfirmed_cost_entries:
        "Algunos costos siguen sin confirmar y no cuentan en el total real confirmado.",
      no_cost_entries_recorded: "Aún no se han registrado costos.",
      no_confirmed_actual_costs:
        "Confirma al menos un costo real antes de que la ganancia real sea un resultado soportado.",
    },
    state: {
      not_started: "Sin iniciar",
      projected_only: "Solo proyectado",
      partially_tracked: "Parcialmente rastreado",
      actuals_in_progress: "Costos reales en curso",
      actuals_confirmed: "Rentabilidad real confirmada",
    },
    state_desc: {
      not_started:
        "El seguimiento de rentabilidad no ha iniciado. Abre el espacio para ingresar un margen esperado.",
      projected_only:
        "Margen esperado registrado. Aún no se han ingresado costos reales.",
      partially_tracked:
        "Hay algunos costos registrados pero ninguno está confirmado.",
      actuals_in_progress:
        "Los costos reales confirmados están entrando. Sigue registrando costos hasta cerrar el trabajo.",
      actuals_confirmed:
        "Confirmaste la rentabilidad final. Las correcciones posteriores se rastrean por separado.",
    },
    section: {
      overview_overline: "Resumen",
      overview_title: "Vista general",
      revenue_overline: "Ingresos",
      revenue_title: "Ingresos",
      costs_overline: "Costos",
      costs_title: "Costos",
      entries_overline: "Registros de costo",
      entries_title: "Registros de costo y documentos de soporte",
      entries_subtitle:
        "Agrega costos a medida que los tengas. Confirma cada entrada para que cuente en tu ganancia real. Las entradas excluidas siguen visibles pero no cuentan.",
      levi_overline: "Revisión de Levi",
      levi_title: "Revisión de Levi",
      levi_subtitle:
        "Levi solo lee los números que has registrado. No inventa totales, no cambia tus entradas y no confirma la rentabilidad por ti.",
      improve_overline: "Mejora el siguiente trabajo",
      improve_title: "Mejora el siguiente trabajo",
      improve_subtitle:
        "Aprendizajes prácticos de este trabajo. Nada aquí cambia tus preferencias de precios ni estimados futuros.",
      margin_overline: "Margen esperado",
      margin_title: "Tu margen esperado",
      margin_subtitle:
        "Ingresa el porcentaje de margen que esperabas para este trabajo. Levi lo usa para proyectar la ganancia esperada y el costo implícito. Puedes actualizarlo cuando quieras.",
      confirm_overline: "Confirmación final",
      confirm_title: "Confirmar rentabilidad real",
      confirm_subtitle:
        "Confirmar congela la instantánea actual para tus registros. Las correcciones posteriores mantienen intacta la instantánea histórica.",
    },
    ov: {
      original_contract: "Contrato original",
      approved_changes: "Cambios aprobados",
      approved_changes_unsupported: "Aún no rastreados en este repositorio",
      revised_contract: "Contrato revisado",
      expected_margin: "Margen esperado",
      expected_margin_hint: "Ingresa un margen esperado abajo",
      expected_profit: "Ganancia esperada",
      implied_cost: "Costo esperado implícito",
      committed_total: "Costos comprometidos",
      actual_confirmed: "Costos reales confirmados",
      actual_profit: "Ganancia real",
      actual_margin: "Margen real",
      remaining_vs_confirmed: "Restante vs confirmado",
    },
    rev: {
      original_contract: "Contrato original",
      approved_changes: "Cambios aprobados",
      revised_contract: "Contrato revisado",
      invoiced: "Facturado",
      collected: "Cobrado",
      remaining_receivable: "Por cobrar",
      source_label: "Base de ingresos",
      source: {
        contract_linked_estimate: "Estimado vinculado al contrato",
        estimate_direct: "Estimado más reciente del proyecto usado como respaldo",
        none: "No se encontró un estimado compatible",
      },
      source_fallback_note:
        "aún no hay contrato firmado; usando el estimado más reciente del proyecto.",
      approved_changes_unsupported_title:
        "Los cambios aprobados aún no se rastrean",
      approved_changes_unsupported_body:
        "Este repositorio todavía no tiene una forma confiable de separar las órdenes de cambio aprobadas de otras propuestas aprobadas. En lugar de adivinar, mostramos el contrato original como base de ingresos conocida y dejamos el seguimiento de órdenes de cambio para una actualización autorizada posterior.",
    },
    costs: {
      committed_total: "Comprometido",
      actual_confirmed: "Real confirmado",
      unconfirmed_count: "Entradas sin confirmar",
      excluded_count: "Entradas excluidas",
      category_col: "Categoría",
      total_col: "Total",
      unconfirmed_hint:
        "Hay entradas de costo sin confirmar que no se incluyen en el total real confirmado. Confírmalas cuando estés listo.",
    },
    category: {
      materials: "Materiales",
      labor: "Mano de obra",
      subcontractors: "Subcontratistas",
      equipment_rentals: "Equipo y rentas",
      disposal: "Desecho / basura",
      permits_fees: "Permisos y trámites",
      delivery_travel: "Entregas y viajes al trabajo",
      other_direct: "Otros costos directos del trabajo",
    },
    classification: {
      actual: "Real",
      committed: "Comprometido",
    },
    entry: {
      add_manual: "Agregar costo manual",
      add_document: "Agregar desde documento",
      empty: "Aún no hay costos registrados.",
      form_title_add: "Agregar registro de costo",
      form_title_edit: "Editar registro de costo",
      form_title_correction: "Corregir entrada confirmada",
      field_amount: "Monto ($)",
      field_category: "Categoría",
      field_classification: "Real o Comprometido",
      field_vendor: "Proveedor / pagador",
      field_incurred_date: "Fecha del gasto",
      field_description: "Descripción",
      field_correction_reason: "Motivo de la corrección",
      correction_placeholder: "p. ej. crédito del proveedor",
      save_add: "Guardar registro",
      save_edit: "Guardar cambios",
      confirm: "Confirmar",
      correct: "Corregir",
      edit: "Editar",
      exclude: "Excluir",
      restore: "Restaurar",
      linked_document: "Documento vinculado",
      source_document_suggestion: "Sugerencia de documento",
      confirmed_badge: "Confirmado",
      unconfirmed_badge: "Sin confirmar",
      excluded_badge: "Excluido",
      included_badge: "Incluido",
      corrected_badge: "Corregido",
      source_manual: "Manual",
    },
    doc: {
      form_title: "Agregar sugerencia de costo desde documento",
      foundation_note:
        "Base del PASE 1: esto crea una sugerencia sin confirmar vinculada a un documento del proyecto. Levi aún no extrae valores del documento. Ingresa lo que sepas y confirma cuando estés listo.",
      field_document_id: "ID del documento",
      field_document_id_placeholder:
        "Pega el ID del documento desde los archivos del proyecto",
      save: "Guardar sugerencia",
    },
    margin: {
      pct_label: "Margen esperado (%)",
      source_label: "Origen de la suposición (opcional)",
      source_placeholder: "p. ej. margen típico de techado, trabajo similar anterior",
      save: "Guardar margen esperado",
      derived_note_prefix: "Levi proyecta",
      derived_note: (profit, cost) =>
        `${profit} de ganancia esperada y ${cost} de costo esperado implícito según el margen que ingresaste.`,
    },
    levi: {
      confirmed_state:
        "Confirmaste la rentabilidad de este trabajo. Las correcciones posteriores se rastrean por separado de la instantánea confirmada.",
      in_progress:
        "Los costos reales confirmados están entrando. Levi está observando cómo el total confirmado avanza hacia el costo esperado implícito.",
      partial:
        "Hay algunos costos registrados pero ninguno está confirmado. Al confirmar cada uno empezarán a contar en la ganancia real.",
      projected_only:
        "Margen esperado registrado pero aún no hay costos reales. Los números proyectados dependen de tu suposición de margen.",
      not_started:
        "El seguimiento de rentabilidad no ha iniciado. Esta sección es opcional y nunca bloquea el resto del proyecto.",
      unconfirmed: (n) =>
        `${n} registro${n === 1 ? "" : "s"} de costo sin confirmar. No se incluyen en el total real confirmado.`,
      no_margin:
        "No hay margen esperado registrado. Ingresa uno para ver la ganancia esperada y el costo esperado implícito.",
      no_revenue:
        "No se encontró un estimado vinculado al contrato. La base de ingresos es limitada hasta que se vincule un estimado.",
      approved_changes_unsupported:
        "Los montos de cambios aprobados todavía no se rastrean en este repositorio. El contrato original sigue siendo la base de ingresos conocida.",
      margin_below: (pct) =>
        `El margen real va ${pct} puntos porcentuales por debajo de tu margen esperado hasta ahora.`,
      margin_above: (pct) =>
        `El margen real va ${pct} puntos porcentuales por encima de tu margen esperado hasta ahora.`,
      promise:
        "Levi nunca cambia tus ingresos, entradas de costo, suposición de margen o estado de confirmación por su cuenta.",
    },
    improve: {
      top_category: (name, amount, pct) =>
        `${name} fue la categoría de costo más grande con ${amount} (${pct} % del total real confirmado).`,
      missing_categories: (list) =>
        `Aún no hay costos registrados en: ${list}. Si alguna de estas aplicó a este trabajo, agregarlas hará que el próximo presupuesto similar sea más exacto.`,
      margin_shortfall: (pct) =>
        `El margen real quedó ${pct} puntos porcentuales por debajo de tu margen esperado. Considera revisar mano de obra, precios de subcontratistas o suposiciones de desperdicio antes de cotizar un trabajo similar.`,
      margin_surplus: (pct) =>
        `El margen real quedó ${pct} puntos porcentuales por encima de tu margen esperado. Revisa si esto refleja un ahorro puntual o un verdadero espacio de precio que puedes aprovechar.`,
      confirm_before_review:
        "Confirma las entradas de costo restantes antes de tratar estos aprendizajes como definitivos.",
      not_yet:
        "Aún no hay suficientes datos confirmados. Cuando hayas registrado y confirmado algunos costos, Levi mostrará aprendizajes prácticos aquí.",
      contractor_control_note:
        "Tú decides si algo de esto influye en tus precios futuros. Nada aquí actualiza tu catálogo de materiales ni tu perfil de precios automáticamente.",
    },
    confirm: {
      warnings_heading: "Antes de confirmar, ten en cuenta lo siguiente",
      action: "Confirmar rentabilidad real",
      confirming: "Confirmando…",
      needs_tracking: "Inicia el seguimiento primero y luego confirma.",
      needs_supported_actual_profit:
        "La ganancia real aún no está soportada. Confirma al menos un costo real incluido con una base de ingresos válida antes de poder confirmar la rentabilidad final.",
      confirmed_state: "Rentabilidad real confirmada.",
      confirmed_at: "Confirmado",
      snapshot_note:
        "Se guardó una instantánea de este momento en tus registros. Las correcciones posteriores se rastrean por separado.",
    },
    toast: {
      load_failed:
        "No se pudieron cargar los datos de rentabilidad — inténtalo de nuevo.",
      tracking_started: "Seguimiento de rentabilidad iniciado.",
      tracking_start_failed:
        "No se pudo iniciar el seguimiento — inténtalo de nuevo.",
      margin_saved: "Margen esperado guardado.",
      margin_save_failed:
        "No se pudo guardar el margen esperado — inténtalo de nuevo.",
      invalid_margin:
        "Ingresa un margen entre 0 y menos de 100 por ciento.",
      invalid_amount: "Ingresa un monto válido.",
      entry_added: "Registro de costo agregado.",
      entry_add_failed:
        "No se pudo agregar el registro de costo — inténtalo de nuevo.",
      entry_saved: "Registro de costo guardado.",
      entry_save_failed:
        "No se pudo guardar el registro de costo — inténtalo de nuevo.",
      needs_correction_flag:
        "Esta entrada está confirmada. Usa Corregir para registrar una corrección controlada.",
      entry_confirmed: "Registro de costo confirmado.",
      entry_confirm_failed:
        "No se pudo confirmar el registro — inténtalo de nuevo.",
      entry_excluded: "Registro de costo excluido.",
      entry_exclude_failed:
        "No se pudo excluir el registro — inténtalo de nuevo.",
      entry_restored: "Registro de costo restaurado.",
      entry_restore_failed:
        "No se pudo restaurar el registro — inténtalo de nuevo.",
      missing_document: "Ingresa el ID del documento fuente.",
      invalid_document:
        "Ese documento no está vinculado a este proyecto o no está accesible.",
      suggestion_created:
        "Sugerencia vinculada al documento creada (sin confirmar).",
      suggestion_failed:
        "No se pudo crear la sugerencia del documento — inténtalo de nuevo.",
      profitability_confirmed: "Rentabilidad real confirmada.",
      tracking_not_started: "Inicia el seguimiento antes de confirmar.",
      no_supported_actual_profit:
        "Confirma al menos un costo real incluido antes de poder confirmar la rentabilidad final.",
      confirm_failed:
        "No se pudo confirmar la rentabilidad — inténtalo de nuevo.",
    },
  },
  // Slice 2P1 Pass B · controles de intercambio para el cliente.
  sharing_card: {
    heading: "Compartir con el cliente",
    subheading: "Tú decides cuándo el cliente ve este documento.",
    state_label: {
      private: "Privado",
      approved: "Listo para compartir",
      shared: "Compartido",
      revoked: "Acceso revocado",
      expired: "Vencido",
    },
    levi_intro: "Nota de Levi:",
    levi: {
      private: "Aún no se ha compartido nada con el cliente.",
      approved: "Este documento está listo. Tú decides cuándo enviarlo.",
      shared:
        "El cliente puede abrir este documento con el enlace activo.",
      revoked: "El enlace anterior del cliente ya no funciona.",
      expired:
        "El enlace del cliente ha vencido. Regénéralo para compartirlo nuevamente.",
    },
    meta: {
      expires_prefix: "Vence",
      expires_none: "Sin fecha de vencimiento",
      viewed_yes: "El cliente abrió el enlace",
      viewed_no: "El cliente aún no ha abierto el enlace",
      signed_yes: "Contrato firmado por el cliente",
      signed_no: "Aún sin firmar",
    },
    action: {
      approve: "Listo para compartir",
      approve_and_email: "Compartir con el cliente · Enviar enlace",
      copy: "Copiar enlace",
      email: "Enviar enlace",
      regenerate: "Regenerar enlace",
      revoke: "Revocar acceso",
      share_again: "Compartir de nuevo",
    },
    action_busy: {
      approve: "Aprobando…",
      copy: "Copiando…",
      email: "Enviando…",
      regenerate: "Regenerando…",
      revoke: "Revocando…",
      share_again: "Restaurando…",
    },
    confirm: {
      approve_email_title: "¿Compartir este documento con el cliente?",
      approve_email_body:
        "Se aprobará el documento para acceso del cliente y se enviará el enlace a {email}.",
      regenerate_title: "¿Regenerar el enlace del cliente?",
      regenerate_body:
        "El enlace anterior del cliente dejará de funcionar de inmediato. Se emitirá uno nuevo.",
      revoke_title: "¿Revocar el acceso del cliente?",
      revoke_body:
        "El enlace actual del cliente dejará de funcionar de inmediato. Podrás compartir de nuevo más tarde.",
      share_again_title: "¿Compartir este documento nuevamente?",
      share_again_body:
        "Se aprobará el documento y se emitirá un enlace nuevo para el cliente.",
      cancel: "Cancelar",
      confirm: "Confirmar",
      email_prompt_label: "Enviar enlace a",
    },
    toast: {
      approved: "Marcado como listo para compartir.",
      approve_failed:
        "No se pudo marcar como listo — inténtalo de nuevo.",
      copied: "Enlace del cliente copiado.",
      copy_failed:
        "No se pudo copiar el enlace — cópialo manualmente abajo.",
      emailed: "Enlace enviado a {email}.",
      emailed_mock:
        "Enlace en cola en modo mock — {email}.",
      email_failed:
        "No se pudo enviar el correo. No se envió nada.",
      regenerated: "Nuevo enlace del cliente emitido.",
      regenerate_failed:
        "No se pudo regenerar el enlace — inténtalo de nuevo.",
      revoked: "Acceso del cliente revocado.",
      revoke_failed:
        "No se pudo revocar el acceso — inténtalo de nuevo.",
      share_again_done:
        "El documento está listo para compartirse nuevamente.",
    },
    history: {
      heading: "Actividad reciente",
      empty: "Aún no hay actividad de compartir.",
      event: {
        approved: "Listo para compartir",
        shared: "Compartido",
        regenerated: "Enlace regenerado",
        link_sent: "Enviado por correo",
        first_viewed: "El cliente abrió el enlace",
        signed: "Contrato firmado",
        revoked: "Acceso revocado",
      },
    },
    preference: {
      no_customer_access:
        "El acceso del cliente está deshabilitado para este trabajo. Los controles de compartir no están disponibles.",
      email_only:
        "El correo es el método preferido de entrega para este cliente.",
      sms_only:
        "El envío por SMS aún no está disponible — usa el correo o copia el enlace.",
      portal_and_email:
        "Este cliente prefiere el correo. Solo están disponibles las herramientas de enlace abajo.",
    },
    load_error:
      "No se pudo cargar el estado de compartir. Actualiza la página.",
  },
  // Slice 2P1 Pass C · Closeout público + guía de Levi al cliente.
  public_closeout: {
    overline: "Cierre del proyecto",
    title: "Cierre de tu proyecto",
    jobsite: "Sitio del trabajo",
    project_type: "Tipo de proyecto",
    completion_date: "Fecha de finalización",
    invoice_status: "Estado de la factura",
    work_summary: "Qué se completó",
    completion_notes: "Notas de tu contratista",
    scope: "Trabajo entregado",
    workmanship_warranty: "Garantía de mano de obra",
    manufacturer_warranty: "Garantía del fabricante",
    maintenance_notes: "Cuidado y mantenimiento",
    warranty_contact: "Contacto de garantía y servicio",
    review_request: "Un mensaje de tu contratista",
    referral_request: "Referidos",
    final_punch_list: "Lista final de detalles",
    footer_note:
      "Este cierre del proyecto fue compartido contigo por tu contratista. Comunícate directamente con tu contratista si tienes preguntas.",
  },
  levi_customer_guide: {
    welcome:
      "Hola, soy Levi. Tu contratista ha compartido contigo el cierre de este proyecto.",
    framing:
      "Solo estás viendo lo que tu contratista decidió compartir. Los costos internos, las notas y los borradores no están incluidos.",
    contact:
      "Si tienes preguntas, comunícate directamente con tu contratista.",
  },
  email_closeout: {
    subject_prefix: "El cierre de tu proyecto está listo",
    cta: "Ver cierre del proyecto",
  },
  // Slice 2Q · Cierre del proyecto guiado por Levi (para el contratista).
  project_closeout: {
    heading: "Cierre del proyecto",
    subheading: "Levi te ayuda a prepararte. Tú decides cuándo cerrar.",
    state: {
      in_progress: "En progreso",
      needs_attention: "Necesita atención",
      ready_to_close: "Listo para cerrar",
      closed: "Cerrado",
    },
    levi_intro: "Lectura de Levi sobre este proyecto:",
    levi_disclaimer: "Levi recomienda. El contratista decide.",
    levi: {
      progress:
        "El proyecto sigue en marcha. Vuelve cuando el equipo lo termine.",
      warn:
        "Algunos elementos se ven incompletos. Revisa la lista antes de cerrar.",
      missing:
        "Faltan elementos del cierre. Completarlos ayuda a tu cliente y a tus registros.",
      ready:
        "Todo lo que puedo verificar se ve listo. Cierra cuando estés satisfecho.",
      closed:
        "Este proyecto está cerrado. Los registros históricos siguen disponibles.",
    },
    item: {
      work_summary: "Resumen del trabajo completado",
      final_photos: "Fotos finales",
      punch_list: "Lista de detalles / órdenes de trabajo abiertas",
      workmanship_warranty: "Garantía de mano de obra",
      manufacturer_warranty: "Garantía del fabricante",
      final_invoice: "Factura final / pago",
      closeout_docs: "Documentos de cierre para el cliente",
    },
    status: {
      ok: "Listo", warn: "Necesita revisión",
      missing: "Falta", na: "No aplica",
    },
    action: { close: "Cerrar proyecto", reopen: "Reabrir proyecto" },
    non_blocking_note:
      "Las advertencias no impiden cerrar — se te pedirá confirmación.",
    closed_at_prefix: "Cerrado el",
    confirm: {
      close_title: "¿Cerrar este proyecto?",
      close_body:
        "Los registros internos siguen disponibles. El acceso del cliente continúa controlado por el ciclo de compartir existente. Cerrar no envía ningún mensaje al cliente.",
      warnings_prefix: "Tienes",
      warnings_suffix:
        "elementos de la lista que pueden requerir atención. Aun así puedes cerrar.",
      cancel: "Cancelar",
      close_confirm: "Cerrar proyecto",
      reopen_title: "¿Reabrir este proyecto?",
      reopen_body:
        "Reabrir devuelve el proyecto al estado activo. La información histórica del cierre se conserva.",
      reopen_confirm: "Reabrir proyecto",
    },
    toast: {
      closed: "Proyecto cerrado.",
      close_failed:
        "No se pudo cerrar el proyecto — inténtalo de nuevo.",
      reopened: "Proyecto reabierto.",
      reopen_failed:
        "No se pudo reabrir el proyecto — inténtalo de nuevo.",
    },
  },
  // Slice 2T · Levi 1.0 Project Snapshot (Espacio de trabajo del proyecto).
  levi_project_snapshot: {
    header_kicker: "Levi",
    header: "Esto es lo que veo en este proyecto.",
    section_recommends: "Levi recomienda",
    section_also_noticed: "También noté",
    section_warnings: "Vale la pena revisar de cerca",
    ask_btn: "Pregúntale a Levi sobre cualquier cosa de este proyecto",
    rec: {
      all_clean: "Todo se ve bien en este proyecto por ahora.",
      no_estimate: "Este proyecto todavía no tiene un estimado.",
      estimate_empty: "El estimado está creado, pero aún no hay renglones.",
      estimate_unpriced: ({ count, names } = {}) => {
        const n = Number(count || 0);
        const list = Array.isArray(names) && names.length > 0 ? ` (${names.join(", ")})` : "";
        if (n === 1) return `Encontré 1 renglón que todavía necesita precio${list}.`;
        return `Encontré ${n} renglones que todavía necesitan precio${list}.`;
      },
      dc_not_run: "Este estimado aún no ha pasado por Doble Verificación.",
      dc_findings_red: ({ red } = {}) => {
        const n = Number(red || 0);
        if (n === 1) return "La Doble Verificación marcó 1 punto rojo — revísalo antes de compartir.";
        return `La Doble Verificación marcó ${n} puntos rojos — revísalos antes de compartir.`;
      },
      dc_findings_yellow: ({ yellow } = {}) => {
        const n = Number(yellow || 0);
        if (n === 1) return "La Doble Verificación marcó 1 punto amarillo que vale la pena revisar.";
        return `La Doble Verificación marcó ${n} puntos amarillos que vale la pena revisar.`;
      },
      dc_stale: "El estimado cambió desde la última Doble Verificación — conviene volver a correrla.",
      no_proposal: "Puedes preparar una propuesta cuando gustes.",
      proposal_awaiting: "La propuesta se compartió, pero el cliente todavía no responde.",
      approved_no_contract: "La propuesta fue aprobada — sigue el contrato.",
      contract_awaiting_signature: "Contrato enviado — esperando la firma.",
      signed_no_invoice: "El contrato ya está firmado — es momento de facturar.",
      invoice_payment_due: "Hay una factura sin pagar en este proyecto.",
      paid_no_closeout: "Todo está pagado. Genera el paquete de cierre cuando estés listo.",
      customer_no_docs_shared: "Todavía no se ha compartido nada con el cliente.",
      project_closed: "Este proyecto está cerrado. Buen trabajo.",
      customer_contact_missing: "Tengo el nombre del cliente, pero todavía necesito un correo o teléfono.",
      no_narrative: "Todavía me falta un poco más de detalle sobre el trabajo.",
    },
    action: {
      open_estimate: "Abrir estimado",
      create_estimate: "Iniciar estimado",
      edit_intake_notes: "Editar notas de recepción",
      jump_missing_pricing: "Corregir precios faltantes",
      run_double_check: "Abrir Doble Verificación",
      open_shared_proposal: "Abrir propuesta",
      open_pending_contract: "Abrir contrato",
      open_unpaid_invoice: "Abrir factura",
      new_proposal: "Preparar propuesta",
      new_contract: "Preparar contrato",
      new_invoice: "Crear factura",
      generate_closeout: "Generar paquete de cierre",
    },
    followup: {
      scope: "Pregúntale a Levi sobre el alcance de este proyecto.",
      customer_status: "Pregúntale a Levi cómo va la relación con el cliente.",
    },
  },
  // Slice 2T · Levi 1.0 Guía de recepción para Proyecto Completo.
  levi_intake_coach: {
    header_kicker: "Levi",
    empty_hint: "Empezaré a ayudarte apenas agregues un nombre del cliente — solo eso necesito.",
    ask_btn: "Pregúntale a Levi",
    status: {
      no_customer_name: "Agrega el nombre del cliente y empezaré a ayudarte a reunir lo demás.",
      ready: "Tienes suficiente información para comenzar.",
      need_more_detail: "Todavía me falta un poco más de detalle sobre el trabajo.",
      address_but_no_email: "Agregaste la dirección del trabajo, pero aún falta el correo del cliente.",
      contact_missing: "La descripción se ve bien, pero todavía necesito un correo o teléfono.",
    },
    also: {
      no_job_address: "Aún falta la dirección del trabajo.",
      no_phone: "Un teléfono ayuda si necesitas contactar rápido al cliente.",
      have_voice_no_typed: "Tus notas de voz están guardadas — siéntete libre de escribir detalles también.",
      no_contact_yet: "Agrega un correo o teléfono para poder contactar al cliente.",
    },
  },
  // Slice 2U · Vista de Soporte al Contratista (solo lectura).
  support: {
    header_kicker: "Soporte al Cliente JobClose",
    header_body_read_only:
      "Vista de Soporte de Solo Lectura. Puedes ver lo que el contratista está experimentando, pero no puedes cambiar ningún registro del contratista.",
    viewed_by: "Consultado por",
    read_only: "Vista de Soporte de Solo Lectura",
    refreshed_at: "Actualizado",
    refresh: "Actualizar",
    back_to_search: "Volver a la búsqueda",
    unknown: "Desconocido",
    yes: "Sí",
    no: "No",
    close: "Cerrar",
    loading: "Cargando…",
    footer_no_mutations:
      "Esta vista es solo lectura. Cualquier acción abre el flujo del contratista — soporte no puede modificar registros del contratista desde este espacio.",
    search: {
      title: "Búsqueda de Contratista",
      subtitle: "Localiza una cuenta de contratista para abrir una vista de soporte de solo lectura.",
      label: "Buscar por correo, nombre de empresa o nombre del contratista",
      placeholder: "ej. acme, jsmith@acme.com, John Smith",
      submit: "Buscar",
      searching: "Buscando…",
      error_min_length: "Por favor ingresa al menos 2 caracteres.",
      error_generic: "La búsqueda falló. Intenta de nuevo.",
      results_count: ({ count } = {}) =>
        Number(count || 0) === 1 ? "1 resultado" : `${count} resultados`,
      results_none: "Ningún contratista coincidió con tu búsqueda.",
      col_name: "Contratista",
      col_company: "Empresa",
      col_email: "Correo",
      col_joined: "Registrado",
      col_onboarding: "Configuración",
      onboarding_complete: "Completada",
      onboarding_incomplete: "En progreso",
      open_account: "Abrir cuenta",
    },
    tenant_context: {
      supporting: "Apoyando a",
      owner: "Titular",
      tenant_id: "ID de Contratista",
      viewed_by: "Consultado por",
    },
    account: {
      title: "Vista de Soporte de Cuenta",
      loading: "Cargando la cuenta del contratista…",
      identity: "Identidad de la cuenta",
      subscription: "Suscripción y derechos",
      aggregates: "Proyectos y documentos",
      sharing: "Compartir con el cliente",
      projects: "Proyectos",
      project_summary: "Resumen diagnóstico del proyecto",
      diagnostics: "Diagnóstico",
      timeline: "Línea de tiempo de la cuenta",
    },
    identity: {
      full_name: "Contratista",
      company: "Empresa",
      email: "Correo",
      phone: "Teléfono",
      created: "Creada",
      preferred_lang: "Idioma de la app",
      onboarding: "Configuración inicial",
      trades: "Oficios",
      trades_none: "Ninguno seleccionado",
    },
    subscription: {
      none: "Aún no se ha creado un registro de suscripción para este contratista.",
      tier: "Nivel",
      status: "Estado",
      billing_cycle: "Ciclo de facturación",
      current_period_end: "Período actual termina",
      trial_end: "Prueba termina",
      launch_offer: "Oferta de lanzamiento bloqueada",
      cancel_at_period_end: "Cancela al fin del período",
      updated: "Actualizada",
    },
    aggregates: {
      projects_total: "Proyectos",
      projects_draft: "Borradores",
      projects_active: "Activos",
      projects_closed: "Cerrados",
      estimates: "Estimados",
      proposals: "Propuestas",
      contracts: "Contratos",
      invoices_total: "Facturas",
      invoices_paid: "Facturas pagadas",
      invoices_unpaid: "Facturas sin pagar",
      work_areas: "Áreas de trabajo",
      voice_failed: "Notas de voz fallidas",
      dc_red: "Doble Verificación rojo",
      dc_yellow: "Doble Verificación amarillo",
      dc_ran: "Estimados con Doble Verificación",
    },
    sharing: {
      kind: "Tipo de documento",
      tokens_hidden: "Los tokens de compartir nunca se muestran aquí — el contratista sigue controlando el acceso del cliente.",
    },
    projects: {
      none: "Este contratista aún no tiene proyectos.",
      customer: "Cliente",
      job_type: "Trabajo",
      address: "Dirección",
      status: "Estado",
      updated: "Actualizado",
      diagnose: "Diagnosticar",
      photos_count: "Fotos",
      estimates: "Estimados",
      docs: "Documentos",
    },
    diagnostics: {
      email_total: "Correos registrados",
      email_failed: "Correos fallidos",
      email_queued: "Correos en cola",
      voice_failed: "Notas de voz fallidas",
      voice_pending: "Notas de voz pendientes",
      login_failed: "Inicios de sesión fallidos",
      login_success: "Inicios de sesión exitosos",
      password_failed: "Cambios de contraseña fallidos",
    },
    timeline: {
      none: "Aún no hay hitos disponibles.",
      milestone: {
        account_created: "Cuenta creada",
        onboarding_completed: "Configuración inicial completa",
        pricing_setup_completed: "Configuración de precios completa",
        first_project_created: "Primer proyecto creado",
        first_estimate_created: "Primer estimado creado",
        first_proposal_shared: "Primera propuesta compartida",
        first_contract_signed: "Primer contrato firmado",
        first_invoice_created: "Primera factura creada",
        last_meaningful_activity: "Última actividad importante",
      },
    },
  },
  // Slice VI-01 · Vision Intelligence & Guided Job Capture Foundation (ES).
  // La terminología pública nunca expone "measurement candidate" —
  // usamos "medición de Levi" / "medición preliminar".
  vi01: {
    overline: "Repaso de Levi",
    loading: "Levi está preparando tu repaso…",
    empty_headline: "Levi está listo.",
    empty_body:
      "Al finalizar este Recorrido en Video, Levi te dirá lo que vio, lo que midió y dónde tiene dudas.",
    headline: ({ work_areas, measurements, observations }) => {
      const parts = [];
      if (work_areas > 0) {
        parts.push(`Identifiqué ${work_areas} área${work_areas === 1 ? "" : "s"} de trabajo.`);
      }
      if (measurements > 0) {
        parts.push(
          `Registré ${measurements} medición${measurements === 1 ? "" : "es"} preliminar${measurements === 1 ? "" : "es"}.`
        );
      }
      if (observations > 0) {
        parts.push(
          `Noté ${observations} observación${observations === 1 ? "" : "es"} que vale la pena revisar contigo.`
        );
      }
      return parts.length ? parts.join(" ") : "Esto es lo que reuní de tu recorrido.";
    },
    confidence_summary: ({ high, uncertain }) => {
      if (high > 0 && uncertain > 0) {
        return `Estoy muy seguro de ${high} medición${high === 1 ? "" : "es"}, y ${uncertain} necesita${uncertain === 1 ? "" : "n"} otra mirada.`;
      }
      if (high > 0) return `Estoy muy seguro de ${high} medición${high === 1 ? "" : "es"}.`;
      if (uncertain > 0) return `${uncertain} medición${uncertain === 1 ? "" : "es"} necesita${uncertain === 1 ? "" : "n"} otra mirada.`;
      return "";
    },
    review_prompt: "¿Prefieres repasar primero las mediciones o las observaciones?",
    choice_measurements: "Repasar mediciones primero",
    choice_observations: "Repasar observaciones",
    measurements_section_title: "Mediciones de Levi — preliminares",
    measurements_empty: "Levi no registró mediciones preliminares en este recorrido.",
    observations_section_title: "Lo que Levi notó",
    observations_empty: "Levi no agregó observaciones en este recorrido.",
    preliminary_measurement_label: ({ label }) => `${label} — preliminar`,
    confidence_high: "Alta confianza",
    confidence_moderate: "Confianza moderada",
    confidence_low: "Baja confianza",
    evidence_at: "en",
    decision_notice:
      "Estos hallazgos son preliminares. Los controles de revisión y aprobación se agregarán en el siguiente paso de decisión de mediciones. Por ahora las mediciones de Levi permanecen registradas y preliminares — nada se redacta, se envía ni se promueve sin ti.",
    show_evidence_panel: "Mostrar evidencia estructurada",
    hide_evidence_panel: "Ocultar evidencia estructurada",
    evidence_panel_source: "Fuente",
    evidence_panel_policy: "Versión de política",
    evidence_panel_footnote:
      "Este panel es para auditoría. El repaso hablado de Levi arriba es la experiencia principal.",
    coaching: {
      heading: "Aviso de Levi",
      dismiss_aria: "Descartar este aviso",
      move_slower: "Intenta girar la cámara un poco más despacio para que Levi te siga.",
      hold_steady: "Trata de mantener la cámara más firme por un momento.",
      improve_lighting: "Está un poco oscuro — enciende una luz o usa una lámpara de trabajo si puedes.",
      move_farther_back: "Retrocede un poco para que Levi pueda ver toda el área.",
      show_opposite_wall: "Muestra también la pared opuesta para que Levi pueda medir todo el cuarto.",
      include_both_corners: "Incluye ambas esquinas en el cuadro para que Levi tenga una referencia.",
      capture_ceiling: "Apunta la cámara al techo por un momento si es parte del trabajo.",
      provide_scale_reference: "Dale a Levi una referencia de escala — una cinta métrica, un pedazo de moldura, o tu mano.",
      revisit_obstructed_area: "Esa área quedó obstruida — ¿podrías volver a mostrarla desde otro ángulo?",
      clarify_ambiguity: "Repite esa parte — Levi no estaba seguro de qué elemento estabas describiendo.",
    },
  },
  // Slice LEVI-VOICE-01 · Preferencias de voz de Levi (ES).
  levi_voice: {
    overline: "La voz de Levi",
    title: "Preferencias de voz",
    description: "Elige cómo suena Levi cuando te habla. Los cambios se aplican de inmediato.",
    tts_enabled_label: "Leer en voz alta las respuestas de Levi",
    language_label: "Idioma de la voz",
    language_option_en: "Inglés",
    language_option_es: "Español",
    gender_label: "Estilo de voz",
    gender_option_friendly_female: "Voz femenina amable",
    gender_option_friendly_male: "Voz masculina amable",
    gender_option_no_preference: "Sin preferencia",
    rate_label: "Velocidad al hablar",
    rate_slower: "Más lento",
    rate_default: "Normal",
    rate_faster: "Más rápido",
    preview_button: "Escuchar una muestra",
    disabled_note: "La voz está apagada. Actívala arriba para escuchar a Levi.",
  },
  universal_search: {
    placeholder: "Buscar clientes, proyectos, direcciones…",
    searching: "Buscando…",
    clear: "Limpiar búsqueda",
    no_results: (q) => `Sin coincidencias para "${q}"`,
    customer_hint: "Cliente",
    other_projects: "Otros proyectos coincidentes",
  },
  status: {
    draft: "Borrador",
    active: "Activo",
    in_progress: "En progreso",
    scheduled: "Programado",
    completed: "Completado",
    archived: "Archivado",
    sent: "Enviado",
    approved: "Aprobado",
    declined: "Rechazado",
    awaiting_signature: "Esperando firma",
    signed: "Firmado",
    paid: "Pagado",
    unpaid: "Sin pagar",
    overdue: "Vencido",
  },
  company_settings: {
    title: "Empresa y ajustes",
    subtitle: "Todo lo que define cómo JobClose AI trabaja para tu empresa.",
    company_overline: "Empresa",
    company_title: "Perfil de la empresa",
    company_body: "Nombre, dirección, teléfono, logotipo, licencia y zona horaria.",
    documents_overline: "Documentos",
    documents_title: "Preferencias de documentos",
    documents_body: "Propuestas, contratos, facturas, avisos, garantía y cierre.",
    closeout_overline: "Cierre y garantía",
    closeout_title: "Preferencias de cierre",
    closeout_body: "Períodos de garantía, flujo de firma y paquete de cierre.",
    pricing_overline: "Precios · Fundacional",
    pricing_title: "Ajustes de precios",
    pricing_body: "Aquí viven tus precios aprobados. Todo incluido por unidad, por trabajo o sistema, Aprender de mis estimados, y configuración de oficios.",
    levi_overline: "Levi",
    levi_title: "Ajustes de Levi · Voz · Persona · Idioma",
    levi_body: "Profesional o cálido, género, idioma, velocidad — escucha cualquier cambio antes de guardar.",
    account_overline: "Cuenta y facturación",
    account_title: "Cuenta · Suscripción",
    account_body: "Stripe maneja los datos de la tarjeta. JobClose nunca los toca.",
    website_overline: "Haz crecer tu negocio",
    website_headline: "¿Necesitas un sitio web profesional?",
    website_product: "Contractor Website Starter · desde $499",
    website_body: "Un sitio web pulido creado por JobClose y personalizado para tu empresa.",
    website_bullet_customized: "Personalizado para tu empresa",
    website_bullet_hosting: "Un año de alojamiento incluido",
    website_bullet_domain: "Tú compras y eres dueño de tu dominio",
    website_cta: "Saber más",
    website_placeholder_label: "Marcador — revisión del PO requerida",
    footer_tagline: "Tu negocio. Tus precios. Impulsado por Levi.",
  },
};

const DICTS = { en: EN, es: ES };

const _get = (dict, path) => {
  const parts = path.split(".");
  let cur = dict;
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = cur[p];
  }
  return cur;
};

// Slice 2D1 · Reactive translator factory. Returns a bound `t` that
// looks up `path` in the requested locale first and transparently
// falls back to English when a key is missing. Unknown keys resolve
// to the key string so callers never crash. This factory has ZERO
// module state — the LangContext owns the single reactive source of
// truth for the current locale and creates a fresh translator on
// every locale change so subscribing components re-render.
export const makeTranslator = (lang) => {
  const primary = DICTS[lang] || EN;
  return (path, ...args) => {
    let val = _get(primary, path);
    if (val == null && primary !== EN) val = _get(EN, path);
    if (typeof val === "function") return val(...args);
    return val == null ? path : val;
  };
};

export const SUPPORTED_LOCALES = Object.freeze(["en", "es"]);
export const DICTIONARIES = Object.freeze({
  en: Object.freeze(EN),
  es: Object.freeze(ES),
});

// Slice 2D1 · Legacy module-level shim retained ONLY so any pre-fix
// test scripts that used the stateless helper keep compiling. The
// application must NOT import `t` from this module — it must use
// `useLang().t` so React re-renders on locale change. Kept for
// backward-compatibility of test infrastructure only.
let _fallbackLang = "en";
try {
  if (typeof localStorage !== "undefined") {
    const v = localStorage.getItem("jc_lang");
    if (v === "en" || v === "es") _fallbackLang = v;
  }
} catch (_) { /* jsdom / SSR — ignore */ }
export const setOnboardingLanguage = (lang) => {
  if (lang === "en" || lang === "es") _fallbackLang = lang;
};
export const currentLanguage = () => _fallbackLang;
export const t = (path, ...args) => makeTranslator(_fallbackLang)(path, ...args);
